import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, Input, InputNumber, List, Tag, Spin, Empty, Typography, Space, Select, message, Modal, Button, Table, Divider, Switch, Checkbox, Alert, Tooltip, Pagination, Form, Row, Col, Collapse, Radio, Image } from 'antd';
import { SearchOutlined, ContainerOutlined, InboxOutlined, CopyOutlined, DeleteOutlined, EditOutlined, PrinterOutlined, TagsOutlined } from '@ant-design/icons';
import { PDFDocument } from 'pdf-lib';
import request from '../../../../utils/request';
import { getProjectList } from '../../../../services/project';
import { searchInboundForecasts, getContainerDetails, createInboundRecord, getForecastByTrackingNumber, checkTrackingNumberInbound, getInboundRecordList, quickInbound } from '../../../../services/inbound-forecast';
import { getInventoryUnitByBarcode, confirmInventoryUnitWeightDimensions, createOriginalContainerUnit, createAccessoriesInventoryUnit, createExceptionInventoryUnit, createInventoryUnit, createInventoryContent, createInventoryContentItem, createInventoryUnitWithSortingGroup, getInventoryUnits, getInventoryUnitDetail } from '../../../../services/inventory';
import { createContainerFromSortingBoard, addToExistingContainer, getAvailableContainers, fetchStorageContainers, fetchStorageContainerDetailWithContents } from '../../../../services/storage';
import { getProductList, createProduct, createSortingCode, getSortingCodeList } from '../../../../services/product'; // 新增：导入获取产品列表的API
// 仓储标签相关导入
import { storageLabelService } from '../../storage/labels/services/storageLabelService';
import { storageContainerAdapter } from '../../storage/labels/adapters/containerAdapter';
import { handleLabelError } from '../../storage/labels/utils/labelUtils';
import StorageLabelPreview from '../../storage/labels/components/StorageLabelPreview';
import type { StorageContainerLabelData } from '../../storage/labels/types/storageLabel';
import { getInventoryConfigList, getInventoryConfigDetail } from '../../../../services/inventory'; // 新增
// 引入库存单元标签适配与预览（复用项目配置页能力）
import { InventoryUnitLabelAdapter } from '../../../project/inventory-config/labels/adapters/inventoryLabelAdapter';
import { InventoryLabelPreview } from '../../../project/inventory-config/labels/components/InventoryLabelPreview';
import { inventoryLabelService } from '../../../project/inventory-config/labels/services/inventoryLabelService';
import type { InventoryLabelData } from '../../../project/inventory-config/labels/types/inventoryLabel';
import { getWarehouseList } from '../../../../services/warehouse'; // 新增：导入仓库服务
import { usePermissions } from '../../../../contexts/PermissionContext';
import type { Project } from '../../../../types/project';
import type { InboundForecast, InboundRecord } from '../../../../types/inbound-forecast';
import type { InventoryUnit } from '../../../../types/inventory';
import type { WarehouseDetail } from '../../../../types/warehouse'; // 新增：导入仓库类型
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../hooks/useAuth'; // 新增：导入useAuth hook
import { recordSortingBoardDeltasAfterSubmit } from '../../../../utils/sortingBoardLocalSync';
import { rollbackSortingBoardForRecord, updateSortingBoardForModify } from '../../../../utils/sortingBoardLocalSync';
import { voidInboundRecord, modifyInboundRecord, getInboundRecordListPaginated, getInboundUnitBalances, getInboundRecordDetail } from '../../../../services/inbound-record';
import ReturnReceivingPanel from './ReturnReceivingPanel';
import { addInboundItemsToBoard } from '../../../../utils/sortingBoardBatch';
import type { BoardPlacement } from '../../../../utils/sortingBoardBatch';
import type { InspectionExecution } from '../../../../types/returnInspection';
import { buildLastInboundRecordSnapshot, saveLastInboundRecord, loadLastInboundRecord, planFillFromRecord, aggregateRecordItems } from '../../../../utils/lastInboundRecord';
import type { LastInboundRecordSnapshot, FillRow } from '../../../../utils/lastInboundRecord';
import { isLocalSpeechSupported, speakTextLocal } from '../../../../utils/localSpeech';
import { getAttachmentUrl } from '../../../../utils/attachmentUrl';
import type { LocalSpeechLang } from '../../../../utils/localSpeech';
// 新增：导入标签页状态管理Hook
// 移除usePersistentTabState导入，直接使用localStorage
// 本地持久化工具（简易）
const SORTING_BOARD_KEY = 'inbound_scan_board_v1';
// 未知库存单元最近一次输入的物理信息本地缓存键
const UNKNOWN_UNIT_LAST_DATA_KEY = 'inbound_unknown_unit_last_data_v1';
// 全局本地索引键（用于记录 入库记录ID -> 分拣增量）
const GLOBAL_RECORD_INDEX_KEY = 'inbound_scan_board_record_index_global_v1';

// 本地持久化函数
function saveSortingBoardStateLocal(payload: { 
  sortingDatabase: any[]; 
  nextLocationNumber: number;
  reusableLocationPool: number[];
}) {
  try {
    const data = { version: 1, updatedAt: new Date().toISOString(), data: payload };
    localStorage.setItem(SORTING_BOARD_KEY, JSON.stringify(data));
    // 调试日志：保存看板摘要
    try {
      // 统计含有分拣条码快照的记录数
      const withSnapshot = Array.isArray(payload.sortingDatabase)
        ? payload.sortingDatabase.filter((it: any) => it?.displaySortingBarcode || (it?.sortingBarcodes && it.sortingBarcodes.length)).length
        : 0;
      console.log('[SortingBoard][SAVE]', {
        total: payload.sortingDatabase?.length || 0,
        withSnapshot,
        nextLocationNumber: payload.nextLocationNumber,
        reusableLocationPool: payload.reusableLocationPool || []
      });
    } catch {}
  } catch {}
}

function loadSortingBoardStateLocal(): { 
  sortingDatabase: any[]; 
  nextLocationNumber?: number;
  reusableLocationPool?: number[];
} | null {
  try {
    const raw = localStorage.getItem(SORTING_BOARD_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    const data = obj?.data || null;
    try {
      const total = data?.sortingDatabase?.length || 0;
      const withSnapshot = Array.isArray(data?.sortingDatabase)
        ? data.sortingDatabase.filter((it: any) => it?.displaySortingBarcode || (it?.sortingBarcodes && it.sortingBarcodes.length)).length
        : 0;
      console.log('[SortingBoard][LOAD]', {
        total, 
        withSnapshot, 
        nextLocationNumber: data?.nextLocationNumber 
      });
      if (total > 0) {
        const sample = data.sortingDatabase[0];
        console.log('[SortingBoard][LOAD][SAMPLE]', {
          inventoryUnitCode: sample?.inventoryUnitCode,
          displaySortingBarcode: sample?.displaySortingBarcode,
          sortingBarcodes: sample?.sortingBarcodes
        });
      }
    } catch {}
    return data;
  } catch { return null; }
}

function clearSortingBoardStateLocal() {
  try {
    localStorage.removeItem(SORTING_BOARD_KEY);
    console.log('[SortingBoard][CLEAR] local cache removed');
  } catch {}
}

// 保存"未知库存单元"最近一次输入的重量与尺寸
function saveUnknownUnitLastData(data: { 
  weight: number; 
  hasLogisticsPackage?: boolean; 
  dimensions: { length: number; width: number; height: number } 
}) {
  try {
    const payload = { ...data, savedAt: new Date().toISOString() };
    localStorage.setItem(UNKNOWN_UNIT_LAST_DATA_KEY, JSON.stringify(payload));
    console.log('[UnknownUnit][SAVE]', payload);
  } catch (e) {
    console.warn('[UnknownUnit][SAVE] failed', e);
  }
}

// 读取"未知库存单元"最近一次输入的重量与尺寸
function loadUnknownUnitLastData(): {
  weight: number;
  hasLogisticsPackage?: boolean;
  dimensions: { length: number; width: number; height: number }
} | null {
  try {
    const raw = localStorage.getItem(UNKNOWN_UNIT_LAST_DATA_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (
      typeof data?.weight !== 'number' ||
      !data?.dimensions ||
      typeof data.dimensions.length !== 'number' ||
      typeof data.dimensions.width !== 'number' ||
      typeof data.dimensions.height !== 'number'
    ) {
      return null;
    }
    return data;
  } catch (e) {
    console.warn('[UnknownUnit][LOAD] failed', e);
    return null;
  }
}

const ORIGINAL_CONTAINER_LAST_DIMENSIONS_KEY = 'inbound_original_container_last_dimensions_v1';
const ACCESSORIES_ITEM_LAST_DIMENSIONS_KEY = 'inbound_accessories_item_last_dimensions_v1';
// 入库记录"打印标签"：一次打印超过这个张数时先确认
const LABEL_BATCH_PRINT_CONFIRM_THRESHOLD = 500;
// 原托盘入库：单个托盘重量（克）超出这个范围时先确认，防止多输或少输一个 0
const PALLET_WEIGHT_CONFIRM_MIN_G = 30000;
const PALLET_WEIGHT_CONFIRM_MAX_G = 800000;

type LastDimensions = { length: number | undefined; width: number | undefined; height: number | undefined; weight?: number; weightEstimated?: boolean };

function saveLastDimensions(key: string, dimensions: { length?: number; width?: number; height?: number; weight?: number; weightEstimated?: boolean }) {
  try {
    localStorage.setItem(key, JSON.stringify(dimensions));
  } catch (e) {
    console.warn('[LastDimensions][SAVE] failed', e);
  }
}

function loadLastDimensions(key: string): LastDimensions | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // 尺寸和重量都可能没填（非上架货物尺寸选填），但至少要有一项，且有值的必须是数字
    const values = [data?.length, data?.width, data?.height, data?.weight];
    if (values.some(value => value !== undefined && typeof value !== 'number') || values.every(value => value === undefined)) {
      return null;
    }
    return { ...data, length: data.length, width: data.width, height: data.height };
  } catch (e) {
    console.warn('[LastDimensions][LOAD] failed', e);
    return null;
  }
}

const { Title, Text } = Typography;

// 库存单元内容物：每条一行，左边产品照片（点击放大），右边"产品名（客户编码 分拣条码）× 数量"，多个分拣条码只显示第一个，后面用"..."。
// 手动选择库存单元、重量尺寸确认两个弹窗共用；showPhotoPlaceholder 为 true 时没有照片也占一个"无照片"的位置
type UnitContentLine = { name: string; customCode?: string; sortingCodes?: string[]; photo?: string | null; quantity?: number | string | null };
// 从库存单元数据得到内容物行：详情/列表接口自带产品名、客户编码、照片、分拣条码；缺的从项目产品里补
const unitContentLinesOf = (unit: any, products: any[]): UnitContentLine[] => {
  const items = Array.isArray(unit?.content?.items) ? unit.content.items : [];
  return items.map((it: any) => {
    const product = (products || []).find((p: any) => Number(p?.id) === Number(it?.product)) || {};
    const rawCodes = Array.isArray(it?.sorting_codes) && it.sorting_codes.length > 0 ? it.sorting_codes : (Array.isArray(product?.sorting_codes) ? product.sorting_codes : []);
    return {
      name: it?.product_name || product?.name || '-',
      customCode: it?.product_code || product?.custom_code || '',
      sortingCodes: rawCodes.map((s: any) => (typeof s === 'string' ? s : s?.code)).filter(Boolean),
      photo: it?.photo || product?.photo || null,
      quantity: it?.quantity,
    };
  });
};
// 备注按"；"拆成多行（原箱/原托盘的备注是"跟踪号-X；第三方参考号-Y；重量为预估"）
const UnitRemarksLines: React.FC<{ remarks?: string | null }> = ({ remarks }) => {
  const parts = String(remarks || '').split('；').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return <Text type="secondary">-</Text>;
  return (
    <div className="unit-remarks-lines" style={{ wordBreak: 'break-all' }}>
      {parts.map((part, i) => <div key={i}>{part}</div>)}
    </div>
  );
};

// 括号里的内容：客户编码 + 第一个分拣条码（有多个时后面加"..."），都没有就不显示括号
const unitContentLineCodes = (line: UnitContentLine): string => {
  const sortingCodes = (line.sortingCodes || []).filter(Boolean);
  const sortingText = sortingCodes.length > 1 ? `${sortingCodes[0]} ...` : (sortingCodes[0] || '');
  return [line.customCode, sortingText].filter(Boolean).join(' ');
};
const UnitContentLines: React.FC<{ items: UnitContentLine[]; showPhotoPlaceholder?: boolean }> = ({ items, showPhotoPlaceholder = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {items.map((p, i) => (
      <div key={i} className="unit-content-line" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {p.photo ? (
          <Image className="unit-content-photo" src={getAttachmentUrl({ url: p.photo })} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
        ) : showPhotoPlaceholder ? (
          <span className="unit-content-no-photo" style={{ width: 40, height: 40, flex: '0 0 40px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', border: '1px dashed #d9d9d9', borderRadius: 4, color: '#bfbfbf', fontSize: 11 }}>无照片</span>
        ) : null}
        <span>
          <Text strong>{p.name}</Text>
          {unitContentLineCodes(p) ? <Text type="secondary">（{unitContentLineCodes(p)}）</Text> : null}
          <Text type="secondary"> × {p.quantity ?? '-'}</Text>
        </span>
      </div>
    ))}
  </div>
);

// 可手动回收的空仓位：该仓位下所有库存单元行的已分拣数量都是 0，且不被当前未提交的扫描记录引用
const computeReclaimableLocations = (
  db: Array<{ temporaryLocation: number; sortedQuantity?: number }>,
  history: Array<{ temporaryLocation: number }>
): number[] => {
  const allZeroByLocation = new Map<number, boolean>();
  db.forEach(item => {
    const location = Number(item.temporaryLocation);
    if (!location) return;
    const isZero = (item.sortedQuantity || 0) === 0;
    allZeroByLocation.set(location, (allZeroByLocation.get(location) ?? true) && isZero);
  });
  const inUse = new Set(history.map(record => Number(record.temporaryLocation)));
  return Array.from(allZeroByLocation.entries())
    .filter(([location, allZero]) => allZero && !inUse.has(location))
    .map(([location]) => location)
    .sort((a, b) => a - b);
};

// 使用从API服务导入的类型定义

// 添加到已有容器的数据结构
interface AddToContainerForm {
  selectedContainerCode: string;
  items: Array<{
    inventoryUnitCode: string;
    inventoryUnitName?: string;
    maxQuantity: number;
    selectedQuantity: number;
    selected: boolean;
  }>;
  groupInfo: {
    groupName: string;
    groupSystemCode: string;
    temporaryLocation: number;
  };
}

const InboundScanPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth(); // 新增：使用useAuth获取当前用户
  // 当前会话内的入库记录ID（用于容器落位分配映射）
  const [currentInboundRecordId, setCurrentInboundRecordId] = useState<number | null>(null);
  
  // 直接使用localStorage进行分拣看板数据持久化
  // 分拣看板数据存储键
  const SORTING_BOARD_STORAGE_KEY = 'inbound_scan_sorting_board_v1';
  
  // 保存分拣看板数据到localStorage
  const saveSortingBoardData = useCallback((data: any) => {
    try {
      const storageData = {
        version: '1.0.0',
        lastUpdate: new Date().toISOString(),
        data
      };
      localStorage.setItem(SORTING_BOARD_STORAGE_KEY, JSON.stringify(storageData));
      console.log('💾 分拣看板数据已保存到localStorage');
    } catch (error) {
      console.error('❌ 保存分拣看板数据失败:', error);
    }
  }, []);
  
  // 从localStorage恢复分拣看板数据
  const restoreSortingBoardData = useCallback((): any | null => {
    try {
      const stored = localStorage.getItem(SORTING_BOARD_STORAGE_KEY);
      if (!stored) return null;
      
      const parsed = JSON.parse(stored);
      return parsed.data;
    } catch (error) {
      console.error('❌ 恢复分拣看板数据失败:', error);
      return null;
    }
  }, []);
  
  // 清理分拣看板数据
  const clearSortingBoardData = useCallback(() => {
    try {
      localStorage.removeItem(SORTING_BOARD_STORAGE_KEY);
      console.log('🧹 分拣看板数据已清理');
    } catch (error) {
      console.error('❌ 清理分拣看板数据失败:', error);
    }
  }, []);
  
  // 添加扫描输入框的ref
  const scanInputRef = useRef<any>(null);
  // 添加主扫描输入框的ref
  const mainScanInputRef = useRef<any>(null);
  // 记录最近一次扫描的原始条码值（用于重量尺寸确认后的回填）
  const lastScannedValueRef = useRef<string>('');
  
  const [projectCode, setProjectCode] = useState<string>('');
  const [warehouseCode, setWarehouseCode] = useState<string>('US-1-NH'); // 新增：仓库编码状态，默认选择US-1-NH
  const [scanValue, setScanValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<InboundForecast[]>([]);
  const [searchError, setSearchError] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseDetail[]>([]); // 新增：仓库列表状态
  const [loadingWarehouses, setLoadingWarehouses] = useState(false); // 新增：仓库加载状态
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentScanValue, setCurrentScanValue] = useState('');
  const [scanType, setScanType] = useState('');
  const [containerData, setContainerData] = useState<any>(null);
  const [loadingContainer, setLoadingContainer] = useState(false);
  const [actualSearchCode, setActualSearchCode] = useState('');
  
  // 物流单号对应预报信息状态
  const [trackingForecastData, setTrackingForecastData] = useState<any>(null);
  const [loadingTrackingForecast, setLoadingTrackingForecast] = useState(false);
  
  // 库存单元扫描相关状态
  const [inventoryUnitScanValue, setInventoryUnitScanValue] = useState('');
  const [scanningInventoryUnit, setScanningInventoryUnit] = useState(false);
  
  // 分拣数据库状态
  const [sortingDatabase, setSortingDatabase] = useState<Array<{
    sortingGroup: string;
    sortingGroupSystemCode: string; // 添加分拣组系统编码
    inventoryUnitCode: string;
    inventoryUnitName?: string; // 库存单元名称（remarks字段）
    temporaryLocation: number;
    sortedQuantity: number; // 累计的已分拣数量
    // 新增：分拣条码持久化快照（不影响现有逻辑，仅用于显示回退）
    productId?: number;
    displaySortingBarcode?: string;
    sortingBarcodes?: string[];
    // 新增：标记该记录是否为空仓位（当分拣组下所有库存单元都已清空时）
    isEmptyLocation?: boolean;
  }>>([]);
  const [nextLocationNumber, setNextLocationNumber] = useState(1);

  // 排序后的分拣数据库状态 - 始终按临时仓位编号从小到大排列
  const sortedSortingDatabase = useMemo(() => {
    return [...sortingDatabase].sort((a, b) => a.temporaryLocation - b.temporaryLocation);
  }, [sortingDatabase]) as typeof sortingDatabase;

  // 分拣看板筛选：按临时分拣仓位过滤
  const [boardLocationFilter, setBoardLocationFilter] = useState<number | undefined>(undefined);
  // 分拣看板筛选：按项目过滤（从分拣组系统编码 SG-{project_code}-{seq} 提取）
  const [boardProjectFilter, setBoardProjectFilter] = useState<string | undefined>(undefined);
  // 分拣看板项目筛选：输入实时筛选（不必选择也立刻生效）
  const [boardProjectSearchText, setBoardProjectSearchText] = useState<string>('');
  const getProjFromGroupCode = (groupCode?: string) => {
    try {
      if (!groupCode) return '';
      const m = String(groupCode).match(/^SG-([^-\s]+)-/i);
      return m && m[1] ? m[1] : '';
    } catch {
      return '';
    }
  };
  const boardProjectOptions = useMemo(() => {
    try {
      const codeSet = new Set<string>();
      for (const it of sortedSortingDatabase) {
        const code = getProjFromGroupCode((it as any).sortingGroupSystemCode);
        if (code) codeSet.add(code);
      }
      const codes = Array.from(codeSet).sort();
      return codes.map(code => {
        const proj = Array.isArray(projects) ? projects.find((p: any) => p.project_code === code) : undefined;
        return { value: code, label: proj ? `${code} - ${proj.name}` : code };
      });
    } catch {
      return [];
    }
  }, [sortedSortingDatabase, projects]);
  const filteredSortingDatabase = useMemo(() => {
    let data = sortedSortingDatabase;
    if (typeof boardLocationFilter === 'number' && !Number.isNaN(boardLocationFilter)) {
      data = data.filter(it => Number(it.temporaryLocation) === Number(boardLocationFilter));
    }
    const selectedCode = (boardProjectFilter || '').trim();
    const searchText = (boardProjectSearchText || '').trim();
    if (selectedCode) {
      data = data.filter(it => getProjFromGroupCode((it as any).sortingGroupSystemCode) === selectedCode);
    } else if (searchText) {
      const needle = searchText.toLowerCase();
      data = data.filter(it => getProjFromGroupCode((it as any).sortingGroupSystemCode).toLowerCase().includes(needle));
    }
    return data;
  }, [sortedSortingDatabase, boardLocationFilter, boardProjectFilter, boardProjectSearchText]);

  // 分拣看板分页（每页记录数可调，默认20条；整组放在同一页，所以实际条数可能少于或多于这个数）
  const [boardPage, setBoardPage] = useState(1);
  const [boardPageSize, setBoardPageSize] = useState(20);
  // 组内不拆页：按临时分拣仓位（temporaryLocation）成组分页，避免一个仓位跨页
  const {
    boardPageItems,
    boardTotalPages,
  } = useMemo(() => {
    // 按临时仓位分组（保持原有顺序）
    const groups: Record<number, typeof filteredSortingDatabase> = {};
    for (const item of filteredSortingDatabase) {
      const key = Number(item?.temporaryLocation) || 0;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    const orderedKeys = Object.keys(groups).map(Number).sort((a, b) => a - b);
    // 组装分页：整组放入同页，尽量不超过上限；若单组超上限，也不拆分
    const pages: Array<typeof filteredSortingDatabase> = [];
    let current: typeof filteredSortingDatabase = [];
    let currentCount = 0;
    for (const key of orderedKeys) {
      const items = groups[key];
      const size = items.length;
      if (currentCount > 0 && (currentCount + size) > boardPageSize) {
        pages.push(current);
        current = [];
        currentCount = 0;
      }
      current = current.concat(items);
      currentCount += size;
      if (currentCount === boardPageSize) {
        pages.push(current);
        current = [];
        currentCount = 0;
      }
    }
    if (current.length > 0) pages.push(current);
    const totalPages = Math.max(1, pages.length);
    const safePage = Math.min(Math.max(1, boardPage), totalPages);
    return {
      boardPageItems: pages[safePage - 1] || [],
      boardTotalPages: totalPages,
    };
  }, [filteredSortingDatabase, boardPage, boardPageSize]);

  useEffect(() => {
    if (boardPage > boardTotalPages) setBoardPage(boardTotalPages);
  }, [boardTotalPages, boardPage]);

  // 启动时自动恢复分拣看板
  useEffect(() => {
    // 直接从localStorage恢复分拣看板数据
    const saved = restoreSortingBoardData();
    console.log('[SortingBoard][LOCAL_RESTORE] 本地存储状态:', saved);
    
    if (saved && Array.isArray(saved.sortingDatabase) && saved.sortingDatabase.length > 0) {
      setSortingDatabase(saved.sortingDatabase);
      if (typeof saved.nextLocationNumber === 'number' && saved.nextLocationNumber > 0) {
        setNextLocationNumber(saved.nextLocationNumber);
      } else {
        const maxLoc = saved.sortingDatabase.reduce((max, it) => Math.max(max, it?.temporaryLocation || 0), 0);
        setNextLocationNumber(maxLoc + 1);
      }
      // 加载可重用仓位池
      if (saved.reusableLocationPool && Array.isArray(saved.reusableLocationPool)) {
        setReusableLocationPool(saved.reusableLocationPool);
        console.log('[SortingBoard][RESTORE] restored reusable locations:', saved.reusableLocationPool.length);
      }
      message.info('已恢复上次分拣看板数据');
      try {
        const missing = saved.sortingDatabase.filter((it: any) => !(it?.displaySortingBarcode || (it?.sortingBarcodes && it?.sortingBarcodes.length)));
        console.log('[SortingBoard][RESTORE] restored items:', saved.sortingDatabase.length, 'missing barcode snapshot:', missing.length);
        if (missing.length > 0) {
          console.log('[SortingBoard][RESTORE][MISSING_SAMPLE]', missing[0]);
        }
      } catch {}
    }
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 防抖自动保存分拣看板
  const saveDebounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (saveDebounceRef.current) window.clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = window.setTimeout(() => {
      // 保存到localStorage
      const boardState = { sortingDatabase, nextLocationNumber, reusableLocationPool };
      saveSortingBoardStateLocal(boardState);
      
      // 同时保存到新的localStorage存储
      saveSortingBoardData(boardState);
    }, 500);
    return () => {
      if (saveDebounceRef.current) window.clearTimeout(saveDebounceRef.current);
    };
  }, [sortingDatabase, nextLocationNumber, saveSortingBoardData]);

  // 页面关闭/隐藏时保存
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 保存到localStorage
      const boardState = { sortingDatabase, nextLocationNumber, reusableLocationPool };
      saveSortingBoardStateLocal(boardState);
      saveSortingBoardData(boardState);
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // 保存到localStorage
        const boardState = { sortingDatabase, nextLocationNumber, reusableLocationPool };
        saveSortingBoardStateLocal(boardState);
        saveSortingBoardData(boardState);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sortingDatabase, nextLocationNumber, saveSortingBoardData]);
  
  // 扫描结果状态
  const [scanResult, setScanResult] = useState<{
    inventoryUnitCode: string;
    scannedValue?: string; // 新增：扫描值
    temporaryLocation: number;
    isNew: boolean;
    matchType?: string; // 新增：匹配类型
  } | null>(null);
  
  // 扫描记录状态
  const [scanHistory, setScanHistory] = useState<Array<{
    inventoryUnitCode: string;
    scannedValue: string; // 新增：实际扫描的条码值
    temporaryLocation: number;
    quantity: number;
    scanTime: Date;
    sortingGroup: string;
    sortingGroupSystemCode: string; // 添加分拣组系统编码
  }>>([]);
  // 手动选择库存单元
  const [manualPickOpen, setManualPickOpen] = useState(false);
  const [manualPickKeyword, setManualPickKeyword] = useState('');
  const [manualPickPage, setManualPickPage] = useState(1);
  const [manualPickPageSize, setManualPickPageSize] = useState(10);
  const [pendingManualScanCode, setPendingManualScanCode] = useState<string | null>(null);
  const [speechLang, setSpeechLang] = useState<LocalSpeechLang>('zh-CN');
  const [speechSupported, setSpeechSupported] = useState<boolean>(false);
  // 重量尺寸确认相关状态（前置声明，供语音播报 effect 使用）
  const [weightDimensionsModalVisible, setWeightDimensionsModalVisible] = useState(false);

  // 调试面板（分拣看板快照 + LocalStorage 调试日志）：默认隐藏，需要时手动打开
  const [debugPanelVisible, setDebugPanelVisible] = useState<boolean>(false);
  // 调试：本机 LocalStorage 变化日志（在"调试 - 分拣看板快照"中展示）
  const [lsDebugLogs, setLsDebugLogs] = useState<string[]>([]);
  const appendLsLog = useCallback((label: string, payload: any) => {
    try {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      const ts = `${hh}:${mm}:${ss}`;
      const text = `[${ts}] ${label} ${JSON.stringify(payload)}\n`;
      setLsDebugLogs(prev => [text, ...prev].slice(0, 100));
    } catch {
      // ignore
    }
  }, []);

  // 记录上次采纳的本地快照更新时间，避免循环回写
  const lastAdoptedUpdatedAtRef = useRef<string>('');
  // 记录最近一次采用/内存的内容签名，避免仅因时间戳变化触发采纳
  const lastKnownBoardSignatureRef = useRef<string>('');

  useEffect(() => {
    setSpeechSupported(isLocalSpeechSupported());
    try {
      const cached = localStorage.getItem('local-speech-lang');
      const lang = String(cached || '').trim();
      if (lang === 'zh-CN' || lang === 'en-US') setSpeechLang(lang as LocalSpeechLang);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('local-speech-lang', speechLang);
    } catch {}
  }, [speechLang]);

  useEffect(() => {
    if (!isModalVisible) return;
    if (scanningInventoryUnit) return;
    if (weightDimensionsModalVisible) return;
    if (!scanResult) return;
    if (!speechSupported) return;
    const latestScannedContext = String(lastScannedValueRef.current || '').trim().toLowerCase();
    const resultContext = String(scanResult?.scannedValue || scanResult?.inventoryUnitCode || '').trim().toLowerCase();
    if (!resultContext) return;
    // 仅播报当前扫描上下文的结果，避免旧 scanResult 被误播报
    if (latestScannedContext && latestScannedContext !== resultContext) return;
    const loc = String(scanResult?.temporaryLocation ?? '').trim();
    if (!loc) return;
    speakTextLocal(loc, {
      lang: speechLang,
      rate: 1,
      pitch: 1,
      volume: 1,
      interrupt: false,
    });
  }, [isModalVisible, scanningInventoryUnit, weightDimensionsModalVisible, scanResult, speechSupported, speechLang]);

  // 调试：localStorage 变更监控
  useEffect(() => {
    // 初始快照
    const computeSignature = (data: any) => {
      try {
        const core = {
          sortingDatabase: Array.isArray(data?.sortingDatabase)
            ? data.sortingDatabase.map((it: any) => ({
                inventoryUnitCode: it.inventoryUnitCode,
                temporaryLocation: it.temporaryLocation,
                sortedQuantity: it.sortedQuantity,
                sortingGroupSystemCode: it.sortingGroupSystemCode
              }))
            : [],
          nextLocationNumber: data?.nextLocationNumber || 0,
          pool: Array.isArray(data?.reusableLocationPool)
            ? [...data.reusableLocationPool].sort((a, b) => Number(a) - Number(b))
            : []
        };
        return JSON.stringify(core);
      } catch { return ''; }
    };

    const readBoard = () => {
      try {
        const raw = localStorage.getItem(SORTING_BOARD_KEY);
        const obj = raw ? JSON.parse(raw) : null;
        const data = obj?.data || null;
        const total = Array.isArray(data?.sortingDatabase) ? data.sortingDatabase.length : 0;
        const updatedAt = obj?.updatedAt || '';
        const signature = computeSignature(data);
        return { raw, total, updatedAt, signature } as { raw: string | null; total: number; updatedAt: string; signature: string };
      } catch { return { raw: null as any, total: 0, updatedAt: '', signature: '' } as any; }
    };
    const adoptBoardFromLocal = () => {
      try {
        const raw = localStorage.getItem(SORTING_BOARD_KEY);
        if (!raw) return false;
        const obj = JSON.parse(raw);
        const data = obj?.data || null;
        const updatedAt = obj?.updatedAt || '';
        if (!data || !Array.isArray(data.sortingDatabase)) return false;
        // 仅当内容签名变化时才采纳，避免仅 updatedAt 触发
        const incomingSig = computeSignature(data);
        if (incomingSig && lastKnownBoardSignatureRef.current && incomingSig === lastKnownBoardSignatureRef.current) {
          appendLsLog('adopt_skip_same', { updatedAt });
          return false;
        }
        setSortingDatabase(data.sortingDatabase);
        if (typeof data.nextLocationNumber === 'number' && data.nextLocationNumber > 0) {
          setNextLocationNumber(data.nextLocationNumber);
        } else {
          const maxLoc = data.sortingDatabase.reduce((max: number, it: any) => Math.max(max, it?.temporaryLocation || 0), 0);
          setNextLocationNumber(maxLoc + 1);
        }
        if (Array.isArray(data.reusableLocationPool)) {
          setReusableLocationPool(data.reusableLocationPool);
        }
        lastAdoptedUpdatedAtRef.current = updatedAt || new Date().toISOString();
        lastKnownBoardSignatureRef.current = incomingSig;
        appendLsLog('adopt_board', { total: data.sortingDatabase.length, next: data.nextLocationNumber, pool: Array.isArray(data.reusableLocationPool) ? data.reusableLocationPool.length : 0 });
        return true;
      } catch { return false; }
    };
    const readIndex = () => {
      try {
        const raw = localStorage.getItem(GLOBAL_RECORD_INDEX_KEY);
        const obj = raw ? JSON.parse(raw) : {};
        const ids = obj && typeof obj === 'object' ? Object.keys(obj) : [];
        return { raw, count: ids.length, ids: ids.slice(0, 20) };
      } catch { return { raw: null as any, count: 0, ids: [] as string[] }; }
    };

    let prevBoard = readBoard();
    let prevIndex = readIndex();
    console.log('[LS-Debug][INIT] BOARD len=', prevBoard.total, ' INDEX count=', prevIndex.count, ' ids=', prevIndex.ids);
    appendLsLog('INIT', { board_total: prevBoard.total, index_count: prevIndex.count, index_ids: prevIndex.ids });

    const onStorage = (e: StorageEvent) => {
      if (!e) return;
      if (e.key === SORTING_BOARD_KEY || e.key === GLOBAL_RECORD_INDEX_KEY) {
        const nextBoard = readBoard();
        const nextIndex = readIndex();
        console.log('[LS-Debug][storage]', {
          key: e.key,
          newValueLen: (e.newValue || '').length,
          oldValueLen: (e.oldValue || '').length,
          board_total: nextBoard.total,
          index_count: nextIndex.count,
          index_ids: nextIndex.ids
        });
        appendLsLog('storage', {
          key: e.key,
          newValueLen: (e.newValue || '').length,
          oldValueLen: (e.oldValue || '').length,
          board_total: nextBoard.total,
          index_count: nextIndex.count
        });
        if (e.key === SORTING_BOARD_KEY) {
          adoptBoardFromLocal();
        }
        prevBoard = nextBoard;
        prevIndex = nextIndex;
      }
    };
    window.addEventListener('storage', onStorage);

    const timer = window.setInterval(() => {
      const nextBoard = readBoard();
      const nextIndex = readIndex();
      // 以签名判定是否真的变化
      const boardChanged = prevBoard.signature !== nextBoard.signature;
      const indexChanged = (prevIndex.raw || '') !== (nextIndex.raw || '');
      if (boardChanged || indexChanged) {
        console.log('[LS-Debug][poll]', {
          board_changed: boardChanged,
          board_total_prev: prevBoard.total,
          board_total_next: nextBoard.total,
          index_changed: indexChanged,
          index_count_prev: prevIndex.count,
          index_count_next: nextIndex.count,
          index_ids_next: nextIndex.ids
        });
        appendLsLog('poll', {
          board_changed: boardChanged,
          board_total_prev: prevBoard.total,
          board_total_next: nextBoard.total,
          index_changed: indexChanged,
          index_count_prev: prevIndex.count,
          index_count_next: nextIndex.count
        });
        if (boardChanged) adoptBoardFromLocal();
        prevBoard = nextBoard;
        prevIndex = nextIndex;
      }
    }, 1500);

    return () => {
      window.removeEventListener('storage', onStorage);
      if (timer) window.clearInterval(timer);
    };
  }, []);

  // 分拣组选择模态框状态
  const [sortingGroupModalVisible, setSortingGroupModalVisible] = useState(false);
  const [pendingInventoryUnit, setPendingInventoryUnit] = useState<{
    code: string;
    inventoryUnit?: InventoryUnit;
    error?: string;
  } | null>(null);
  const [availableSortingGroups, setAvailableSortingGroups] = useState<Array<{
    name: string;
    systemCode: string;
  }>>([]);

  // 尺寸输入模态框状态
  const [dimensionsModalVisible, setDimensionsModalVisible] = useState(false);
  const [pendingDimensionsData, setPendingDimensionsData] = useState<{
    scannedValue: string;
    weight?: number;
    hasLogisticsPackage?: boolean;
    dimensions: {
      length?: number;
      width?: number;
      height?: number;
    };
  } | null>(null);

  // 快捷入库开关移除：统一在扫描容器时按容器声明判断是否直接快捷入库
  const quickInboundEnabled = false; // 保留只读常量以兼容现有分支与日志
  
  // 清理分拣看板数据的函数
  const handleClearSortingBoard = useCallback(() => {
    Modal.confirm({
      title: '确认清理分拣看板数据',
      content: '这将清除所有已保存的分拣看板数据，包括分拣数据库、位置信息等。此操作不可恢复，确定要继续吗？',
      okText: '确认清理',
      cancelText: '取消',
      okType: 'danger',
      onOk: () => {
        clearSortingBoardData();
        // 同时清理旧的localStorage数据（保持向后兼容）
        clearSortingBoardStateLocal();
        // 重置状态
        setSortingDatabase([]);
        setNextLocationNumber(1);
        setReusableLocationPool([]);
        message.success('分拣看板数据已清理');
      }
    });
  }, [clearSortingBoardData]);

  // 添加提交状态
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 入库层创建状态（调试/可视化）：面板显隐由用户手动切换，默认隐藏；后台轮询与显隐无关（成功/失败仍会有 message 提示）
  const [layerStatusVisible, setLayerStatusVisible] = useState<boolean>(false);
  const [layerStatusText, setLayerStatusText] = useState<string>('');
  const layerPollTimerRef = useRef<number | null>(null);
  const [layerPollInProgress, setLayerPollInProgress] = useState<boolean>(false);
  const stopLayerPolling = useCallback(() => {
    if (layerPollTimerRef.current) {
      window.clearTimeout(layerPollTimerRef.current as any);
      layerPollTimerRef.current = null;
    }
    setLayerPollInProgress(false);
  }, []);
  const startLayerPolling = useCallback(async (unitIds: number[], warehouseId?: number) => {
    try {
      setLayerPollInProgress(true);
      const maxAttempts = 30;
      const intervalMs = 1500;
      let attempt = 0;
      const pollOnce = async () => {
        try {
          attempt += 1;
          const params: any = { unit_ids: unitIds.join(',') };
          if (warehouseId) params.warehouse_id = warehouseId;
          const res = await request.get('/api/v1/inventory/inventory-units/receipt-layer-status/', { params });
          const units = (res?.data?.units) || {};
          const total = unitIds.length;
          const done = unitIds.filter(id => (units[id]?.count || 0) > 0).length;
          setLayerStatusText(`建层进度：${done}/${total} (尝试${attempt}/${maxAttempts})`);
          if (done >= total) {
            stopLayerPolling();
            message.success('入库层已创建完成');
            return;
          }
          if (attempt >= maxAttempts) {
            stopLayerPolling();
            message.warning('入库层创建未完成，已达最大尝试次数，请查看后端日志');
            return;
          }
          layerPollTimerRef.current = window.setTimeout(pollOnce, intervalMs) as any;
        } catch (e) {
          stopLayerPolling();
          message.warning('查询建层状态失败，请稍后重试');
        }
      };
      await pollOnce();
    } catch {
      stopLayerPolling();
    }
  }, [stopLayerPolling]);

  // 入库备注状态
  const [inboundRemarks, setInboundRemarks] = useState('');
  // 卸载清理轮询
  useEffect(() => {
    return () => {
      if (layerPollTimerRef.current) {
        window.clearTimeout(layerPollTimerRef.current as any);
        layerPollTimerRef.current = null;
      }
    };
  }, []);

  // 非常规操作选项
  const irregularOperationOptions = [
    { value: 'exception_inventory_inbound', label: '异常库存入库' },
    { value: 'original_box_pallet_inbound', label: '原箱/原托盘入库' },
    { value: 'accessories_materials_labels_inbound', label: '配件/包材/标签等非上架货物入库' },
    { value: 'return_inspection', label: '退货入库初检' },
    { value: 'original_package_direct_outbound', label: '原包装免上架直接出库' },
    { value: 'other', label: '其他' }
  ];

  // 获取操作类型的中文名称
  const getIrregularOperationLabel = (value: string): string => {
    // 先检查非常规操作选项
    const option = irregularOperationOptions.find(opt => opt.value === value);
    if (option) {
      return option.label;
    }
    
    // 处理其他操作类型
    const otherOperations: Record<string, string> = {
      'scan_inbound': '扫描入库'
    };
    
    return otherOperations[value] || value;
  };

  // 非常规操作状态
  const [irregularOperation, setIrregularOperation] = useState<string>('');
  // 当非常规操作被设为"原箱/原托盘入库"或"配件/包材/标签等"时（包含自动设置与手动选择），
  // 若存在扫描值且备注为空，则自动填入备注
  useEffect(() => {
    try {
      const sv = (currentScanValue || '').trim();
      if (irregularOperation === 'original_box_pallet_inbound') {
        if (sv && (!originalContainerRemarks || !originalContainerRemarks.trim())) {
          setOriginalContainerRemarks(sv);
          setOriginalRemarksLocked(true);
        }
      } else if (irregularOperation === 'accessories_materials_labels_inbound') {
        if (sv && (!accessoriesItemRemarks || !accessoriesItemRemarks.trim())) {
          setAccessoriesItemRemarks(sv);
          setAccessoriesRemarksLocked(true);
        }
      }
    } catch {}
  }, [irregularOperation, currentScanValue]);
  // 非常规操作类型记录（使用Set确保去重）
  const [operationTypes, setOperationTypes] = useState<Set<string>>(new Set());
  // 实际发生的非常规操作集合：仅在相关动作成功后记录
  const [performedIrregularOps, setPerformedIrregularOps] = useState<Set<string>>(new Set());
  // 备注锁定状态（默认锁定，用户可"取消禁用"解锁）
  const [originalRemarksLocked, setOriginalRemarksLocked] = useState<boolean>(true);
  const [accessoriesRemarksLocked, setAccessoriesRemarksLocked] = useState<boolean>(true);
  // 库存单元标签预览状态（箱/托盘 4x6）
  const [inventoryUnitLabelPreviewVisible, setInventoryUnitLabelPreviewVisible] = useState(false);
  const [inventoryUnitLabelPreviewData, setInventoryUnitLabelPreviewData] = useState<InventoryLabelData | undefined>(undefined);
  
  // 计算库存单元扫描框是否应该被禁用
  const isInventoryUnitScanDisabled = irregularOperation !== '' && irregularOperation !== null && irregularOperation !== undefined;
  
  // 原箱/原托盘类型状态
  const [originalContainerType, setOriginalContainerType] = useState<string>('');

  // 原箱/原托盘尺寸和重量状态
  const [originalContainerDimensions, setOriginalContainerDimensions] = useState<{
    length: number | undefined;
    width: number | undefined;
    height: number | undefined;
    weight?: number;
  }>({
    length: undefined,
    width: undefined,
    height: undefined
  });
  // 原箱/原托盘"高"输入框引用：选择"托盘"自动填入长宽后，聚焦到高，方便直接输入
  const originalContainerHeightInputRef = useRef<HTMLInputElement>(null);
  // 原箱/原托盘"长"输入框引用：选择"箱"清空长宽后，聚焦到长，方便直接输入
  const originalContainerLengthInputRef = useRef<HTMLInputElement>(null);
  
  // 原箱/原托盘备注状态
  const [originalContainerRemarks, setOriginalContainerRemarks] = useState('');
  // 原箱/原托盘第三方参考号（箱子/托盘上已贴的其他WMS系统箱标号码）和"无第三方参考号"勾选状态
  const [originalContainerThirdPartyRef, setOriginalContainerThirdPartyRef] = useState('');
  const [originalContainerNoThirdPartyRef, setOriginalContainerNoThirdPartyRef] = useState(false);
  // 托盘"重量为预估"：仓库暂无托盘称重设备，默认勾选；有准确重量时取消勾选
  const [originalContainerWeightEstimated, setOriginalContainerWeightEstimated] = useState(true);
  
  // 原箱/原托盘数量状态
  const [originalContainerQuantity, setOriginalContainerQuantity] = useState(1);
  
  // 创建库存单元状态
  const [isCreatingInventoryUnit, setIsCreatingInventoryUnit] = useState(false);
  
  // 配件/包材/标签相关状态
  const [accessoriesItemType, setAccessoriesItemType] = useState<string>('');
  const [accessoriesItemDimensions, setAccessoriesItemDimensions] = useState<{
    length: number | undefined;
    width: number | undefined;
    height: number | undefined;
    weight?: number;
  }>({
    length: undefined,
    width: undefined,
    height: undefined
  });
  // 原包装免上架直接出库 重量(g)
  const [directOutboundWeightG, setDirectOutboundWeightG] = useState<number | undefined>(undefined);
  const [accessoriesItemRemarks, setAccessoriesItemRemarks] = useState('');
  const [accessoriesItemQuantity, setAccessoriesItemQuantity] = useState(1);
  const [isCreatingAccessoriesInventoryUnit, setIsCreatingAccessoriesInventoryUnit] = useState(false);
  
  // 异常库存相关状态
  // 重命名：总重量(g) 独立字段，避免与尺寸混淆
  const [exceptionTotalWeightG, setExceptionTotalWeightG] = useState<number | undefined>(undefined);
  // 会话累计的异常库存总重量（同一入库单多次异常录入时用于整单加权）
  const [exceptionSessionTotalWeightG, setExceptionSessionTotalWeightG] = useState<number>(0);
  // 兼容保留旧的尺寸状态（但不再使用）
  const [exceptionInventoryDimensions, setExceptionInventoryDimensions] = useState<{
    length: number | undefined;
    width: number | undefined;
    height: number | undefined;
  }>({
    length: undefined,
    width: undefined,
    height: undefined
  });
  const [exceptionInventoryQuantity, setExceptionInventoryQuantity] = useState(1);
  const [isCreatingExceptionInventoryUnit, setIsCreatingExceptionInventoryUnit] = useState(false);
  // 新仓储容器创建模态框状态
  const [createContainerModalOpen, setCreateContainerModalOpen] = useState(false);
  // 仓储标签预览相关状态
  const [labelPreviewVisible, setLabelPreviewVisible] = useState(false);
  const [labelPreviewData, setLabelPreviewData] = useState<StorageContainerLabelData | undefined>(undefined);
  // "生成-打印 新仓储容器"后的标签预览：这个容器来自哪个临时分拣仓位（按容器编码对应，避免串到别的预览）
  const [labelPreviewLocation, setLabelPreviewLocation] = useState<{ containerCode: string; location: number } | null>(null);
  
  // 添加到已有容器模态框状态
  const [addToContainerModalOpen, setAddToContainerModalOpen] = useState(false);
  const [availableContainers, setAvailableContainers] = useState<any[]>([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [addToContainerForm, setAddToContainerForm] = useState<AddToContainerForm>({
    selectedContainerCode: '',
    items: [],
    groupInfo: {
      groupName: '',
      groupSystemCode: '',
      temporaryLocation: 0,
    }
  });
  const [addToContainerRemarks, setAddToContainerRemarks] = useState('');
  const [containerSelectionError, setContainerSelectionError] = useState('');

  
  const [createContainerForm, setCreateContainerForm] = useState<{
    groupName: string;
    groupSystemCode: string;
    temporaryLocation: number;
    containerType: 'box' | 'pallet' | 'tote_bag' | 'cart' | 'direct' | 'shipping_bag' | 'other';
    remarks: string;
    items: Array<{ inventoryUnitCode: string; inventoryUnitName?: string; quantity: number; selected: boolean; displaySortingBarcode?: string; sortingBarcodes?: string[] }>
  }>({
    groupName: '',
    groupSystemCode: '',
    temporaryLocation: 0,
    containerType: 'box',
    remarks: '',
    items: [],
  });

  useEffect(() => {
    // 调试：将状态暴露到 window
    try {
      // @ts-ignore
      (window as any).dbg = { ...(window as any).dbg, createOpen: createContainerModalOpen };
      console.log('[CreateContainerModal] open state changed:', createContainerModalOpen);
      if (createContainerModalOpen) {
        setTimeout(() => {
          try {
            const modals = Array.from(document.querySelectorAll('.ant-modal')) as HTMLElement[];
            const masks = Array.from(document.querySelectorAll('.ant-modal-mask')) as HTMLElement[];
            const bodyStyle = getComputedStyle(document.body);
            console.log('[CreateContainerModal][DOM] modal.count=', modals.length, 'mask.count=', masks.length);
            modals.forEach((el, i) => console.log(`[CreateContainerModal][DOM][Modal#${i}] zIndex=${el.style.zIndex} rect=`, el.getBoundingClientRect()));
            masks.forEach((el, i) => console.log(`[CreateContainerModal][DOM][Mask#${i}] zIndex=${el.style.zIndex} rect=`, el.getBoundingClientRect()));
            console.log('[CreateContainerModal][DOM] body.overflow=', bodyStyle.overflow, ' body.position=', bodyStyle.position);
          } catch (e) {
            console.warn('[CreateContainerModal][DOM] inspect error:', e);
          }
        }, 300);
      }
    } catch {}
  }, [createContainerModalOpen]);

  // 创建容器弹窗：最近一次入库记录快照（本地存储）与"填入最近入库记录"的对照结果
  const [lastRecordSnapshot, setLastRecordSnapshot] = useState<LastInboundRecordSnapshot | null>(null);
  const [lastRecordFill, setLastRecordFill] = useState<{ rows: FillRow[]; ignoredCodes: string[]; filledCount: number } | null>(null);
  const [fillingLastRecord, setFillingLastRecord] = useState(false);

  // 点击"填入最近入库记录"：先向后端核对该入库记录（未作废、数量以记录当前明细为准），
  // 再把同一分拣组内的库存单元勾选并填入 min(记录数量, 看板现有数量)
  const handleFillFromLastRecord = async () => {
    const snapshot = lastRecordSnapshot;
    if (!snapshot) {
      message.info('暂无最近一次入库记录');
      return;
    }
    if (snapshot.warehouseCode && warehouseCode && snapshot.warehouseCode !== warehouseCode) {
      message.error(`最近入库记录属于仓库 ${snapshot.warehouseCode}，与当前仓库 ${warehouseCode} 不一致，不能填入`);
      return;
    }
    setFillingLastRecord(true);
    try {
      let recordItems: Array<{ inventoryUnitCode: string; quantity: number }> = [];
      try {
        const detail = await getInboundRecordDetail(snapshot.recordId);
        if (detail?.is_voided) {
          message.error('最近一次入库记录已作废，不能填入');
          return;
        }
        const detailItems = Array.isArray(detail?.items) ? detail.items : [];
        if (detailItems.length > 0) {
          recordItems = aggregateRecordItems(detailItems.map(it => ({
            code: it?.inventory_unit_detail?.system_barcode || '',
            quantity: Number(it?.quantity || 0)
          })));
        }
      } catch (err) {
        console.error('[填入最近入库记录] 核对入库记录失败', err);
        message.error('无法核对最近一次入库记录，请稍后重试');
        return;
      }
      // 后端明细里没有库存单元时，退回使用提交时保存的快照（同一分拣组）
      if (recordItems.length === 0) {
        recordItems = snapshot.items
          .filter(it => it.sortingGroupSystemCode === createContainerForm.groupSystemCode)
          .map(it => ({ inventoryUnitCode: it.inventoryUnitCode, quantity: it.quantity }));
      }
      const board = reclaimStateRef.current.sortingDatabase;
      const boardQuantityByCode: Record<string, number> = {};
      board.forEach(row => {
        boardQuantityByCode[row.inventoryUnitCode] = (boardQuantityByCode[row.inventoryUnitCode] || 0) + (row.sortedQuantity || 0);
      });
      const plan = planFillFromRecord({
        recordItems,
        modalItems: createContainerForm.items,
        boardQuantityByCode
      });
      if (plan.rows.length === 0) {
        setLastRecordFill(null);
        message.warning('最近一次入库记录不包含该分拣组的库存单元，未做任何填入');
        return;
      }
      setCreateContainerForm(prev => ({ ...prev, items: plan.items }));
      setLastRecordFill({ rows: plan.rows, ignoredCodes: plan.ignoredCodes, filledCount: plan.filledCount });
      if (plan.filledCount === 0) {
        message.warning('看板上没有可填入的数量，请核对对照表');
      } else {
        message.success(`已按最近入库记录填入 ${plan.filledCount} 个库存单元，请核对对照表后再创建`);
      }
    } finally {
      setFillingLastRecord(false);
    }
  };

  const openCreateContainerModal = (payload: { groupName: string; groupSystemCode: string; temporaryLocation: number; items: Array<{ inventoryUnitCode: string; inventoryUnitName?: string; quantity: number; displaySortingBarcode?: string; sortingBarcodes?: string[] }> }) => {
    setLastRecordSnapshot(loadLastInboundRecord());
    setLastRecordFill(null);
    setCreateContainerForm({
      groupName: payload.groupName,
      groupSystemCode: payload.groupSystemCode,
      temporaryLocation: payload.temporaryLocation,
      containerType: 'box',
      remarks: '',
      items: (payload.items || []).map(it => ({ 
        ...it, 
        selected: it.quantity > 0, // 只有数量大于0的才默认选中
        quantity: it.quantity || 0 // 确保数量有值
      })),
    });
    setCreateContainerModalOpen(true);
  };

  const handleCreateContainerCancel = () => setCreateContainerModalOpen(false);

  // 自动拆分：从后端获取可用余额(FIFO)并为每条 item 生成 splits；返回是否完全覆盖
  // 可传入上下文以覆盖全局 projectCode/warehouseCode（用于按分拣组精确定位项目）
  const autoAllocateSplits = async (
    items: Array<{ inventory_unit_code: string; quantity: number }>,
    ctx?: { project_code?: string; warehouse_code?: string }
  ) => {
    try {
      const unitCodes = Array.from(new Set(items.map(it => String(it.inventory_unit_code || '').trim()).filter(Boolean)));
      const effProjectCode = (ctx?.project_code || projectCode || '').trim();
      const effWarehouseCode = (ctx?.warehouse_code || warehouseCode || '').trim();
      if (!effProjectCode || !effWarehouseCode || unitCodes.length === 0) {
        return { full: false, mapped: items.map(it => ({ ...it, splits: [] })), shortages: items.map(it => ({ unit_code: it.inventory_unit_code, need: Number(it.quantity || 0), have: 0 })) };
      }
      const resp = await getInboundUnitBalances({ project_code: effProjectCode, warehouse_code: effWarehouseCode, unit_codes: unitCodes });
      const rows = Array.isArray(resp?.results) ? resp.results : [];
      const balanceMap = new Map<string, Array<{ inbound_record_id: number; available_qty: number }>>();
      rows.forEach((r: any) => {
        const code = String(r?.unit_code || '').trim();
        const balances = Array.isArray(r?.balances) ? r.balances : [];
        // FIFO 已排序，转换字段
        balanceMap.set(code, balances.map((b: any) => ({ inbound_record_id: Number(b.inbound_record_id), available_qty: Number(b.available_qty || 0) })).filter(b => b.inbound_record_id > 0 && b.available_qty > 0));
      });
      let allCovered = true;
      const shortages: Array<{ unit_code: string; need: number; have: number }> = [];
      const mapped = items.map(it => {
        const code = String(it.inventory_unit_code || '').trim();
        let need = Number(it.quantity || 0);
        const fifo = Array.from(balanceMap.get(code) || []);
        const splits: Array<{ inbound_record_id: number; qty: number }> = [];
        let acc = 0;
        for (const b of fifo) {
          if (need <= 0) break;
          const take = Math.min(need, b.available_qty);
          if (take > 0) {
            splits.push({ inbound_record_id: b.inbound_record_id, qty: take });
            acc += take;
            need -= take;
          }
        }
        if (need > 0) {
          allCovered = false;
          shortages.push({ unit_code: code, need: Number(it.quantity || 0), have: acc });
        }
        return { ...it, splits };
      });
      return { full: allCovered, mapped, shortages };
    } catch {
      return { full: false, mapped: items.map(it => ({ ...it, splits: [] })), shortages: items.map(it => ({ unit_code: it.inventory_unit_code, need: Number(it.quantity || 0), have: 0 })) };
    }
  };

  const handleCreateContainerConfirm = async () => {
    try {
      console.log('🔧 [创建容器] 开始执行创建容器操作');
      console.log('🔧 [创建容器] 当前表单数据:', {
        containerType: createContainerForm.containerType,
        groupSystemCode: createContainerForm.groupSystemCode,
        warehouseCode: warehouseCode,
        remarks: createContainerForm.remarks,
        itemsCount: createContainerForm.items.length
      });
      
      const selectedItems = createContainerForm.items.filter(it => it.selected && (it.quantity || 0) > 0);
      console.log('🔧 [创建容器] 选中的库存单元:', selectedItems.map(item => ({
        code: item.inventoryUnitCode,
        name: item.inventoryUnitName,
        quantity: item.quantity,
        selected: item.selected
      })));
      
      if (selectedItems.length === 0) {
        console.warn('⚠️ [创建容器] 没有选中的库存单元');
        message.warning('请至少选择一个库存单元');
        return;
      }

      // 构建请求数据
      const requestData: any = {
        container_type: createContainerForm.containerType,
        group_system_code: createContainerForm.groupSystemCode,
        warehouse_code: warehouseCode,
        remarks: createContainerForm.remarks,
        items: selectedItems.map(item => ({
          inventory_unit_code: item.inventoryUnitCode,
          quantity: item.quantity
        }))
      };

      // 直接存储：校验并传递存储仓位
      if (createContainerForm.containerType === 'direct') {
        const storageLocationCode = (createContainerForm as any).storageLocationCode;
        if (!storageLocationCode || !String(storageLocationCode).trim()) {
          message.error('直接存储必须填写"直接存储仓位"');
          return;
        }
        requestData.storage_location_code = String(storageLocationCode).trim();
      }
      
      // 自动拆分（FIFO+余额）。若可完全覆盖，则直接用 splits 覆盖；否则提示并退出
      // 从分拣组系统编码中精确提取项目编码用于 FIFO 查询
      const ctxProjectCode = extractProjectCodeFromGroupSystemCode(createContainerForm.groupSystemCode);
      const auto = await autoAllocateSplits(requestData.items || [], { project_code: ctxProjectCode, warehouse_code: warehouseCode });
      // 永不阻断：即便不足也继续提交。将可分配部分写入 splits，剩余由后端记为"待对账"
      requestData.items = auto.mapped;
      
      // 调用后端创建仓储容器接口
      console.log('🔧 [创建容器] 开始调用API...');
      const response = await createContainerFromSortingBoard(requestData);
      console.log('🔧 [创建容器] API调用成功，响应状态:', response.status);
      console.log('🔧 [创建容器] 完整响应对象:', response);
      console.log('🔧 [创建容器] 响应数据:', response.data);

              // 更新分拣看板中的分拣数量
        if (response.data.sorting_board_updates && response.data.sorting_board_updates.length > 0) {
          const updatedSortingDatabase = [...sortingDatabase];
          
          response.data.sorting_board_updates.forEach(update => {
            // 找到对应的分拣看板记录并更新分拣数量
            const recordIndex = updatedSortingDatabase.findIndex(
              record => record.inventoryUnitCode === update.inventory_unit_code
            );
            
            if (recordIndex !== -1) {
              const currentQuantity = updatedSortingDatabase[recordIndex].sortedQuantity || 0;
              const newQuantity = Math.max(0, currentQuantity - update.quantity_reduced);
              updatedSortingDatabase[recordIndex].sortedQuantity = newQuantity;
              
              console.log(`[分拣看板更新] ${update.inventory_unit_code}: ${currentQuantity} -> ${newQuantity} (减少 ${update.quantity_reduced})`);
            }
          });
          
          // 先检查并收集空仓位，然后再过滤数据
          const emptyLocations: number[] = [];
          const emptyLocationsByGroup: Set<string> = new Set();
          
          // 检查每个分拣组是否完全清空
          const sortingGroupStats = new Map<string, { totalQuantity: number; locations: Set<number> }>();
          
          updatedSortingDatabase.forEach(record => {
            const groupKey = record.sortingGroupSystemCode;
            if (!sortingGroupStats.has(groupKey)) {
              sortingGroupStats.set(groupKey, { totalQuantity: 0, locations: new Set() });
            }
            const stats = sortingGroupStats.get(groupKey)!;
            stats.totalQuantity += (record.sortedQuantity || 0);
            if (record.temporaryLocation) {
              stats.locations.add(record.temporaryLocation);
            }
          });
          
          // 找出完全清空的分拣组
          sortingGroupStats.forEach((stats, groupKey) => {
            if (stats.totalQuantity === 0 && stats.locations.size > 0) {
              emptyLocationsByGroup.add(groupKey);
              stats.locations.forEach(location => emptyLocations.push(location));
            }
          });
          
          // 将空仓位添加到可重用仓位池
          if (emptyLocations.length > 0) {
            setReusableLocationPool(prev => {
              const newPool = [...prev, ...emptyLocations];
              // 去重并排序，确保数字最小的仓位优先被重用
              const uniqueSortedPool = Array.from(new Set(newPool)).sort((a, b) => a - b);
              console.log(`[空仓位回收] 回收了 ${emptyLocations.length} 个空仓位: ${emptyLocations.join(', ')}，可重用仓位池更新为: ${uniqueSortedPool.join(', ')}`);
              return uniqueSortedPool;
            });
          }
          
          // 过滤掉 sortedQuantity 为 0 的库存单元记录
          const filteredSortingDatabase = updatedSortingDatabase.filter(record => (record.sortedQuantity || 0) > 0);
          
          // 更新状态
          setSortingDatabase(filteredSortingDatabase);
          
          // 自动保存更新后的分拣看板状态，包含更新后的可重用仓位池
          saveSortingBoardStateLocal({ 
            sortingDatabase: filteredSortingDatabase, 
            nextLocationNumber,
            reusableLocationPool: [...(reusableLocationPool || []), ...emptyLocations]
          });
        }

      // 容器创建成功后：直接存储跳过标签预览与打印；其余类型正常生成标签
      if (response.data && response.data.container) {
        if (createContainerForm.containerType === 'direct') {
          message.success(`仓储容器创建成功（直接存储）。系统编码：${response.data.container.system_code}`);
          try { sessionStorage.setItem('refresh_storage_containers', '1'); } catch {}
          fetchRecentContainers();
        } else {
          try {
            console.log('✅ [创建容器] 容器创建成功，开始生成标签');
            console.log('✅ [创建容器] 容器数据:', response.data.container);
            console.log('✅ [创建容器] 容器内容数量:', response.data.container_contents_count);
            console.log('✅ [创建容器] 分拣看板更新信息:', response.data.sorting_board_updates);
            
            // 创建内容摘要（包含详细的库存单元信息）
            const contentSummary = storageContainerAdapter.createContentSummary(
              selectedItems.map(item => ({
                name: item.inventoryUnitName || item.inventoryUnitCode,
                quantity: item.quantity,
                type: item.inventoryUnitName ? '已知物品' : '未知物品'
              }))
            );

            // 为标签添加详细的库存单元列表
            contentSummary.items = selectedItems.map(item => ({
              code: item.inventoryUnitCode,
              quantity: item.quantity
            }));

            // 使用仓储容器适配器转换数据
            const labelData = storageContainerAdapter.transform(
              response.data.container,
              {
                contentSummary,
                printOptions: {
                  copies: 1,
                  directPrint: true
                }
              }
            );

            console.log('[仓储容器标签] 标签数据准备完成:', labelData);

            // 显示标签预览而不是直接打印；预览里显示这个容器来自哪个临时分拣仓位
            setLabelPreviewLocation({ containerCode: String(labelData.containerInfo.systemCode || ''), location: createContainerForm.temporaryLocation });
            setLabelPreviewData(labelData);
            setLabelPreviewVisible(true);
            
            message.success(`仓储容器创建成功！系统编码：${response.data.container.system_code}，包含 ${response.data.container_contents_count} 个库存单元。请查看标签预览。`);
            console.log('[仓储容器标签] 标签预览数据已准备:', labelData);
            try { sessionStorage.setItem('refresh_storage_containers', '1'); } catch {}
            fetchRecentContainers();

          } catch (labelError) {
            console.error('[仓储容器标签] 标签处理失败:', labelError);
            const errorInfo = handleLabelError(labelError, '打印');
            message.success(`仓储容器创建成功！系统编码：${response.data.container.system_code}，包含 ${response.data.container_contents_count} 个库存单元`);
            message.warning(`标签打印失败：${errorInfo.details}`);
            try { sessionStorage.setItem('refresh_storage_containers', '1'); } catch {}
            fetchRecentContainers();
          }
        }
      } else {
        message.success('仓储容器创建成功！');
        try { sessionStorage.setItem('refresh_storage_containers', '1'); } catch {}
        fetchRecentContainers();
      }
      
      setCreateContainerModalOpen(false);
      
    } catch (err: any) {
      console.error('❌ [创建容器] 创建仓储容器失败');
      console.error('❌ [创建容器] 错误对象:', err);
      console.error('❌ [创建容器] 错误类型:', err.constructor.name);
      console.error('❌ [创建容器] 错误消息:', err.message);
      
      // 详细的错误信息
      if (err.response) {
        console.error('❌ [创建容器] HTTP响应状态:', err.response.status);
        console.error('❌ [创建容器] HTTP响应状态文本:', err.response.statusText);
        console.error('❌ [创建容器] HTTP响应头:', err.response.headers);
        console.error('❌ [创建容器] HTTP响应数据:', err.response.data);
        console.error('❌ [创建容器] 请求配置:', err.config);
      } else if (err.request) {
        console.error('❌ [创建容器] 请求已发送但无响应:', err.request);
        console.error('❌ [创建容器] 请求配置:', err.config);
      } else {
        console.error('❌ [创建容器] 请求配置错误:', err.config);
      }
      
      // 网络错误详情
      if (err.code) {
        console.error('❌ [创建容器] 错误代码:', err.code);
      }
      
      // 提取错误消息
      const errorMessage = err.response?.data?.detail || err.message || '创建仓储容器失败';
      console.error('❌ [创建容器] 最终显示给用户的错误消息:', errorMessage);
      
      message.error(errorMessage);
    }
  };

  // 项目编码提取公共函数
  const extractProjectCodeFromGroupSystemCode = (groupSystemCode: string): string => {
    console.log('[项目编码提取] 开始解析分拣组系统编码:', groupSystemCode);
    
    if (!groupSystemCode) {
      console.warn('[项目编码提取] ⚠️ 分拣组系统编码为空');
      return '';
    }
    
    try {
      const parts = groupSystemCode.split('-');
      console.log('[项目编码提取] 分割后的部分:', parts);
      
      if (parts.length === 3 && parts[0] === 'SG') {
        const projectCode = parts[1];
        console.log('[项目编码提取] ✅ 成功提取项目编码:', projectCode);
        return projectCode;
      } else {
        console.warn('[项目编码提取] ⚠️ 分拣组系统编码格式无效:', {
          input: groupSystemCode,
          parts: parts,
          expectedFormat: 'SG-项目编码-序号'
        });
      }
    } catch (error) {
      console.error('[项目编码提取] ❌ 解析分拣组系统编码失败:', error);
    }
    return '';
  };
  // 添加到已有容器相关函数
  const openAddToContainerModal = (payload: { groupName: string; groupSystemCode: string; temporaryLocation: number; items: Array<{ inventoryUnitCode: string; inventoryUnitName?: string; quantity: number; displaySortingBarcode?: string; sortingBarcodes?: string[] }> }) => {
    console.log('=== 打开添加到已有容器模态框 ===');
    console.log('[模态框] 接收的payload:', payload);
    
    // 设置分拣组信息
    const groupInfo = {
      groupName: payload.groupName,
      groupSystemCode: payload.groupSystemCode,
      temporaryLocation: payload.temporaryLocation,
    };
    
    console.log('[模态框] 分拣组信息:', groupInfo);
    
    // 设置库存单元列表，只有数量大于0的才默认选中
    const items = (payload.items || []).map(item => ({
      inventoryUnitCode: item.inventoryUnitCode,
      inventoryUnitName: item.inventoryUnitName,
      maxQuantity: item.quantity,
      selectedQuantity: item.quantity || 0, // 确保数量有值
      selected: (item.quantity || 0) > 0, // 只有数量大于0的才默认选中
    }));
    
    setAddToContainerForm({
      selectedContainerCode: '',
      items,
      groupInfo,
    });
    
    // 加载可用容器列表 - 直接传递groupSystemCode，避免状态更新时序问题
    fetchAvailableContainers(groupInfo.groupSystemCode);
    
    setAddToContainerModalOpen(true);
  };

  const fetchAvailableContainers = async (groupSystemCode?: string) => {
    setLoadingContainers(true);
    try {
      // 提取当前分拣组的项目编码进行筛选
      // 优先使用传入的参数，否则从状态中获取
      const targetGroupSystemCode = groupSystemCode || addToContainerForm.groupInfo.groupSystemCode;
      const currentProjectCode = extractProjectCodeFromGroupSystemCode(targetGroupSystemCode);
      
      console.log('=== 容器筛选调试信息开始 ===');
      console.log('[容器筛选] 传入的groupSystemCode参数:', groupSystemCode);
      console.log('[容器筛选] 状态中的groupSystemCode:', addToContainerForm.groupInfo.groupSystemCode);
      console.log('[容器筛选] 实际使用的groupSystemCode:', targetGroupSystemCode);
      console.log('[容器筛选] 当前分拣组项目编码:', currentProjectCode);
      console.log('[容器筛选] 当前仓库编码:', warehouseCode);
      
      const requestParams = {
        warehouse_code: warehouseCode,
        project_code: currentProjectCode, // 使用分拣组的项目编码进行筛选
        sorting_group_system_code: targetGroupSystemCode, // 新增：按分拣组system_code筛选
        limit: 500 // 增大限制以获取更多同项目容器
      };
      
      console.log('[容器筛选] API请求参数:', requestParams);
      console.log('[容器筛选] 开始调用 getAvailableContainers API...');
      
      // 特别检查 project_code 参数
      if (!requestParams.project_code) {
        console.error('🚨 [容器筛选] 严重错误：project_code 参数为空！', {
          currentProjectCode,
          requestParams,
          groupSystemCode: addToContainerForm.groupInfo.groupSystemCode
        });
      } else {
        console.log('✅ [容器筛选] project_code 参数正常:', requestParams.project_code);
      }
      
      const response = await getAvailableContainers(requestParams);
      
      // 检查实际发送的请求URL
      console.log('📡 [容器筛选] 实际请求的URL应该包含 project_code=' + requestParams.project_code);
      
      console.log('[容器筛选] API响应状态:', response.status);
      console.log('[容器筛选] API响应完整数据:', response.data);
      console.log('[容器筛选] 原始容器数量:', response.data.containers?.length || 0);
      
      if (response.data.containers && response.data.containers.length > 0) {
        console.log('[容器筛选] 前3个原始容器示例:', response.data.containers.slice(0, 3));
        
        // 检查每个容器的项目编码
        const projectCodes = Array.from(new Set(response.data.containers.map(c => c.project_code)));
        console.log('[容器筛选] 返回容器的所有项目编码:', projectCodes);
        console.log('[容器筛选] 是否只包含目标项目编码?', projectCodes.length === 1 && projectCodes[0] === currentProjectCode);
      } else {
        console.warn('[容器筛选] ⚠️ API返回空容器列表');
      }
      
      // 转换数据格式以适应现有的UI组件
      const formattedContainers = response.data.containers.map(container => ({
        system_code: container.system_code,
        name: container.name || container.system_code,
        type: container.container_type_display || container.container_type,
        status: container.status_display || container.status,
        warehouse_name: container.warehouse_name,
        project_name: container.project_name,
        project_code: container.project_code,
        current_location_code: (container as any).current_location_code || null,
        created_at: container.created_at,
        remarks: container.remarks
      }));
      
      setAvailableContainers(formattedContainers);
      
      console.log(`[容器筛选] 格式化后容器数量: ${formattedContainers.length}`);
      console.log('[容器筛选] 格式化后的前3个容器:', formattedContainers.slice(0, 3));
      
      if (formattedContainers.length > 0) {
        // 验证格式化后的容器项目编码
        const formattedProjectCodes = Array.from(new Set(formattedContainers.map(c => c.project_code)));
        console.log('[容器筛选] 格式化后容器的所有项目编码:', formattedProjectCodes);
        console.log('[容器筛选] 格式化后是否只包含目标项目?', formattedProjectCodes.length === 1 && formattedProjectCodes[0] === currentProjectCode);
        
        console.log('[容器筛选] 第一个容器详情:', {
          system_code: formattedContainers[0].system_code,
          project_code: formattedContainers[0].project_code,
          project_name: formattedContainers[0].project_name,
          type: formattedContainers[0].type,
          status: formattedContainers[0].status
        });
      } else {
        console.warn('[容器筛选] ⚠️ 格式化后容器列表为空');
      }
      
      console.log('=== 容器筛选调试信息结束 ===');
    } catch (error: any) {
      console.error('获取可用容器列表失败:', error);
      const errorMessage = error.response?.data?.detail || error.message || '获取可用容器列表失败';
      message.error(errorMessage);
      setAvailableContainers([]); // 失败时设置为空数组
    } finally {
      setLoadingContainers(false);
    }
  };

  const handleAddToContainerCancel = () => {
    setAddToContainerModalOpen(false);
    setAddToContainerRemarks('');
    setContainerSelectionError(''); // 清空错误信息
    setAddToContainerForm({
      selectedContainerCode: '',
      items: [],
      groupInfo: {
        groupName: '',
        groupSystemCode: '',
        temporaryLocation: 0,
      }
    });
  };
  const handleAddToContainerConfirm = async () => {
    try {
      // 验证是否选择了容器
      if (!addToContainerForm.selectedContainerCode) {
        message.warning('请选择要添加到的容器');
        return;
      }

      // 验证是否有选中的库存单元
      const selectedItems = addToContainerForm.items.filter(item => item.selected && item.selectedQuantity > 0);
      if (selectedItems.length === 0) {
        message.warning('请至少选择一个库存单元并设置数量');
        return;
      }

      console.log('[添加到容器] 开始处理:', {
        containerCode: addToContainerForm.selectedContainerCode,
        groupInfo: addToContainerForm.groupInfo,
        selectedItems: selectedItems.map(item => ({
          code: item.inventoryUnitCode,
          quantity: item.selectedQuantity
        })),
        remarks: addToContainerRemarks
      });

      // 自动拆分（FIFO+余额）
      const req: any = {
        container_code: addToContainerForm.selectedContainerCode,
        group_system_code: addToContainerForm.groupInfo.groupSystemCode,
        remarks: addToContainerRemarks,
        items: selectedItems.map(item => ({
          inventory_unit_code: item.inventoryUnitCode,
          quantity: item.selectedQuantity
        }))
      };
      // 从分拣组系统编码中精确提取项目编码用于 FIFO 查询
      const ctxProjectCode = extractProjectCodeFromGroupSystemCode(addToContainerForm.groupInfo.groupSystemCode);
      const auto = await autoAllocateSplits(req.items || [], { project_code: ctxProjectCode, warehouse_code: warehouseCode });
      // 永不阻断：即便不足也继续提交。将可分配部分写入 splits，剩余由后端记为"待对账"
      req.items = auto.mapped;
      // 调用后端API添加到现有容器
      const response = await addToExistingContainer(req);

      console.log('[添加到容器] API响应:', response.data);

      // 更新分拣看板中的分拣数量 (复用现有逻辑)
      if (response.data.sorting_board_updates && response.data.sorting_board_updates.length > 0) {
        const updatedSortingDatabase = [...sortingDatabase];
        
        response.data.sorting_board_updates.forEach(update => {
          // 找到对应的分拣看板记录并更新分拣数量
          const recordIndex = updatedSortingDatabase.findIndex(
            record => record.inventoryUnitCode === update.inventory_unit_code
          );
          
          if (recordIndex !== -1) {
            const currentQuantity = updatedSortingDatabase[recordIndex].sortedQuantity || 0;
            const newQuantity = Math.max(0, currentQuantity - update.quantity_reduced);
            updatedSortingDatabase[recordIndex].sortedQuantity = newQuantity;
            
            console.log(`[分拣看板更新] ${update.inventory_unit_code}: ${currentQuantity} -> ${newQuantity} (减少 ${update.quantity_reduced})`);
          }
        });
        
        // 检查并收集空仓位
        const emptyLocations: number[] = [];
        const emptyLocationsByGroup: Set<string> = new Set();
        
        // 检查每个分拣组是否完全清空
        const sortingGroupStats = new Map<string, { totalQuantity: number; locations: Set<number> }>();
        
        updatedSortingDatabase.forEach(record => {
          const groupKey = record.sortingGroupSystemCode;
          if (!sortingGroupStats.has(groupKey)) {
            sortingGroupStats.set(groupKey, { totalQuantity: 0, locations: new Set() });
          }
          const stats = sortingGroupStats.get(groupKey)!;
          stats.totalQuantity += (record.sortedQuantity || 0);
          if (record.temporaryLocation) {
            stats.locations.add(record.temporaryLocation);
          }
        });
        
        // 找出完全清空的分拣组
        sortingGroupStats.forEach((stats, groupKey) => {
          if (stats.totalQuantity === 0 && stats.locations.size > 0) {
            emptyLocationsByGroup.add(groupKey);
            stats.locations.forEach(location => emptyLocations.push(location));
          }
        });
        
        // 将空仓位添加到可重用仓位池
        if (emptyLocations.length > 0) {
          setReusableLocationPool(prev => {
            const newPool = [...prev, ...emptyLocations];
            // 去重并排序，确保数字最小的仓位优先被重用
            const uniqueSortedPool = Array.from(new Set(newPool)).sort((a, b) => a - b);
            console.log(`[空仓位回收] 回收了 ${emptyLocations.length} 个空仓位: ${emptyLocations.join(', ')}，可重用仓位池更新为: ${uniqueSortedPool.join(', ')}`);
            return uniqueSortedPool;
          });
        }
        
        // 过滤掉 sortedQuantity 为 0 的库存单元记录
        const filteredSortingDatabase = updatedSortingDatabase.filter(record => (record.sortedQuantity || 0) > 0);
        
        // 更新状态
        setSortingDatabase(filteredSortingDatabase);
        
        // 自动保存更新后的分拣看板状态
        saveSortingBoardStateLocal({ 
          sortingDatabase: filteredSortingDatabase, 
          nextLocationNumber,
          reusableLocationPool: [...(reusableLocationPool || []), ...emptyLocations]
        });

        console.log(`[分拣看板更新] 更新了 ${response.data.sorting_board_updates.length} 个库存单元的分拣数量`);
      }

      // 成功提示
      const { added_contents_count, updated_contents_count } = response.data;
      let successMsg = `成功添加 ${selectedItems.length} 个库存单元到容器 ${addToContainerForm.selectedContainerCode}`;
      if (added_contents_count > 0 && updated_contents_count > 0) {
        successMsg += `（新增 ${added_contents_count} 项，更新 ${updated_contents_count} 项）`;
      } else if (added_contents_count > 0) {
        successMsg += `（新增 ${added_contents_count} 项）`;
      } else if (updated_contents_count > 0) {
        successMsg += `（更新 ${updated_contents_count} 项）`;
      }
      
      message.success(successMsg);
      
      // 关闭模态框
      handleAddToContainerCancel();
      
    } catch (error: any) {
      console.error('添加到容器失败:', error);
      const errorMessage = error.response?.data?.detail || error.message || '添加到容器失败';
      message.error(errorMessage);
    }
  };
  const handleContainerSelect = async (containerCode: string) => {
    if (!containerCode || !containerCode.trim()) {
      console.log('[容器选择] 容器编码为空，清空选择');
      setAddToContainerForm(prev => ({
        ...prev,
        selectedContainerCode: ''
      }));
      return;
    }

    const trimmedCode = containerCode.trim();
    console.log('[容器选择] 处理容器编码:', trimmedCode, '(原始输入大小写)');

    // 查找选中的容器信息（先在本地列表中查找）- 大小写不敏感
    let selectedContainer = availableContainers.find(container => 
      container.system_code.toLowerCase() === trimmedCode.toLowerCase()
    );
    
    // 如果在本地列表中没找到，尝试从API获取容器信息
    if (!selectedContainer) {
      console.log('[容器选择] 本地列表中未找到，尝试从API获取容器信息:', trimmedCode);
      try {
        // 提取当前分拣组的项目编码，确保只查找同项目的容器
        const currentProjectCode = extractProjectCodeFromGroupSystemCode(
          addToContainerForm.groupInfo.groupSystemCode
        );
        
        console.log('[容器选择] 使用项目编码筛选:', currentProjectCode);
        
        // 调用API获取单个容器信息 - 仍然只查找同项目的容器
        const response = await getAvailableContainers({
          warehouse_code: warehouseCode,
          project_code: currentProjectCode, // 使用当前分拣组的项目编码
          sorting_group_system_code: addToContainerForm.groupInfo.groupSystemCode, // 新增：强一致的分拣组筛选
          limit: 500 // 增大限制，尝试获取更多容器
        });
        
        // 在API返回的完整列表中查找 - 大小写不敏感
        const apiContainer = response.data.containers.find(container => 
          container.system_code.toLowerCase() === trimmedCode.toLowerCase()
        );
        if (apiContainer) {
          selectedContainer = {
            system_code: apiContainer.system_code,
            name: apiContainer.name || apiContainer.system_code,
            type: apiContainer.container_type_display || apiContainer.container_type,
            status: apiContainer.status_display || apiContainer.status,
            warehouse_name: apiContainer.warehouse_name,
            project_name: apiContainer.project_name,
            project_code: apiContainer.project_code,
            created_at: apiContainer.created_at,
            remarks: apiContainer.remarks
          };
          console.log('[容器选择] 从API找到容器信息:', selectedContainer);
          console.log('[容器选择] 大小写转换: 用户输入', trimmedCode, '→ 数据库标准格式', selectedContainer.system_code);
        }
      } catch (error) {
        console.error('[容器选择] 获取容器信息失败:', error);
      }
    }
    
    if (selectedContainer) {
      // 由于API已经按项目筛选，理论上不应该出现项目不匹配的情况
      // 但仍保留验证作为安全检查
      const currentProjectCode = extractProjectCodeFromGroupSystemCode(
        addToContainerForm.groupInfo.groupSystemCode
      );
      const selectedContainerProjectCode = selectedContainer.project_code;
      
      // 输出调试日志
      console.log('=== 容器项目验证调试信息 ===');
      console.log('分拣组系统编码:', addToContainerForm.groupInfo.groupSystemCode);
      console.log('从分拣组系统编码解析的项目编码:', currentProjectCode);
      console.log('当前选择的目标容器所属项目编码:', selectedContainerProjectCode);
      console.log('项目编码是否一致:', currentProjectCode === selectedContainerProjectCode);
      console.log('选中容器信息:', {
        system_code: selectedContainer.system_code,
        name: selectedContainer.name,
        project_name: selectedContainer.project_name,
        project_code: selectedContainer.project_code
      });
      
      // 验证项目是否一致（理论上不应该失败，因为API已预筛选）
      if (currentProjectCode && selectedContainerProjectCode && currentProjectCode !== selectedContainerProjectCode) {
        console.error('⚠️ 数据异常：API预筛选后仍出现项目不匹配:', {
          current: currentProjectCode,
          selected: selectedContainerProjectCode,
          groupSystemCode: addToContainerForm.groupInfo.groupSystemCode
        });
        const errorMsg = `系统错误：容器项目不匹配（${selectedContainer.project_name}），请刷新页面重试`;
        setContainerSelectionError(errorMsg);
        return;
      }
      
      console.log('✅ 项目验证通过，允许选择容器');
      
      if (trimmedCode !== selectedContainer.system_code) {
        console.log('[容器选择] 大小写标准化: 用户输入', trimmedCode, '→ 数据库标准格式', selectedContainer.system_code);
      }
    } else {
      console.warn('❌ 未找到容器信息:', trimmedCode);
      const errorMsg = `未找到容器编码为 "${trimmedCode}" 的有效容器，请检查编码是否正确`;
      setContainerSelectionError(errorMsg);
      return;
    }
    
    // 清空错误信息
    setContainerSelectionError('');
    
    setAddToContainerForm(prev => ({
      ...prev,
      selectedContainerCode: selectedContainer.system_code // 使用数据库中的标准格式
    }));
  };

  const handleItemQuantityChange = (inventoryUnitCode: string, quantity: number) => {
    setAddToContainerForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.inventoryUnitCode === inventoryUnitCode 
          ? { ...item, selectedQuantity: Math.max(0, quantity) }
          : item
      )
    }));
  };

  const handleItemSelectionChange = (inventoryUnitCode: string, selected: boolean) => {
    setAddToContainerForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.inventoryUnitCode === inventoryUnitCode 
          ? { ...item, selected }
          : item
      )
    }));
  };

  const [currentInventoryUnit, setCurrentInventoryUnit] = useState<InventoryUnit | null>(null);
  const [actualWeightDimensions, setActualWeightDimensions] = useState({
    actual_weight: 0,
    actual_length: 0,
    actual_width: 0,
    actual_height: 0
  });
  const [confirmingWeightDimensions, setConfirmingWeightDimensions] = useState(false);
  // 库存配置相关状态
  const [inventoryConfig, setInventoryConfig] = useState<any>(null);
  const [barcodeToSortingGroupMap, setBarcodeToSortingGroupMap] = useState<{[key: string]: any}>({});
  const [inventoryUnitCodeToSortingGroupMap, setInventoryUnitCodeToSortingGroupMap] = useState<{[key: string]: any}>({});
  const [projectProducts, setProjectProducts] = useState<any[]>([]);
  
  // 入库记录相关状态
  const [inboundRecords, setInboundRecords] = useState<InboundRecord[]>([]);
  const [voidConfirming, setVoidConfirming] = useState(false);
  const [modifyVisible, setModifyVisible] = useState(false);
  const [modifySubmitting, setModifySubmitting] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const [modifyForm] = Form.useForm();
  // 批量标签打印预览状态
  const [labelBatchVisible, setLabelBatchVisible] = useState(false);
  const [labelBatchLoading, setLabelBatchLoading] = useState(false);
  const [labelBatchItems, setLabelBatchItems] = useState<Array<{
    unitId: number;
    systemBarcode: string;
    unitType: string;
    nameResolved: string;
    contentItems: Array<{ product_name: string; quantity: number }>;
    labelQuantity: number; // 标签张数：等于这条入库明细的入库数量
    labelData?: any; // InventoryLabelData
    previewUrl?: string;
    isPDF?: boolean;
    selected: boolean;
    loading: boolean;
    error?: string;
  }>>([]);
  const [labelBatchPrinting, setLabelBatchPrinting] = useState(false);
  // 合并后用于打印的隐藏 iframe：打印窗口可能还开着，所以不按定时器移除，下次打印或关闭弹窗时才清理
  const labelPrintFrameRef = useRef<{ iframe: HTMLIFrameElement; url: string } | null>(null);
  const removeLabelPrintFrame = () => {
    const frame = labelPrintFrameRef.current;
    if (!frame) return;
    labelPrintFrameRef.current = null;
    try { frame.iframe.remove(); } catch {}
    try { URL.revokeObjectURL(frame.url); } catch {}
  };
  useEffect(() => removeLabelPrintFrame, []);
  const [loadingInboundRecords, setLoadingInboundRecords] = useState(false);
  const [inboundRecordsPagination, setInboundRecordsPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // 可重用仓位池状态
  const [reusableLocationPool, setReusableLocationPool] = useState<number[]>([]);
  // 最近仓储容器列表
  const [recentContainers, setRecentContainers] = useState<any[]>([]);
  const [loadingRecentContainers, setLoadingRecentContainers] = useState(false);
  // 下部折叠面板展开状态（受控，避免刷新后折叠状态丢失）
  const [lowerActiveKeys, setLowerActiveKeys] = useState<Array<string>>(['recent-inbound', 'recent-containers']);
  // 保证指定面板处于"展开"状态（用于刷新按钮点击后强制展开）
  const ensureLowerPanelOpen = (key: string) => {
    setLowerActiveKeys(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(key) ? arr : [...arr, key];
    });
  };
  
  // ===== 调试面板：分拣看板与临时仓位快照 =====
  const debugBoardSnapshot = useMemo(() => {
    try {
      const byLocation = [...sortedSortingDatabase].reduce((acc: Record<number, any[]>, item) => {
        const key = Number(item.temporaryLocation) || 0;
        if (!acc[key]) acc[key] = [];
        acc[key].push({
          inventoryUnitCode: item.inventoryUnitCode,
          inventoryUnitName: item.inventoryUnitName || '-',
          sortedQuantity: item.sortedQuantity || 0,
          sortingGroup: item.sortingGroup,
          sortingGroupSystemCode: item.sortingGroupSystemCode,
          displaySortingBarcode: item.displaySortingBarcode,
          sortingBarcodes: item.sortingBarcodes || []
        });
        return acc;
      }, {});
      return {
        totalRecords: sortedSortingDatabase.length,
        nextLocationNumber,
        reusableLocationPoolCount: reusableLocationPool.length,
        reusableLocationPoolPreview: reusableLocationPool.slice(0, 20),
        locations: Object.keys(byLocation).sort((a, b) => Number(a) - Number(b)).map(k => ({
          temporaryLocation: Number(k),
          items: byLocation[Number(k)]
        }))
      };
    } catch (e) {
      return { error: String(e) } as any;
    }
  }, [sortedSortingDatabase, nextLocationNumber, reusableLocationPool]);
  
  // 添加扫描记录创建后自动聚焦的useEffect
  useEffect(() => {
    if (scanHistory.length > 0 && !scanningInventoryUnit && scanInputRef.current && !isInventoryUnitScanDisabled) {
      // 延迟聚焦，确保组件渲染完成
      setTimeout(() => {
        scanInputRef.current?.focus();
      }, 100);
    }
  }, [scanHistory.length, scanningInventoryUnit, isInventoryUnitScanDisabled]);

  // 已移除快捷入库开关逻辑


  
  // 真实搜索入库预报
  const handleSearchInboundForecasts = async (searchTerm: string) => {
    setIsSearching(true);
    setSearchError('');
    
    try {
      console.log('开始搜索入库预报:', searchTerm);
      
      // 构建搜索参数
      const searchParams: any = {
        page: 1,
        page_size: 50,
      };

      // 判断搜索词类型并设置相应的搜索参数
      if (searchTerm.startsWith('AF-IBF-')) {
        // 预报编号格式：AF-IBF-项目编码-序号
        searchParams.system_code = searchTerm;
      } else if (searchTerm.length >= 10 && /^[A-Z0-9]+$/.test(searchTerm)) {
        // 物流单号格式：通常是大写字母和数字组合，长度较长
        searchParams.tracking_number = searchTerm;
      } else {
        // 通用搜索
        searchParams.search = searchTerm;
      }

      // 如果指定了项目编码，添加到搜索条件
      if (projectCode) {
        searchParams.project_code = projectCode;
      }

      console.log('搜索参数:', searchParams);
      
      // 调用真实API
      const response = await searchInboundForecasts(searchParams);
      
      console.log('搜索结果:', response);
      
      setSearchResults(response.results);
      
      if (response.results.length === 0) {
        setSearchError('未找到相关的入库预报记录');
      }
    } catch (error: any) {
      console.error('搜索入库预报失败:', error);
      setSearchError(error.response?.data?.detail || '搜索失败，请重试');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // 验证容器扫描编码
  const validateContainerScanCode = (containerCode: string) => {
    console.log('开始验证容器扫描编码:', containerCode);
    
    // 1. 检查编码格式
    if (!containerCode.startsWith('AF-IBF-') && !containerCode.startsWith('IBF-')) {
      return {
        isValid: false,
        error: '编码格式不正确，必须以AF-IBF-或IBF-开头'
      };
    }
    
    // 2. 分割编码
    const parts = containerCode.split('-');
    if (parts.length < 6) {
      return {
        isValid: false,
        error: `编码格式不正确，至少需要6个部分，当前只有${parts.length}个部分`
      };
    }
    
    // 3. 提取各部分信息
    try {
      // 基础编码（去掉最后两个部分）
      const baseCode = parts.slice(0, -2).join('-');
      // 总数量（倒数第二个部分）
      const totalQuantity = parseInt(parts[parts.length - 2]);
      // 当前序号（倒数第一个部分）
      const sequenceNumber = parseInt(parts[parts.length - 1]);
      
      console.log('解析结果:', {
        baseCode,
        totalQuantity,
        sequenceNumber
      });
      
      // 4. 验证序号范围（总数量应该大于等于序号）
      if (sequenceNumber > totalQuantity) {
        return {
          isValid: false,
          error: `序号超出范围：${sequenceNumber} > ${totalQuantity}`
        };
      }
      
      // 5. 验证序号有效性
      if (sequenceNumber < 1) {
        return {
          isValid: false,
          error: `序号无效：${sequenceNumber} < 1`
        };
      }
      
      return {
        isValid: true,
        baseCode,
        totalQuantity,
        sequenceNumber,
        originalCode: containerCode
      };
      
    } catch (error) {
      return {
        isValid: false,
        error: `编码格式不正确，无法解析数量和序号: ${error}`
      };
    }
  };

  // 识别扫描类型
  const identifyScanType = (scannedCode: string) => {
    const code = scannedCode.trim();
    
    // 入库预报容器标签 - 以"IBF-"开头或包含"IBF-"
    if (code.startsWith('IBF-') || code.includes('IBF-')) {
      return '入库预报容器';
    }
    
    // 物流单号 - 其他格式
    return '物流单号';
  };
  // 物流单号匹配到多条预报时，记录操作人员在选择弹窗中的选择：
  // 具体预报对象 = 选中某条预报；null = 明确选择"不关联任何预报"；undefined = 尚未选择
  const trackingForecastSelectionRef = useRef<any>(undefined);

  // 物流单号匹配到多条预报时的选择表单：列出所有候选预报，供操作人员手动选择关联哪一条
  const TrackingForecastSelectForm: React.FC<{ candidates: any[] }> = ({ candidates }) => {
    const [selectedKey, setSelectedKey] = useState<string>('');

    const handleChange = (e: any) => {
      const key = e.target.value;
      setSelectedKey(key);
      if (key === 'none') {
        trackingForecastSelectionRef.current = null;
      } else {
        trackingForecastSelectionRef.current = candidates.find((c: any) => String(c.id) === key);
      }
    };

    return (
      <div>
        <Radio.Group onChange={handleChange} value={selectedKey} style={{ width: '100%' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {candidates.map((c: any) => {
              const forecastTotal = c.receiving_progress?.forecast_total ?? '-';
              const receivedTotal = c.receiving_progress?.received_total ?? '-';
              return (
                <Radio key={c.id} value={String(c.id)} style={{ width: '100%', whiteSpace: 'nowrap' }}>
                  {c.system_code} {c.name || ''}（创建时间：{c.created_at ? new Date(c.created_at).toLocaleString() : '-'}；收货进度：{forecastTotal}/{receivedTotal}）
                </Radio>
              );
            })}
            <Radio value="none">不关联任何预报，仅记录物流单号入库</Radio>
          </Space>
        </Radio.Group>
      </div>
    );
  };

  // 容器装载内容确认弹窗中，操作人员填写的实际入库数量/备注（Modal.confirm 的 content 只渲染一次，
  // 用 ref 保存最新值，供 onOk 时读取）
  const quickInboundConfirmValuesRef = useRef<{ actualQuantity: number; remarks: string }>({
    actualQuantity: 0,
    remarks: ''
  });

  // 容器装载内容确认弹窗标题：左侧固定文案，右侧蓝色显示容器编码
  const renderQuickInboundConfirmTitle = (containerCode: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>快捷入库 请确认容器装载内容</span>
      <span style={{ color: '#1677ff' }}>容器编码:{containerCode}</span>
    </div>
  );

  // 容器装载内容确认表单：展示库存单元系统编码/内容物/预报装载数量，并让操作人员填写实际入库数量与备注
  const QuickInboundConfirmForm: React.FC<{ contents: any[]; forecastRemarks?: string }> = ({ contents, forecastRemarks }) => {
    const forecastQuantity = contents[0]?.quantity ?? 0;
    const [actualQuantity, setActualQuantity] = useState<number>(forecastQuantity);
    const [remarks, setRemarks] = useState<string>('');

    useEffect(() => {
      quickInboundConfirmValuesRef.current = { actualQuantity, remarks };
    }, [actualQuantity, remarks]);

    return (
      <div>
        {contents.map((item: any, idx: number) => (
          <div
            key={idx}
            style={{
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: idx < contents.length - 1 ? '1px solid #f0f0f0' : 'none'
            }}
          >
            <div>
              库存单元编码：<strong>{item.inventory_unit?.system_code}</strong>
              {item.inventory_unit?.unit_type_display ? `（${item.inventory_unit.unit_type_display}）` : ''}
            </div>
            {Array.isArray(item.inventory_unit?.content_items) && item.inventory_unit.content_items.length > 0 && (
              <div>
                库存单元内容：
                {item.inventory_unit.content_items.map((ci: any, ciIdx: number) => (
                  <span key={ciIdx}>
                    {ci.product_name}(编码:{ci.product_custom_code || '-'} 条码:{ci.sorting_code || '-'})×{ci.quantity}件
                    {ciIdx < item.inventory_unit.content_items.length - 1 ? '、' : ''}
                  </span>
                ))}
              </div>
            )}
            <div style={{ marginTop: 4 }}>入库预报备注：<span style={{ color: '#f5222d' }}>{forecastRemarks || '-'}</span></div>
            <div style={{ marginTop: 4 }}>预报装载数量：<strong>{item.quantity}</strong></div>
          </div>
        ))}
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center' }}>
          <span>实际入库数量：</span>
          <InputNumber
            min={0}
            value={actualQuantity}
            onChange={(v) => setActualQuantity(Number(v) || 0)}
            style={{ width: 120, marginLeft: 8 }}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <div>备注：</div>
          <Input.TextArea
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="可选填写，将作为该入库记录的备注信息"
            style={{ marginTop: 4 }}
          />
        </div>
      </div>
    );
  };

  // 获取容器数据
  const fetchContainerData = async (containerCode: string) => {
    setLoadingContainer(true);
    try {
      console.log('开始获取容器数据:', containerCode);
      console.log('[DEBUG] fetchContainerData 调用参数:', containerCode);
      
      // 验证容器编码格式
      const validationResult = validateContainerScanCode(containerCode);
      if (!validationResult.isValid) {
        Modal.error({
          title: '容器编码验证失败',
          content: validationResult.error,
          centered: true
        });
        setContainerData(null);
        setIsModalVisible(false);
        setCurrentScanValue('');
        setScanType('');
        setActualSearchCode('');
        return;
      }
      
      console.log('容器编码验证通过:', validationResult);
      setActualSearchCode(containerCode);
      
      // 请求后端查找容器（使用完整编码进行验证）
      console.log('🔍 [容器数据获取] 开始调用后端API获取容器详情');
      const data = await getContainerDetails(containerCode);
      console.log('🔍 [容器数据获取] 后端API调用成功');
      console.log('🔍 [容器数据获取] 完整响应数据:', data);
      console.log('🔍 [容器数据获取] 容器对象:', data.container);
      console.log('🔍 [容器数据获取] 容器仓库信息:', data.container?.warehouse);
      console.log('🔍 [容器数据获取] 容器项目信息:', data.container?.inbound_forecast?.project);
      console.log('🔍 [容器数据获取] 快捷入库声明 is_quick_inbound_declared:', data.container?.is_quick_inbound_declared);

      // 验证预报状态：预报已取消的容器不能再通过扫描容器编码入库
      if (data.container?.inbound_forecast?.status === 'cancelled') {
        Modal.error({
          title: '预报已取消',
          content: `此容器关联的入库预报（系统编码：${data.container.inbound_forecast.system_code}）已取消，不能通过扫描容器编码进行入库。请改为扫描该容器上的物流运单号，以创建无预报关联的入库记录；如该容器没有可扫描的物流运单号，可使用"无单号入库"入口处理。`,
          centered: true
        });
        setContainerData(null);
        setIsModalVisible(false);
        setCurrentScanValue('');
        setScanType('');
        setActualSearchCode('');
        return;
      }

      // 根据预报信息的特殊指令自动设置非常规操作（和物流单号分支的处理逻辑保持一致）
      console.log('【容器分支】开始处理特殊指令映射');
      if (data.container?.inbound_forecast?.forecast_type === 'irregular' && data.container.inbound_forecast.special_instructions) {
        console.log('【容器分支】预报为非常规类型，特殊指令:', data.container.inbound_forecast.special_instructions);

        // 特殊指令到非常规操作的映射
        const specialInstructionsToIrregularOperationMap: Record<string, string> = {
          'accessories_materials_labels': 'accessories_materials_labels_inbound',
          'original_box_pallet_storage': 'original_box_pallet_inbound',
          'original_package_direct_outbound': 'original_package_direct_outbound',
          'return_inspection': 'return_inspection',
          'other': '其他'
        };

        const mappedOperation = specialInstructionsToIrregularOperationMap[data.container.inbound_forecast.special_instructions];
        if (mappedOperation) {
          setIrregularOperation(mappedOperation);
          console.log('【容器分支】自动设置非常规操作:', mappedOperation);
        } else {
          console.log('【容器分支】特殊指令无对应映射，清空非常规操作');
          setIrregularOperation('');
          setOriginalContainerType('');
          setOriginalContainerDimensions({
            length: undefined,
            width: undefined,
            height: undefined
          });
          setOriginalContainerRemarks('');
          setOriginalContainerThirdPartyRef('');
          setOriginalContainerNoThirdPartyRef(false);
          setOriginalContainerWeightEstimated(true);
        }
      } else {
        console.log('【容器分支】预报为常规类型或无特殊指令，清空非常规操作');
        setIrregularOperation('');
        setOriginalContainerType('');
        setOriginalContainerDimensions({
          length: undefined,
          width: undefined,
          height: undefined
        });
        setOriginalContainerRemarks('');
        setOriginalContainerThirdPartyRef('');
        setOriginalContainerNoThirdPartyRef(false);
        setOriginalContainerWeightEstimated(true);
      }

      // 验证项目匹配
      console.log('🔍 [项目验证] 开始验证项目匹配');
      console.log('🔍 [项目验证] 前端选中项目编码 projectCode:', projectCode);
      console.log('🔍 [项目验证] 容器项目信息:', data.container?.inbound_forecast?.project);
      console.log('🔍 [项目验证] 容器项目编码:', data.container?.inbound_forecast?.project?.project_code);
      
      if (projectCode && data.container?.inbound_forecast?.project?.project_code) {
        const containerProjectCode = data.container.inbound_forecast.project.project_code;
        console.log('🔍 [项目验证] 进行项目匹配验证');
        console.log('🔍 [项目验证] 前端选中项目编码 projectCode:', projectCode);
        console.log('🔍 [项目验证] 容器所属项目编码 containerProjectCode:', containerProjectCode);
        console.log('🔍 [项目验证] 项目编码是否匹配:', containerProjectCode === projectCode);
        
        if (containerProjectCode !== projectCode) {
          console.error('❌ [项目验证] 项目不匹配，验证失败');
          console.error('❌ [项目验证] 容器项目:', containerProjectCode);
          console.error('❌ [项目验证] 当前项目:', projectCode);
          Modal.error({
            title: '项目不匹配',
            content: `容器所属项目（${containerProjectCode}）与当前选择的项目（${projectCode}）不匹配，无法查看！`,
            centered: true
          });
          setContainerData(null);
          setIsModalVisible(false);
          setCurrentScanValue('');
          setScanType('');
          return;
        } else {
          console.log('✅ [项目验证] 项目匹配验证通过');
        }
      } else {
        console.log('⚠️ [项目验证] 跳过项目验证');
        console.log('⚠️ [项目验证] 原因:', {
          hasProjectCode: !!projectCode,
          hasContainerProject: !!data.container?.inbound_forecast?.project,
          hasContainerProjectCode: !!data.container?.inbound_forecast?.project?.project_code
        });
      }
      
      // 验证仓库匹配
      console.log('🔍 [仓库验证] 开始验证仓库匹配');
      console.log('🔍 [仓库验证] 前端选中仓库编码 warehouseCode:', warehouseCode);
      console.log('🔍 [仓库验证] 容器数据结构:', data.container);
      console.log('🔍 [仓库验证] 容器预报仓库信息:', data.container?.inbound_forecast?.warehouse);
      console.log('🔍 [仓库验证] 容器预报仓库编码:', data.container?.inbound_forecast?.warehouse?.code);
      
      if (warehouseCode && data.container?.inbound_forecast?.warehouse?.code) {
        const containerWarehouseCode = data.container.inbound_forecast.warehouse.code;
        console.log('🔍 [仓库验证] 进行仓库匹配验证');
        console.log('🔍 [仓库验证] 前端选中仓库编码 warehouseCode:', warehouseCode);
        console.log('🔍 [仓库验证] 容器所属仓库编码 containerWarehouseCode:', containerWarehouseCode);
        console.log('🔍 [仓库验证] 仓库编码是否匹配:', containerWarehouseCode === warehouseCode);
        
        if (containerWarehouseCode !== warehouseCode) {
          console.error('❌ [仓库验证] 仓库不匹配，验证失败');
          console.error('❌ [仓库验证] 容器仓库:', containerWarehouseCode);
          console.error('❌ [仓库验证] 当前仓库:', warehouseCode);
          Modal.error({
            title: '仓库不匹配',
            content: `容器所属仓库（${containerWarehouseCode}）与当前选择的仓库（${warehouseCode}）不匹配，无法查看！`,
            centered: true
          });
          setContainerData(null);
          setIsModalVisible(false);
          setCurrentScanValue('');
          setScanType('');
          return;
        } else {
          console.log('✅ [仓库验证] 仓库匹配验证通过');
        }
      } else {
        console.log('⚠️ [仓库验证] 跳过仓库验证');
        console.log('⚠️ [仓库验证] 原因:', {
          hasWarehouseCode: !!warehouseCode,
          hasContainerForecastWarehouse: !!data.container?.inbound_forecast?.warehouse,
          hasContainerForecastWarehouseCode: !!data.container?.inbound_forecast?.warehouse?.code
        });
      }
      
      // 通过所有一致性校验后：判断是否声明支持快捷入库
      if (data.container?.is_quick_inbound_declared === true) {
        console.log('✅ [快捷入库] 容器声明支持快捷入库，检查重量尺寸确认');

        // 兜底校验：快捷入库定义为无子容器且只装载一种库存单元
        const hasChild = Array.isArray(data.child_containers) && data.child_containers.length > 0;
        const contents = Array.isArray(data.contents) ? data.contents : [];
        if (hasChild || contents.length !== 1) {
          console.warn('容器不满足快捷入库前提（无子容器且仅一种库存单元），回退到常规处理');
          setContainerData(data);
          setIsModalVisible(true);
          return;
        }

        // 提取唯一库存单元条码
        const inventoryUnitBarcode: string | undefined = contents[0]?.inventory_unit?.system_code;
        if (!inventoryUnitBarcode) {
          console.error('无法获取容器内库存单元条码，回退到常规处理');
          setContainerData(data);
          setIsModalVisible(true);
          return;
        }

        try {
          const unit = await getLatestInventoryUnit(inventoryUnitBarcode);
          if (unit && unit.unit_type !== 'AIU' && !unit.weight_dimensions_confirmed) {
            // 需要先确认重量尺寸
            setActualWeightDimensions({
              actual_weight: unit.weight || 0,
              actual_length: unit.length || 0,
              actual_width: unit.width || 0,
              actual_height: unit.height || 0
            });
            setCurrentInventoryUnit(unit);
            setPendingQuickInbound({ containerCode, inventoryUnitBarcode, contents, forecastRemarks: data.container?.inbound_forecast?.remarks });
            setWeightDimensionsModalVisible(true);
            return; // 等待用户确认后再继续
          }
        } catch (e) {
          console.error('获取库存单元详情失败，回退到常规处理', e);
          setContainerData(data);
          setIsModalVisible(true);
          return;
        }

        // 已确认或AIU：弹出容器装载内容确认，用户确认后才继续执行快捷入库
        quickInboundConfirmValuesRef.current = { actualQuantity: contents[0]?.quantity ?? 0, remarks: '' };
        const shouldProceedQuickInbound = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: renderQuickInboundConfirmTitle(containerCode),
            content: <QuickInboundConfirmForm contents={contents} forecastRemarks={data.container?.inbound_forecast?.remarks} />,
            centered: true,
            width: 680,
            okText: '确认入库',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!shouldProceedQuickInbound) {
          setContainerData(null);
          setIsModalVisible(false);
          setCurrentScanValue('');
          setScanType('');
          setActualSearchCode('');
          setTimeout(() => {
            if (mainScanInputRef.current) {
              mainScanInputRef.current.focus();
            }
          }, 100);
          return;
        }
        const { actualQuantity: confirmedActualQuantity, remarks: confirmedRemarks } = quickInboundConfirmValuesRef.current;

        // 已确认或AIU：直接执行快捷入库
        const hideLoading = message.loading('正在处理快捷入库...', 0);
        try {
          const response = await quickInbound(containerCode, {
            actual_quantity: confirmedActualQuantity,
            remarks: confirmedRemarks
          });
          hideLoading();
          message.success(
            `快捷入库处理完成！\n` +
            `入库记录ID: ${response.inbound_record_id}\n` +
            `入库记录编码: ${response.inbound_record_code}\n` +
            `仓储容器编码: ${response.storage_container_code}\n` +
            `入库项目数量: ${response.inbound_items_count}\n` +
            `容器内容数量: ${response.container_contents_count}`
          );
          try { setCurrentInboundRecordId(Number(response?.inbound_record_id) || null); } catch {}

          if (response.storage_container_id) {
            try {
              const labelData: StorageContainerLabelData = {
                id: `storage-container-${response.storage_container_id}`,
                templateId: 'storage-container-4.25x1.25',
                containerInfo: {
                  id: response.storage_container_id,
                  systemCode: response.storage_container_code,
                  name: `仓储容器-${response.storage_container_code}`,
                  type: response.container_type || 'box',
                  typeDisplay: response.container_type_display || '箱子',
                  status: 'active',
                  statusDisplay: '正常',
                  warehouse: {
                    id: response.warehouse_id || 1,
                    name: response.warehouse_name || '未知仓库',
                    code: response.warehouse_code || 'UNKNOWN'
                  },
                  project: {
                    id: response.project_id || 1,
                    name: response.project_name || '未知项目',
                    code: response.project_code || 'UNKNOWN'
                  },
                  location: undefined,
                  createdBy: response.created_by_username || '系统用户',
                  createdAt: new Date().toLocaleString(),
                  updatedAt: new Date().toLocaleString()
                },
                contentSummary: {
                  totalItems: response.container_contents_count || 0,
                  totalQuantity: response.total_quantity || response.container_contents_count || 0,
                  itemTypes: response.container_contents?.map((item: any) => 
                    item.inventory_unit_name || '快捷入库物品'
                  ) || ['快捷入库物品'],
                  items: response.container_contents?.map((item: any) => ({
                    code: item.inventory_unit_code,
                    quantity: item.quantity
                  })) || []
                },
                printOptions: {
                  copies: 1,
                  directPrint: false
                }
              };
              setLabelPreviewData(labelData);
              setLabelPreviewVisible(true);
              message.info('仓储容器标签已生成，请查看预览');
            } catch (labelError) {
              console.error('【快捷入库】标签生成失败:', labelError);
            }
          }

          setTimeout(() => {
            if (scanInputRef.current) {
              scanInputRef.current.focus();
            }
          }, 300);
        } catch (error: any) {
          hideLoading?.();
          const errorMessage = error.response?.data?.error || '快捷入库处理失败，请重试';
          Modal.error({
            title: '快捷入库处理失败',
            content: errorMessage,
            centered: true
          });
          setTimeout(() => {
            if (mainScanInputRef.current) {
              mainScanInputRef.current.focus();
            }
          }, 100);
        }
        return;
      }

      // 未声明支持快捷入库：进入常规处理页面
      console.log('[DEBUG] 设置容器数据:', data);
      setContainerData(data);
      setIsModalVisible(true);
      
      // 显示验证成功信息
      if (data.container?.scan_validation) {
        const { sequence_number, total_quantity } = data.container.scan_validation;
        message.success(`验证通过：容器${sequence_number}/${total_quantity}`);
      }
      
    } catch (error: any) {
      console.error('获取容器数据失败:', error);
      const errorMessage = error.response?.data?.error || '获取容器数据失败';
      
      // 处理验证错误
      if (error.response?.status === 400) {
        Modal.error({
          title: '容器验证失败',
          content: errorMessage,
          centered: true
        });
        // 清空状态，回到扫描页面初始状态
        setContainerData(null);
        setIsModalVisible(false);
        setCurrentScanValue('');
        setScanType('');
        setActualSearchCode('');
      } else if (error.response?.status === 404) {
        Modal.error({
          title: '容器不存在',
          content: '无此容器',
          centered: true
        });
        // 清空状态，回到扫描页面初始状态
        setContainerData(null);
        setIsModalVisible(false);
        setCurrentScanValue('');
        setScanType('');
        setActualSearchCode('');
      } else {
        Modal.error({
          title: '获取容器数据失败',
          content: errorMessage,
          centered: true
        });
      }
      setContainerData(null);
    } finally {
      setLoadingContainer(false);
    }
  };
  // 清空扫描状态
  const clearScanStates = () => {
    setTrackingForecastData(null);
    setLoadingTrackingForecast(false);
    setIsModalVisible(false);
    setCurrentScanValue('');
    setScanType('');
    setScanValue('');
          setIrregularOperation('');  // 清空非常规操作选择
    setOriginalContainerType('');  // 清空原箱/原托盘类型
    setOriginalContainerDimensions({
      length: undefined,
      width: undefined,
      height: undefined
    });  // 清空原箱/原托盘尺寸
    setOriginalContainerRemarks('');  // 清空原箱/原托盘备注
    setOriginalContainerThirdPartyRef('');
    setOriginalContainerNoThirdPartyRef(false);
    setOriginalContainerWeightEstimated(true);
    setIsCreatingInventoryUnit(false);  // 清空创建状态

    // 清空配件/包材/标签等非上架产品相关状态
    setAccessoriesItemType('');
    setAccessoriesItemDimensions({
      length: undefined,
      width: undefined,
      height: undefined
    });
    setAccessoriesItemRemarks('');
    setIsCreatingAccessoriesInventoryUnit(false);

    // 重置库存单元数量
    setOriginalContainerQuantity(1);
    setAccessoriesItemQuantity(1);
    
    // 清空异常库存相关状态
    setExceptionInventoryDimensions({
      length: undefined,
      width: undefined,
      height: undefined
    });
    setExceptionInventoryQuantity(1);
    setIsCreatingExceptionInventoryUnit(false);
    setDirectOutboundWeightG(undefined);
    
    // 清空尺寸输入模态框相关状态
    setDimensionsModalVisible(false);
    setPendingDimensionsData(null);
  };



  // 显示项目不匹配的对话框
  const showProjectMismatchDialog = async (selectedProject: string, forecastProject: string) => {
    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '项目不匹配警告',
        content: (
          <div>
            <p>当前选择的项目：<strong>{selectedProject}</strong></p>
            <p>预报所属项目：<strong>{forecastProject}</strong></p>
            <p>是否继续处理该物流单号？</p>
          </div>
        ),
        okText: '继续处理',
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });
  };
  // 处理物流单号预报信息验证
  const processTrackingNumber = async (trackingNumber: string) => {
    try {
      console.log('【步骤2】开始获取物流单号预报信息');
      console.log('【步骤2】物流单号:', trackingNumber);
      console.log('【步骤2】当前项目编码:', projectCode);
      
      const forecastResponse = await getForecastByTrackingNumber(trackingNumber, {
        project_code: projectCode || undefined,
        warehouse_code: warehouseCode || undefined
      });
      console.log('【步骤2】预报信息API响应:', forecastResponse);
      const candidates = forecastResponse.results || [];
      console.log('【步骤2】预报信息结果数量:', candidates.length);

      let forecast: any = null;
      if (candidates.length === 1) {
        forecast = candidates[0];
      } else if (candidates.length > 1) {
        console.log('【步骤2】匹配到多条预报，等待用户手动选择:', candidates.map((c: any) => c.system_code));
        trackingForecastSelectionRef.current = undefined;
        const selected = await new Promise<any>((resolve) => {
          Modal.confirm({
            title: (
              <span>
                该物流单号<span style={{ color: '#1677ff' }}> {trackingNumber} </span>同时匹配到以下 {candidates.length} 条预报，请选择本次入库要关联的预报：
              </span>
            ),
            content: <TrackingForecastSelectForm candidates={candidates} />,
            centered: true,
            width: 800,
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
              if (trackingForecastSelectionRef.current === undefined) {
                message.warning('请先选择一条预报，或选择"不关联任何预报"');
                return Promise.reject();
              }
              resolve(trackingForecastSelectionRef.current);
            },
            onCancel: () => resolve(undefined)
          });
        });
        if (selected === undefined) {
          console.log('【步骤2】用户取消多预报选择，退回扫描页面');
          clearScanStates();
          return;
        }
        forecast = selected; // 具体预报对象，或 null（用户选择了"不关联任何预报"）
      }

      if (forecast) {
        console.log('【步骤2】找到对应预报信息:', forecast);
        console.log('【步骤2】预报系统编码:', forecast.system_code);
        console.log('【步骤2】预报项目信息:', forecast.project_detail);
        console.log('[DEBUG] 设置trackingForecastData:', forecast);
        
        // 项目匹配验证（当快捷入库开关打开时跳过）
        if (projectCode && forecast.project_detail?.project_code) {
          const forecastProjectCode = forecast.project_detail.project_code;
          console.log('【步骤2】前端选中项目编码:', projectCode);
          console.log('【步骤2】预报所属项目编码:', forecastProjectCode);
          console.log('【步骤2】项目匹配检查:', projectCode === forecastProjectCode ? '匹配' : '不匹配');
          
          if (forecastProjectCode !== projectCode) {
            console.warn('【步骤2】项目不匹配:', {
              selectedProject: projectCode,
              forecastProject: forecastProjectCode
            });
            
            // 弹出确认对话框
            console.log('【步骤2】显示项目不匹配确认对话框');
            const shouldContinue = await showProjectMismatchDialog(projectCode, forecastProjectCode);
            if (!shouldContinue) {
              console.log('【步骤2】用户取消处理，退回扫描页面');
              clearScanStates();
              return;
            } else {
              console.log('【步骤2】用户确认继续处理');
            }
          } else {
            console.log('【步骤2】项目匹配，继续处理');
          }
        } else {
          console.log('【步骤2】未选择项目编码或预报无项目信息，跳过项目匹配验证');
        }

        // 仓库匹配验证：比对当前选择的仓库编码与预报所属仓库编码
        if (warehouseCode && forecast.warehouse_detail?.code) {
          const forecastWarehouseCode = forecast.warehouse_detail.code;
          console.log('【步骤2】仓库匹配检查:', warehouseCode === forecastWarehouseCode ? '匹配' : '不匹配');
          if (forecastWarehouseCode !== warehouseCode) {
            console.warn('【步骤2】仓库不匹配:', {
              selectedWarehouse: warehouseCode,
              forecastWarehouse: forecastWarehouseCode
            });
            // 复用项目不匹配弹窗样式与交互文案
            const shouldContinueWarehouse = await new Promise<boolean>((resolve) => {
              Modal.confirm({
                title: '仓库不匹配警告',
                content: (
                  <div>
                    <p>当前选择的仓库：<strong>{warehouseCode}</strong></p>
                    <p>预报所属仓库：<strong>{forecastWarehouseCode}</strong></p>
                    <p>是否继续处理该物流单号？</p>
                  </div>
                ),
                okText: '继续处理',
                cancelText: '取消',
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
              });
            });
            if (!shouldContinueWarehouse) {
              console.log('【步骤2】用户取消处理（仓库不匹配），退回扫描页面');
              clearScanStates();
              return;
            } else {
              console.log('【步骤2】用户确认继续处理（仓库不匹配）');
            }
          }
        } else {
          console.log('【步骤2】跳过仓库匹配验证：缺少仓库编码或预报仓库信息');
        }
        
        setTrackingForecastData(forecast);
        message.success(`找到物流单号对应的预报信息：${forecast.system_code}`);
        console.log('【步骤2】预报信息设置完成');
        
        // 根据预报信息的特殊指令自动设置非常规操作
        console.log('【步骤2】开始处理特殊指令映射');
        if (forecast.forecast_type === 'irregular' && forecast.special_instructions) {
          console.log('【步骤2】预报为非常规类型，特殊指令:', forecast.special_instructions);
          
          // 特殊指令到非常规操作的映射
          const specialInstructionsToIrregularOperationMap: Record<string, string> = {
            'accessories_materials_labels': 'accessories_materials_labels_inbound',
            'original_box_pallet_storage': 'original_box_pallet_inbound',
            'original_package_direct_outbound': 'original_package_direct_outbound',
            'return_inspection': 'return_inspection',
            'other': '其他'
          };
          
          const mappedOperation = specialInstructionsToIrregularOperationMap[forecast.special_instructions];
          if (mappedOperation) {
            setIrregularOperation(mappedOperation);
            console.log('【步骤2】自动设置非常规操作:', mappedOperation);
                     } else {
             console.log('【步骤2】特殊指令无对应映射，清空非常规操作');
             setIrregularOperation('');
             setOriginalContainerType('');
            setOriginalContainerDimensions({
              length: undefined,
              width: undefined,
              height: undefined
            });
            setOriginalContainerRemarks('');
            setOriginalContainerThirdPartyRef('');
            setOriginalContainerNoThirdPartyRef(false);
            setOriginalContainerWeightEstimated(true);
          }
                 } else {
           console.log('【步骤2】预报为常规类型或无特殊指令，清空非常规操作');
           setIrregularOperation('');
           setOriginalContainerType('');
          setOriginalContainerDimensions({
            length: undefined,
            width: undefined,
            height: undefined
          });
          setOriginalContainerRemarks('');
          setOriginalContainerThirdPartyRef('');
          setOriginalContainerNoThirdPartyRef(false);
          setOriginalContainerWeightEstimated(true);
        }
      } else {
        console.log('【步骤2】未找到物流单号对应的预报信息');
        setTrackingForecastData(null);
        message.info('未找到该物流单号对应的预报信息');
      }
      
      // 物流单号不支持快捷入库，直接打开模态框
      console.log('【步骤2】物流单号不支持快捷入库，打开常规入库模态框');
      setCurrentScanValue(trackingNumber);
      setScanType('物流单号');
      setIsModalVisible(true);
      console.log('【步骤2】模态框已打开，等待用户操作');
      
      // 模态框打开后，聚焦到库存单元扫描框
      // 增加延迟时间，确保模态框完全渲染后再聚焦
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
          console.log('【步骤2】库存单元扫描框自动聚焦成功');
        } else {
          console.warn('【步骤2】库存单元扫描框ref未找到，无法自动聚焦');
        }
      }, 300);
      
      console.log('【步骤2】物流单号预报信息验证流程完成');
      
    } catch (error: any) {
      console.error('【步骤2】物流单号预报信息验证失败:', error);
      console.error('【步骤2】错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method
      });
      const errorMessage = error.response?.data?.detail || '物流单号预报信息验证失败';
      message.error(`${errorMessage}，请重试`);
    }
  };
  // 获取物流单号对应的预报信息
  const fetchTrackingForecastData = async (trackingNumber: string) => {
    setLoadingTrackingForecast(true);
    try {
      console.log('开始获取物流单号对应预报信息:', trackingNumber);
      
      const response = await getForecastByTrackingNumber(trackingNumber);
      console.log('物流单号预报信息获取成功:', response);
      
      if (response.results && response.results.length > 0) {
        const forecast = response.results[0];
        console.log('找到对应预报信息:', forecast);
        
        // 验证项目匹配
        if (projectCode && forecast.project_detail?.project_code) {
          const forecastProjectCode = forecast.project_detail.project_code;
          console.log('前端选中项目编码 projectCode:', projectCode);
          console.log('预报所属项目编码 forecastProjectCode:', forecastProjectCode);
          
          if (forecastProjectCode !== projectCode) {
            console.warn('项目不匹配:', {
              selectedProject: projectCode,
              forecastProject: forecastProjectCode
            });
            
            // 弹出确认对话框
            const shouldContinue = await new Promise<boolean>((resolve) => {
              Modal.confirm({
                title: '项目不匹配警告',
                content: (
                  <div>
                    <p>当前选择的项目：<strong>{projectCode}</strong></p>
                    <p>预报所属项目：<strong>{forecastProjectCode}</strong></p>
                    <p>是否继续处理该物流单号？</p>
                  </div>
                ),
                okText: '继续处理',
                cancelText: '取消',
                onOk: () => {
                  resolve(true);
                },
                onCancel: () => {
                  resolve(false);
                }
              });
            });
            
            if (!shouldContinue) {
              console.log('用户取消处理，退回扫描页面');
              // 清空状态，退回扫描页面
              setTrackingForecastData(null);
              setLoadingTrackingForecast(false);
              setIsModalVisible(false);
              setCurrentScanValue('');
              setScanType('');
              return;
            }
          }
        }
        
        setTrackingForecastData(forecast);
        message.success(`找到物流单号对应的预报信息：${forecast.system_code}`);
      } else {
        console.log('未找到物流单号对应的预报信息');
        setTrackingForecastData(null);
        message.info('未找到该物流单号对应的预报信息');
      }
      
    } catch (error: any) {
      console.error('获取物流单号预报信息失败:', error);
      const errorMessage = error.response?.data?.detail || '获取物流单号预报信息失败';
      message.error(errorMessage);
      setTrackingForecastData(null);
    } finally {
      setLoadingTrackingForecast(false);
    }
  };
  // 验证并获取容器数据（用于快捷入库模式）
  const validateAndGetContainerData = async (containerCode: string): Promise<any> => {
    try {
      console.log('验证并获取容器数据:', containerCode);
      
      // 验证容器编码格式
      const validationResult = validateContainerScanCode(containerCode);
      if (!validationResult.isValid) {
        console.error('容器编码验证失败:', validationResult.error);
        return null;
      }
      
      // 如果扫描的编码不包含AF-前缀，尝试添加前缀
      let searchCode = containerCode;
      if (!containerCode.startsWith('AF-') && containerCode.includes('IBF-')) {
        searchCode = `AF-${containerCode}`;
        console.log('自动添加AF-前缀，搜索编码:', searchCode);
      }
      
      const data = await getContainerDetails(searchCode);
      console.log('容器数据获取成功:', data);
      
      // 验证项目匹配
      if (projectCode && data.container?.inbound_forecast?.project?.project_code) {
        const containerProjectCode = data.container.inbound_forecast.project.project_code;
        
        if (containerProjectCode !== projectCode) {
          console.warn('项目不匹配:', {
            selectedProject: projectCode,
            containerProject: containerProjectCode
          });
          
          // 使用 Promise 包装 Modal.confirm
          return new Promise((resolve) => {
            Modal.confirm({
              title: '项目不匹配警告',
              content: (
                <div>
                  <p>当前选择的项目：<strong>{projectCode}</strong></p>
                  <p>容器所属项目：<strong>{containerProjectCode}</strong></p>
                  <p>是否继续处理该容器的快捷入库？</p>
                </div>
              ),
              okText: '继续处理',
              cancelText: '取消',
              onOk: () => {
                resolve(data);
              },
              onCancel: () => {
                resolve(null);
              }
            });
          });
        }
      }
      
      // 项目匹配或未选择项目，返回数据
      return data;
      
    } catch (error: any) {
      console.error('获取容器数据失败:', error);
      const errorMessage = error.response?.data?.error || '获取容器数据失败';
      message.error(errorMessage);
      return null;
    }
  };

  // 处理快捷入库逻辑
  const handleQuickInbound = async (scannedValue: string, scanType: string) => {
    console.log('=== 快捷入库模式开始 ===');
    console.log('扫描值:', scannedValue);
    console.log('扫描类型:', scanType);
    console.log('当前项目编码:', projectCode);
    
    try {
      // 1. 快捷入库模式下，项目信息将从预报容器中自动获取，无需验证
      console.log('【快捷入库】步骤1: 快捷入库模式，项目信息将从预报容器中自动获取');
      console.log('【快捷入库】当前项目编码:', projectCode || '未选择（将从预报容器获取）');
      
      // 2. 快捷入库仅支持预报容器扫描
      if (scanType !== '入库预报容器') {
        console.error('【快捷入库】错误: 快捷入库仅支持扫描入库预报容器编码');
        message.error('快捷入库仅支持扫描入库预报容器编码');
        // 错误提示后自动聚焦到主扫描框
        setTimeout(() => {
          if (mainScanInputRef.current) {
            mainScanInputRef.current.focus();
            console.log('【快捷入库】扫描类型错误后自动聚焦到主扫描框');
          }
        }, 100);
        return;
      }
      
      // 3. 验证容器编码格式
      console.log('【快捷入库】步骤2: 验证容器编码格式');
      const containerValidation = validateContainerScanCode(scannedValue);
      if (!containerValidation.isValid) {
        console.error('【快捷入库】容器编码验证失败:', containerValidation.error);
        message.error(`容器编码验证失败: ${containerValidation.error}`);
        // 错误提示后自动聚焦到主扫描框
        setTimeout(() => {
          if (mainScanInputRef.current) {
            mainScanInputRef.current.focus();
            console.log('【快捷入库】容器编码验证失败后自动聚焦到主扫描框');
          }
        }, 100);
        return;
      }
      console.log('【快捷入库】容器编码验证通过');
      
      // 4. 直接执行快捷入库流程
      console.log('【快捷入库】步骤3: 直接执行快捷入库流程');
      
      try {
        console.log('【快捷入库】开始执行快捷入库');
        
        // 显示处理中的提示
        const hideLoading = message.loading('正在处理快捷入库...', 0);
        console.log('【快捷入库】显示加载提示');

        // 5. 调用快捷入库API
        console.log('【快捷入库】步骤4: 调用快捷入库API');
        const response = await quickInbound(scannedValue);
        console.log('【快捷入库】API响应:', response);
        
        // 关闭加载提示
        hideLoading();
        console.log('【快捷入库】关闭加载提示');
        
        // 6. 显示成功提示
        message.success(
          `快捷入库处理完成！\n` +
          `入库记录ID: ${response.inbound_record_id}\n` +
          `入库记录编码: ${response.inbound_record_code}\n` +
          `仓储容器编码: ${response.storage_container_code}\n` +
          `入库项目数量: ${response.inbound_items_count}\n` +
          `容器内容数量: ${response.container_contents_count}`
        );
        console.log('【快捷入库】快捷入库处理完成:', response);

        // 主动刷新"最近入库记录"并设置聚焦刷新标记
        try { sessionStorage.setItem('refresh_inbound_records', '1'); } catch {}
        fetchInboundRecords();
        
        // 7. 自动生成仓储容器标签预览
        if (response.storage_container_id) {
          try {
            console.log('【快捷入库】开始生成仓储容器标签:', response);
            
            // 构建标签数据（与分拣看板完全相同的逻辑）
            const labelData: StorageContainerLabelData = {
              id: `storage-container-${response.storage_container_id}`,
              templateId: 'storage-container-4.25x1.25', // 使用相同模板
              containerInfo: {
                id: response.storage_container_id,
                systemCode: response.storage_container_code,
                name: `仓储容器-${response.storage_container_code}`,
                type: response.container_type || 'box',
                typeDisplay: response.container_type_display || '箱子',
                status: 'active',
                statusDisplay: '正常',
                warehouse: {
                  id: response.warehouse_id || 1,
                  name: response.warehouse_name || '未知仓库',
                  code: response.warehouse_code || 'UNKNOWN'
                },
                project: {
                  id: response.project_id || 1,
                  name: response.project_name || '未知项目',
                  code: response.project_code || 'UNKNOWN'
                },
                location: undefined,
                createdBy: response.created_by_username || '系统用户',
                createdAt: new Date().toLocaleString(),
                updatedAt: new Date().toLocaleString()
              },
              contentSummary: {
                totalItems: response.container_contents_count || 0,
                totalQuantity: response.total_quantity || response.container_contents_count || 0,
                itemTypes: response.container_contents?.map((item: any) => 
                  item.inventory_unit_name || '快捷入库物品'
                ) || ['快捷入库物品'],
                items: response.container_contents?.map((item: any) => ({
                  code: item.inventory_unit_code,
                  quantity: item.quantity
                })) || []
              },
              printOptions: {
                copies: 1,
                directPrint: false
              }
            };

            console.log('【快捷入库】标签数据准备完成:', labelData);

            // 显示标签预览
            setLabelPreviewData(labelData);
            setLabelPreviewVisible(true);
            
            // 提示用户查看标签
            message.info('仓储容器标签已生成，请查看预览');
            console.log('【快捷入库】标签预览已显示');
            
          } catch (labelError) {
            console.error('【快捷入库】标签生成失败:', labelError);
            // 不影响主流程，只记录日志
          }
        }
        
        // 8. 聚焦到库存单元扫描框
        // 增加延迟时间，确保快捷入库流程完全完成后再聚焦
        setTimeout(() => {
          if (scanInputRef.current) {
            scanInputRef.current.focus();
            console.log('【快捷入库】库存单元扫描框自动聚焦成功');
          } else {
            console.warn('【快捷入库】库存单元扫描框ref未找到，无法自动聚焦');
          }
        }, 300);
        
      } catch (error: any) {
        console.error('【快捷入库】快捷入库处理失败:', error);
        console.error('【快捷入库】错误详情:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method
        });
        
        // 关闭加载提示
        message.destroy();
        
        // 显示错误信息
        const errorMessage = error.response?.data?.error || '快捷入库处理失败，请重试';
        message.error(errorMessage);
        // 错误提示后自动聚焦到主扫描框
        setTimeout(() => {
          if (mainScanInputRef.current) {
            mainScanInputRef.current.focus();
            console.log('【快捷入库】快捷入库流程内部错误后自动聚焦到主扫描框');
          }
        }, 100);
      }
      
      console.log('【快捷入库】快捷入库模式流程完成');
      
    } catch (error: any) {
      console.error('【快捷入库】快捷入库处理失败:', error);
      console.error('【快捷入库】错误详情:', {
        message: error?.message || '未知错误',
        stack: error?.stack || '无堆栈信息'
      });
      message.error('快捷入库处理失败，请重试');
      // 错误提示后自动聚焦到主扫描框
      setTimeout(() => {
        if (mainScanInputRef.current) {
          mainScanInputRef.current.focus();
          console.log('【快捷入库】快捷入库处理失败后自动聚焦到主扫描框');
        }
      }, 100);
    }
  };

  // 处理扫描输入
  const handleScanInput = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && scanValue.trim()) {
      // 基础验证：必须先选择项目与仓库
      {
        // 1. 基础验证：项目编码选择
        if (!projectCode) {
          message.warning('请先选择项目编码后再进行扫描！');
          return;
        }
        
        // 2. 基础验证：仓库选择
        if (!warehouseCode) {
          message.warning('请先选择仓库后再进行扫描！');
          return;
        }
      }
      
      const scannedValue = scanValue.trim();
      const type = identifyScanType(scannedValue);
      
      if (type === '物流单号') {
        // 物流单号按原有常规流程处理（不再区分快捷入库模式）
        
        // 常规模式下，执行原有的物流单号处理逻辑
        try {
          console.log('=== 物流单号验证流程开始 ===');
          console.log('[DEBUG] 扫描物流单号:', scannedValue);
          console.log('扫描值:', scannedValue);
          console.log('当前项目编码:', projectCode);
          
          // 2. 检查物流单号是否已存在入库记录
          console.log('【步骤1】开始检查物流单号是否已存在入库记录');
          const checkParams: any = {
            tracking_number: scannedValue
          };
          
          // 注意：不在项目编码过滤，需要在全部入库记录中查找
          console.log('【步骤1】在全部入库记录中查找物流单号，不限定项目');
          
          console.log('【步骤1】API请求参数:', checkParams);
          const inboundCheckResponse = await checkTrackingNumberInbound(checkParams);
          console.log('【步骤1】API响应结果:', inboundCheckResponse);
          
          if (inboundCheckResponse.exists) {
            console.warn('【步骤1】物流单号已存在入库记录:', inboundCheckResponse);
            console.log('【步骤1】找到入库记录数量:', inboundCheckResponse.count);
            console.log('【步骤1】入库记录详情:', inboundCheckResponse.records);
            
            // 显示已存在入库记录的详细信息
            const existingRecords = inboundCheckResponse.records;
            const recordInfo = existingRecords.map((record: any) => 
              `• ${record.system_code} (${record.project_code}) - ${record.total_quantity}个 - ${record.created_at.split('T')[0]}`
            ).join('\n');
            
            console.log('【步骤1】准备显示警告对话框，记录信息:', recordInfo);
            
            Modal.confirm({
              title: '物流单号已存在入库记录',
              content: (
                <div>
                  <p>该物流单号已存在以下入库记录：</p>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    padding: '8px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    marginTop: '8px'
                  }}>
                    <pre style={{ margin: 0, fontSize: '12px' }}>{recordInfo}</pre>
                  </div>
                  <p style={{ marginTop: '8px', color: '#666' }}>
                    请选择操作：
                  </p>
                  <ul style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                    <li><strong>继续处理</strong>：继续执行物流单号验证和入库流程</li>
                    <li><strong>取消</strong>：退回扫描页面，清空当前扫描状态</li>
                  </ul>
                </div>
              ),
              okText: '继续处理',
              cancelText: '取消',
              onOk: async () => {
                console.log('【步骤1】用户确认继续处理已存在的物流单号');
                // 用户确认继续处理，执行原有的物流单号处理逻辑
                await processTrackingNumber(scannedValue);
              },
              onCancel: () => {
                console.log('【步骤1】用户取消处理已存在的物流单号');
                clearScanStates();
                // 取消后自动聚焦到主扫描输入框
                setTimeout(() => {
                  if (mainScanInputRef.current) {
                    mainScanInputRef.current.focus();
                  }
                }, 100);
              }
            });
            return;
          } else {
            console.log('【步骤1】物流单号无入库记录，继续下一步验证');
          }
          
          // 3. 物流单号预报信息验证
          console.log('【步骤2】开始物流单号预报信息验证');
          await processTrackingNumber(scannedValue);
          
          // 物流单号处理完成后，自动聚焦到主扫描输入框
          setTimeout(() => {
            if (mainScanInputRef.current) {
              mainScanInputRef.current.focus();
            }
          }, 100);
          
        } catch (error: any) {
          console.error('【错误】物流单号验证失败:', error);
          console.error('【错误】错误详情:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method
          });
          const errorMessage = error.response?.data?.detail || error.response?.data?.error || '物流单号验证失败';
          message.error(`${errorMessage}，请重试`);
          
          // 错误处理后，自动聚焦到主扫描输入框
          setTimeout(() => {
            if (mainScanInputRef.current) {
              mainScanInputRef.current.focus();
            }
          }, 100);
        }
        
      } else if (type === '入库预报容器') {
        console.log('=== 入库预报容器验证流程开始 ===');
        console.log('[DEBUG] 扫描入库预报容器:', scannedValue);
        
        // 容器编码处理逻辑保持不变
        setCurrentScanValue(scannedValue);
        setScanType(type);
        
        // 统一流程：先打开模态框，再在 fetchContainerData 内部根据声明与校验决定是否快捷入库或展示处理页面
        // 统一流程：在 fetchContainerData 内部按声明/校验决定快捷入库或常规处理
        fetchContainerData(scannedValue);
      } else {
        // 显示未知扫描类型提示
        console.log('=== 未知扫描类型 ===');
        console.log('[DEBUG] 未知扫描类型:', scannedValue);
        message.warning('无法识别的扫描类型，请检查扫描值是否正确');
        setScanValue('');
      }
      
      // 清空输入框
      setScanValue('');
    }
  };

  // 处理扫描输入变化
  const handleScanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScanValue(e.target.value);
  };

  // 处理模态框关闭
  const handleModalClose = () => {
    // 调用clearScanStates来清空所有扫描相关状态，包括scanValue
    clearScanStates();
    
    // 额外清空一些不在clearScanStates中的状态
    setContainerData(null);
    setActualSearchCode('');
    setInventoryUnitScanValue('');
    setScanningInventoryUnit(false);
    setScanResult(null);
    setScanHistory([]); // 清空扫描记录
    setInboundRemarks('');
    
    // 清空分拣组选择相关状态
    setSortingGroupModalVisible(false);
    setPendingInventoryUnit(null);
    setAvailableSortingGroups([]);
    
    // 清空重量尺寸确认相关状态
    setWeightDimensionsModalVisible(false);
    setCurrentInventoryUnit(null);
    setActualWeightDimensions({
      actual_weight: 0,
      actual_length: 0,
      actual_width: 0,
      actual_height: 0
    });
    setConfirmingWeightDimensions(false);
    
    // 清空操作类型记录（历史选择）与实际发生集合
    setOperationTypes(new Set());
    setPerformedIrregularOps(new Set());
    
    // 聚焦到库存单元扫描框
    // 增加延迟时间，确保状态更新完成后再聚焦
    setTimeout(() => {
      if (scanInputRef.current) {
        scanInputRef.current.focus();
        console.log('[DEBUG] 模态框关闭后库存单元扫描框自动聚焦成功');
      } else {
        console.warn('[DEBUG] 模态框关闭后库存单元扫描框ref未找到，无法自动聚焦');
      }
    }, 300);
  };
  // 统计当前会话的扫描记录
  const calculateSessionTotals = () => {
    const sessionTotals: Record<string, number> = {};
    scanHistory.forEach(record => {
      const code = record.inventoryUnitCode;
      sessionTotals[code] = (sessionTotals[code] || 0) + record.quantity;
    });
    return sessionTotals;
  };

  // 计算扫描记录统计数据
  const calculateScanHistoryStats = () => {
    if (scanHistory.length === 0) {
      return {
        totalProductQuantity: 0,
        uniqueInventoryUnits: 0,
        uniqueSortingGroups: 0
      };
    }

    // 计算产品数量总计
    const totalProductQuantity = scanHistory.reduce((sum, record) => sum + record.quantity, 0);

    // 计算库存单元种类总计（去重）
    const uniqueInventoryUnits = new Set(scanHistory.map(record => record.inventoryUnitCode)).size;

    // 计算分拣组数量总计（去重）
    const uniqueSortingGroups = new Set(scanHistory.map(record => record.sortingGroupSystemCode)).size;

    return {
      totalProductQuantity,
      uniqueInventoryUnits,
      uniqueSortingGroups
    };
  };

  // 获取未分组库存分拣组的实际系统编码
  const getUngroupedSortingGroupSystemCode = () => {
    if (!inventoryConfig || !projectCode) {
      return `SG-${projectCode}-UNGROUPED`; // 备选方案
    }

    // 查找未分组库存分拣组
    const ungroupedGroup = inventoryConfig.sorting_groups?.find((group: any) => 
      group.name?.includes('未分组库存分拣组') || 
      group.group_type === 'ungrouped' ||
      group.name?.includes(`SYS-未分组库存分拣组-${projectCode}`)
    );

    if (ungroupedGroup && ungroupedGroup.system_code) {
      return ungroupedGroup.system_code;
    }

    // 如果找不到，返回动态生成的编码作为备选
    return `SG-${projectCode}-UNGROUPED`;
  };

  // 记录非常规操作类型
  const recordOperationType = (operationType: string) => {
    console.log('[DEBUG] recordOperationType 被调用:');
    console.log('  要记录的操作类型:', operationType);
    console.log('  记录前的operationTypes:', Array.from(operationTypes));
    
    if (operationType) {
      // 转换为中文名称进行记录
      const chineseLabel = getIrregularOperationLabel(operationType);
      console.log('  转换为中文名称:', chineseLabel);
      
      setOperationTypes(prev => {
        const newSet = new Set(Array.from(prev).concat(chineseLabel));
        console.log('  记录后的operationTypes:', Array.from(newSet));
        return newSet;
      });
    } else {
      console.log('  操作类型为空，不记录');
    }
  };
  // 将当前会话数量累加到分拣数据库
  const addToSortingDatabaseTotals = (sessionTotals: Record<string, number>) => {
    setSortingDatabase(prev => 
      prev.map(item => ({
        ...item,
        sortedQuantity: (item.sortedQuantity || 0) + (sessionTotals[item.inventoryUnitCode] || 0)
      }))
    );
  };
  // 处理提交记录
  const handleSubmitInboundData = async () => {
    // 防重复提交检查
    if (isSubmitting) {
      message.warning('正在提交中，请稍候...');
      return;
    }

    // 验证必要数据（当快捷入库开关打开时跳过项目和仓库验证）
    if (!quickInboundEnabled) {
      if (!projectCode) {
        message.error('请先选择项目编码');
        return;
      }

      if (!warehouseCode) {
        message.error('请先选择仓库编码');
        return;
      }
    } else {
      console.log('快捷入库模式已开启，跳过项目和仓库验证');
    }

    if (scanHistory.length === 0) {
      message.error('请先扫描库存单元');
      return;
    }

    try {
      setIsSubmitting(true);

      // 显示加载提示
      const hideLoading = message.loading('正在创建入库记录...', 0);

      // 1. 准备入库记录数据
      
      // 最终非常规操作：仅记录实际发生集合
      console.log('[DEBUG] 非常规操作(最终): 仅记录实际发生集合 performedIrregularOps=', Array.from(performedIrregularOps));
      const finalOperationTypes = new Set<string>(performedIrregularOps);
      
      // 调试信息：检查物流单号和容器信息
      console.log('[DEBUG] 物流单号和容器信息检查:');
      console.log('  scanValue:', scanValue);
      console.log('  currentScanValue:', currentScanValue);
      console.log('  trackingForecastData:', trackingForecastData);
      console.log('  containerData:', containerData);
      console.log('  scanType:', scanType);
      
      const inboundRecordData = {
        project: projectCode,
        warehouse: warehouseCode,
        inbound_forecast: trackingForecastData?.id || containerData?.container?.inbound_forecast?.id,
        inbound_forecast_container: containerData ? currentScanValue : undefined,
        tracking_number: scanType === '物流单号' ? currentScanValue : undefined,
        is_quick_inbound: false, // 不是快速入库
        irregular_operation: finalOperationTypes.size > 0 ? Array.from(finalOperationTypes) : [],
        remarks: inboundRemarks,
        inventory_items: scanHistory.map(record => ({
          inventory_unit_code: record.inventoryUnitCode,
          quantity: record.quantity,
          remarks: `扫描入库 - ${record.sortingGroup}`
        })),
        // 新增：传递扫描的容器编码，用于更新扫描状态
        scanned_container_code: containerData ? currentScanValue : undefined
      };

      console.log('[DEBUG] 最终入库记录数据:', inboundRecordData);

      // 额外调试：上架费生成关键字段检查（浏览器端）
      try {
        console.group('[DEBUG][Putaway] 生成上架费前置检查');
        const variantExpect = quickInboundEnabled ? 'Inbound-Quick' : 'Inbound-Standard';
        console.log('  期望变体键 variant_key:', variantExpect, '(由前端 quickInboundEnabled 推断)');
        console.log('  quickInboundEnabled:', quickInboundEnabled);
        console.log('  扫描条目数 scanHistory.length:', scanHistory.length);
        let knownWeight = 0;
        let unknownWeight = 0;
        const weightSamples: Array<{code: string; qty: number; unit_type: string; weight_g: number|undefined}> = [];
        let aiuQtySum = 0;
        let aiuBatchWeightInput = Number(exceptionSessionTotalWeightG || 0);
        scanHistory.forEach((rec, idx) => {
          const key = (rec.inventoryUnitCode || '').toLowerCase();
          const mapHit = inventoryUnitCodeToSortingGroupMap[key];
          const inv = mapHit?.inventoryUnit;
          const unitType = inv?.unit_type || '-';
          const weightG = inv?.weight_g ?? inv?.weight ?? inv?.weight_grams;
          const sg = mapHit?.sortingGroup?.system_code || rec.sortingGroupSystemCode;
          const name = (inv?.remarks as any) || (inv as any)?.name || (inv as any)?.system_barcode || rec.inventoryUnitCode || '-';
          const w = typeof weightG === 'number' || typeof weightG === 'string' ? Number(weightG) : undefined;
          if (typeof w === 'number' && !Number.isNaN(w) && w > 0) knownWeight++; else unknownWeight++;
          console.log(`  [${idx}] code=${rec.inventoryUnitCode} unit_type=${unitType} weight_g=${w ?? 'N/A'} qty=${rec.quantity} sg=${sg} name=${name}`);
          weightSamples.push({ code: rec.inventoryUnitCode, qty: Number(rec.quantity || 0), unit_type: String(unitType), weight_g: w });
          if (String(unitType) === 'AIU') {
            aiuQtySum += Number(rec.quantity || 0);
          }
        });
        console.log('  统计：有重量条目=', knownWeight, ' 无重量条目=', unknownWeight);
        if (unknownWeight > 0) {
          console.warn('  警告：存在未携带重量的扫描条目，可能导致上架费无法命中梯度规则');
        }
        // 展示"本次用于匹配梯度的权重输入"：逐条使用 weight_g（缺失按 0）
        console.group('[DEBUG][Putaway][Tier] 本次用于匹配梯度的 weight_g 列表(逐条)');
        weightSamples.forEach((s, i) => {
          console.log(`   [${i}] code=${s.code} unit_type=${s.unit_type} qty=${s.qty} weight_g=${s.weight_g ?? 0}`);
        });
        console.groupEnd();
        // 前端参考：如果涉及 AIU，展示"批次均重(仅参考、不参与计费)"
        if (aiuQtySum > 0) {
          const aiuBatchAvg = aiuQtySum > 0 ? (aiuBatchWeightInput / aiuQtySum) : 0;
          console.info('[DEBUG][Putaway][Tier] 前端参考(不参与计费)：AIU 批次均重 = exception_total_weight_g / 本单AIU数量 =', aiuBatchWeightInput, '/', aiuQtySum, '=', aiuBatchAvg.toFixed(2), 'g');
        }
        console.groupEnd();
      } catch (e) {
        console.warn('[DEBUG][Putaway] 前置检查日志异常', e);
      }
      
      // 调试信息：详细检查各个字段
      console.log('[DEBUG] 字段详细检查:');
      console.log('  inbound_forecast:', inboundRecordData.inbound_forecast);
      console.log('  inbound_forecast_container:', inboundRecordData.inbound_forecast_container);
      console.log('  tracking_number:', inboundRecordData.tracking_number);
      console.log('  irregular_operation:', inboundRecordData.irregular_operation);

      // 使用"会话累计"的异常库存总重量进行整单加权，避免本次输入被重复累计
      const exceptionTotalWeightForOrder = Number(exceptionSessionTotalWeightG || 0);
      (inboundRecordData as any).exception_total_weight_g = exceptionTotalWeightForOrder;
      try {
        console.group('[DEBUG][AIU] 异常库存加权参数');
        console.log('  exceptionTotalWeightG(本次输入):', exceptionTotalWeightG);
        console.log('  exceptionSessionTotalWeightG(会话累计):', exceptionSessionTotalWeightG);
        console.log('  exception_total_weight_g(提交入库参数):', (inboundRecordData as any).exception_total_weight_g);
        const aiuInScan = scanHistory
          .map(r => {
            const key = (r.inventoryUnitCode || '').toLowerCase();
            const hit = inventoryUnitCodeToSortingGroupMap[key];
            const unitType = hit?.inventoryUnit?.unit_type;
            return { code: r.inventoryUnitCode, qty: r.quantity, unitType };
          })
          .filter(it => it.unitType === 'AIU');
        console.log('  本次提交涉及 AIU 条目:', aiuInScan);
        const qNewTotal = aiuInScan.reduce((s, it) => s + Number(it.qty || 0), 0);
        console.log('  预计本单 AIU 新增总数量(Q_new_total):', qNewTotal, ' 预计新增总重量(W_new_total):', exceptionTotalWeightForOrder);
        console.groupEnd();
      } catch (e) {
        console.warn('[DEBUG][AIU] 组装加权参数日志异常', e);
      }

      // 3. 调用API创建入库记录
      const response = await createInboundRecord(inboundRecordData);
      try { setCurrentInboundRecordId(Number(response?.data?.id) || null); } catch {}

      // 3.1 提交成功后，触发建层状态轮询（取 scanHistory 中的 inventoryUnitCode -> 后端id 映射）
      try {
        // 尝试从本地映射中获取 id（若已构建过 inventoryUnitCode->InventoryUnit 的 map）
        const unitIds: number[] = [];
        scanHistory.forEach((rec) => {
          const key = (rec.inventoryUnitCode || '').toLowerCase();
          const mapHit = inventoryUnitCodeToSortingGroupMap[key];
          const inv = mapHit?.inventoryUnit as any;
          if (inv?.id) unitIds.push(Number(inv.id));
        });
        const uniqUnitIds = Array.from(new Set(unitIds)).filter(x => Number.isFinite(x));
        // 为避免过度过滤导致 0/1，暂不按仓库限制
        if (uniqUnitIds.length > 0) {
          startLayerPolling(uniqUnitIds, undefined);
        } else {
          // 无法映射到id：不再自动弹出面板，仅记录文案，用户手动打开面板时可见
          setLayerStatusText('已提交入库，等待后台创建入库层...');
        }
      } catch {}

      // 上架费调试：事件已发布后，主动查询该入库记录关联的计费记录与规则（便于前端快速定位）
      try {
        console.group('[DEBUG][Putaway] 入库完成后的计费检查');
        console.log('  inbound_record_id:', response?.data?.id);
        // 轻量查询：通过账单接口按来源过滤
        const params = { page: 1, page_size: 50, source_table: 'inbound.InboundRecord', source_id: response?.data?.id } as any;
        console.log('  查询 BillingRecords 参数:', params);
        const br = await request.get('/api/v1/billing/billing-records/', { params });
        const rows = br?.data?.results || [];
        console.log('  计费记录条数:', rows.length);
        rows.forEach((r: any, i: number) => {
          console.log(`  [${i}] type=${r.operation_type_name} rule=${r.rule_system_code} unit_price=${r.unit_price} qty=${r.quantity} amount=${r.amount} currency=${r.currency} meta=`, r.meta);
        });
        // 过滤上架费
        const putaway = rows.filter((r: any) => (r.operation_type_name || '').includes('上架') || (r.meta?.fee_type === 'putaway'));
        console.log('  上架费命中数:', putaway.length);
        putaway.forEach((r: any, i: number) => {
          console.log(`  [putaway ${i}] unit_price=${r.unit_price} qty=${r.quantity} amount=${r.amount} variant=${r.meta?.variant} rule=${r.rule_system_code}`);
        });
        console.groupEnd();
      } catch (e) {
        console.warn('[DEBUG][Putaway] 入库后计费检查失败:', e);
      }

      // 提交后：回查本次扫描中的 AIU 实际重量更新情况
      try {
        console.group('[DEBUG][AIU] 提交后回查实际重量');
        const aiuCodes = Array.from(new Set(
          scanHistory
            .map(r => r.inventoryUnitCode)
            .filter(code => {
              const hit = inventoryUnitCodeToSortingGroupMap[(code || '').toLowerCase()];
              return (hit?.inventoryUnit?.unit_type === 'AIU');
            })
        ));
        if (aiuCodes.length === 0) {
          console.log('  本次提交未包含 AIU 扫描条目');
        }
        for (const code of aiuCodes) {
          try {
            const inv = await getLatestInventoryUnit(code);
            console.log(
              `  AIU ${code} => actual_weight=`,
              (inv as any)?.actual_weight ?? inv?.actual_weight,
              ' cum_weight_g=', (inv as any)?.cum_weight_g,
              ' cum_quantity=', (inv as any)?.cum_quantity,
              ' warehouse_total_quantity=', (inv as any)?.warehouse_total_quantity
            );
          } catch (e) {
            console.warn(`  回查 AIU ${code} 失败:`, e);
          }
        }
        console.groupEnd();
      } catch (e) {
        console.warn('[DEBUG][AIU] 提交后回查日志异常', e);
      }

      // 提交后：回查本次上架费"匹配到的梯度"
      try {
        console.group('[DEBUG][Putaway][Tier] 提交后回查命中梯度');
        const ridRows = await request.get('/api/v1/billing/billing-records/', { params: {
          page: 1,
          page_size: 100,
          source_table: 'inbound.InboundRecord',
          source_id: (response?.data?.id),
        }});
        const allRows = Array.isArray(ridRows?.data?.results) ? ridRows.data.results : [];
        const putawayRows = allRows.filter((r: any) => (r.meta?.fee_type === 'putaway'));
        if (putawayRows.length === 0) {
          console.log('  未发现上架费计费记录');
        }
        putawayRows.forEach((r: any, i: number) => {
          const t = r?.meta?.tier;
          const tierText = t ? `tier_id=${t.id} [${t.min_value ?? '-'}, ${t.max_value ?? '-'}]` : '未返回tier(可能为固定价或未命中梯度)';
          console.log(`  [${i}] rule=${r.rule_system_code} unit_price=${r.unit_price} qty=${r.quantity} amount=${r.amount} variant=${r.meta?.variant} -> ${tierText}`);
        });
        console.groupEnd();
      } catch (e) {
        console.warn('[DEBUG][Putaway][Tier] 回查命中梯度失败:', e);
      }

      // 检查响应是否成功（通常API成功返回200或201状态码，或者有data字段）
      if (response.data && (response.status === 200 || response.status === 201)) {
        console.log('入库记录创建成功:', response.data);

        // 4. 记录操作类型到统计
        recordOperationType('scan_inbound');

        // 关闭加载提示
        hideLoading();
        
        // 5. 显示成功提示
        message.success(`入库记录创建成功！记录ID: ${response.data.id}`);
        // 标记下次聚焦自动刷新"最近15条记录"，并立即刷新一次
        try { sessionStorage.setItem('refresh_inbound_records', '1'); } catch {}
        fetchInboundRecords();
        
        // 5.1 保存"最近一次入库记录"快照（创建仓储容器弹窗里"填入最近入库记录"使用）
        try {
          saveLastInboundRecord(buildLastInboundRecordSnapshot({
            recordId: Number(response.data.id),
            systemCode: (response.data as any).system_code,
            projectCode,
            warehouseCode,
            scanHistory: scanHistory || []
          }));
        } catch (e) {
          console.warn('[LastInboundRecord] 保存最近入库记录失败', e);
        }

        // 6. 统计当前会话的扫描记录并累加到分拣数据库
        const sessionTotals = calculateSessionTotals();
        addToSortingDatabaseTotals(sessionTotals);

        // 6.1 记录本次入库对分拣看板的增量映射（仅索引，不重复改看板）
        try {
          recordSortingBoardDeltasAfterSubmit({
            recordId: Number(response.data.id),
            scanHistory: (scanHistory || []).map(r => ({
              inventoryUnitCode: r.inventoryUnitCode,
              quantity: Number(r.quantity || 0),
              temporaryLocation: Number(r.temporaryLocation),
              sortingGroupSystemCode: r.sortingGroupSystemCode
            }))
          });
          try {
            const rawBoard = localStorage.getItem(SORTING_BOARD_KEY) || '';
            const rawIdx = localStorage.getItem(GLOBAL_RECORD_INDEX_KEY) || '';
            appendLsLog('submit_after_index', { boardLen: rawBoard.length, indexLen: rawIdx.length });
          } catch {}
          console.log('[SortingBoardSync] 已记录本次入库记录的增量索引');
        } catch (e) {
          console.warn('[SortingBoardSync] 记录增量索引失败', e);
        }
        
        // 注意：空仓位检测已移至"生成-打印 新仓位容器"后执行
        
        // 8. 清空当前会话的扫描记录
        setScanHistory([]);
        
        // 9. 清空入库备注
        setInboundRemarks('');
        
        // 10. 重置异常库存会话累计重量，避免跨单叠加
        setExceptionSessionTotalWeightG(0);
        setExceptionTotalWeightG(undefined);
        console.log('[DEBUG][AIU] 成功提交后已重置会话累计重量与输入重量');

        // 11. 关闭模态框
        handleModalClose();
      } else {
        // 关闭加载提示
        hideLoading();
        
        // 显示错误信息
        message.error('创建入库记录失败，请重试');
        console.error('API响应异常:', response);
      }
    } catch (error: any) {
      // 关闭加载提示
      message.destroy();
      
      // 显示错误信息
      const errorMessage = error.response?.data?.error || '创建入库记录失败，请重试';
      message.error(errorMessage);
      console.error('创建入库记录失败:', error);
    } finally {
      // 重置提交状态
      setIsSubmitting(false);
    }
  };

  // 新增：提交后关闭弹窗并清空项目选择（不改动其他逻辑）
  const handleSubmitInboundAndResetProject = async () => {
    await handleSubmitInboundData();
    try { setProjectCode(''); } catch {}
  };

  // 检测空仓位的函数
  const detectEmptyLocations = (): number[] => {
    const emptyLocations: number[] = [];
    
    // 遍历分拣数据库，检测数量为0的仓位
    sortingDatabase.forEach(item => {
      if (item.sortedQuantity === 0) {
        // 检查该仓位是否已经在可重用仓位池中
        if (!reusableLocationPool.includes(item.temporaryLocation)) {
          emptyLocations.push(item.temporaryLocation);
        }
      }
    });
    
    console.log(`[空仓位检测] 检测到空仓位: ${emptyLocations.join(', ')}`);
    return emptyLocations;
  };

  // 扫到的物流单号匹配到退货入库初检预报：弹窗里用独立的"收货核对"面板，不走普通的库存单元扫描/分拣看板流程
  const isReturnReceiving = scanType === '物流单号' && (trackingForecastData as any)?.special_instructions === 'return_inspection';

  // 退货入库初检提交成功：把入库的货（正常入库的 LIU 和 AIU，销毁的件不入库）一次性加进入库分拣看板，
  // 分配临时分拣仓位，写增量索引（作废/修改入库记录时用），并触发建层轮询——和普通扫描入库提交后的处理保持一致，
  // 之后照常在看板里生成仓储容器并上架。
  const handleReturnExecuted = (execution: InspectionExecution): BoardPlacement[] => {
    const items = execution.inbound_items || [];
    if (items.length === 0) return [];
    const current = reclaimStateRef.current;
    const { state: next, placements, skipped } = addInboundItemsToBoard(
      {
        sortingDatabase: current.sortingDatabase as any,
        nextLocationNumber: current.nextLocationNumber,
        reusableLocationPool: current.reusableLocationPool,
      },
      items,
    );
    setSortingDatabase(next.sortingDatabase as any);
    setNextLocationNumber(next.nextLocationNumber);
    setReusableLocationPool(next.reusableLocationPool);
    saveSortingBoardStateLocal(next as any);
    saveSortingBoardData(next);
    try {
      if (execution.inbound_record) {
        recordSortingBoardDeltasAfterSubmit({ recordId: Number(execution.inbound_record), scanHistory: placements });
      }
    } catch (err) {
      console.warn('[SortingBoardSync] 记录退货入库的增量索引失败', err);
    }
    // 保存"最近一次入库记录"快照（创建仓储容器弹窗里"填入最近入库记录"使用），和普通扫描入库提交后的处理一致；
    // 条目来自实际入库的库存单元（销毁的件不在里面），没有可加入看板的条目时不覆盖上一条记录
    try {
      if (execution.inbound_record && placements.length > 0) {
        const forecastData = trackingForecastData as any;
        saveLastInboundRecord(buildLastInboundRecordSnapshot({
          recordId: Number(execution.inbound_record),
          systemCode: execution.inbound_record_code || undefined,
          projectCode: forecastData?.project_detail?.project_code || projectCode,
          warehouseCode: forecastData?.warehouse_detail?.code || warehouseCode,
          scanHistory: placements,
        }));
      }
    } catch (e) {
      console.warn('[LastInboundRecord] 保存退货入库的最近入库记录失败', e);
    }
    try {
      const unitIds = Array.from(new Set(items.map(item => item.inventory_unit))).filter(id => Number.isFinite(id));
      if (unitIds.length > 0) startLayerPolling(unitIds, undefined);
    } catch {}
    const locations = Array.from(new Set(placements.map(p => p.temporaryLocation))).sort((a, b) => a - b);
    if (locations.length > 0) {
      message.success(`退货入库的货已加入入库分拣看板，临时分拣仓位：${locations.join('、')}`);
    }
    if (skipped.length > 0) {
      message.warning(`有 ${skipped.length} 项没有分拣组，无法加入分拣看板：${skipped.map(s => s.inventory_unit_barcode).join('、')}`);
    }
    return placements;
  };

  // 手动回收空仓位：入库弹窗开着、正在提交或正在扫库存单元时不允许回收，避免和正在进行的分配冲突
  const isReclaimBlocked = isModalVisible || isSubmitting || scanningInventoryUnit;

  // 确认框回调里要读最新状态，每次渲染同步一份
  const reclaimStateRef = useRef({ sortingDatabase, reusableLocationPool, nextLocationNumber, scanHistory, blocked: isReclaimBlocked });
  reclaimStateRef.current = { sortingDatabase, reusableLocationPool, nextLocationNumber, scanHistory, blocked: isReclaimBlocked };

  // 回收指定仓位：删除其 0 数量占位行，把仓位号放进可复用池（去重、从小到大）
  const applyReclaimLocations = (requested: number[]) => {
    const state = reclaimStateRef.current;
    if (state.blocked) {
      message.warning('入库弹窗处理中，暂不能回收仓位，请先完成或取消当前入库');
      return;
    }
    const reclaimable = new Set(computeReclaimableLocations(state.sortingDatabase, state.scanHistory));
    const targets = requested.filter(location => reclaimable.has(location));
    if (targets.length === 0) {
      message.info('没有可回收的空仓位');
      return;
    }
    const targetSet = new Set(targets);
    const nextDatabase = state.sortingDatabase.filter(item => !targetSet.has(Number(item.temporaryLocation)));
    const nextPool = Array.from(new Set([...state.reusableLocationPool, ...targets])).sort((a, b) => a - b);
    setSortingDatabase(nextDatabase);
    setReusableLocationPool(nextPool);
    const boardState = { sortingDatabase: nextDatabase, nextLocationNumber: state.nextLocationNumber, reusableLocationPool: nextPool };
    saveSortingBoardStateLocal(boardState);
    saveSortingBoardData(boardState);
    console.log(`[空仓位回收] 手动回收 ${targets.length} 个仓位: ${targets.join(', ')}，可重用仓位池: ${nextPool.join(', ')}`);
    message.success(`已回收临时分拣仓位 ${targets.join('、')}，可被新的分拣组复用`);
  };

  const confirmReclaimLocations = (locations: number[]) => {
    if (isReclaimBlocked) {
      message.warning('入库弹窗处理中，暂不能回收仓位，请先完成或取消当前入库');
      return;
    }
    if (locations.length === 0) {
      message.info('没有可回收的空仓位');
      return;
    }
    Modal.confirm({
      title: '回收空仓位？',
      content: `将回收临时分拣仓位 ${locations.join('、')}（它们的已分拣数量均为 0）。回收后这些仓位号会放入可复用池，新的分拣组会优先使用。`,
      okText: '回收',
      cancelText: '取消',
      onOk: () => applyReclaimLocations(locations)
    });
  };

  // 分拣数据库操作函数 - 按分拣组分配仓位
  const findInventoryUnitInDatabase =(code: string): number | null => {
    const item = sortedSortingDatabase.find(item => item.inventoryUnitCode === code);
    return item ? item.temporaryLocation : null;
  };

  // 查找分拣组是否已有临时仓位（按分拣组系统编码）
  const findSortingGroupLocation = (sortingGroupSystemCode: string): number | null => {
    const item = sortedSortingDatabase.find(item => item.sortingGroupSystemCode === sortingGroupSystemCode);
    try {
      console.debug('[DBG][Board] findSortingGroupLocation:', {
        sortingGroupSystemCode,
        hit: item?.temporaryLocation ?? null,
        groupsOnBoard: Array.from(new Set(sortedSortingDatabase.map(it => it.sortingGroupSystemCode))).slice(0, 20)
      });
    } catch {}
    return item ? item.temporaryLocation : null;
  };

  const addInventoryUnitToDatabase = (
    code: string,
    sortingGroup: string = '默认分拣组',
    sortingGroupSystemCode: string = 'SG-DEFAULT-1',
    inventoryUnitName?: string,
    opts?: { productId?: number; displaySortingBarcode?: string; sortingBarcodes?: string[] }
  ): number => {
    // 统一键标准化，避免大小写/空字符串导致重复
    const normalizedCode = (code || '').toString();
    const normalizedGroupCode = (sortingGroupSystemCode || '').toString().trim() || getUngroupedSortingGroupSystemCode();

    // 先检查分拣组是否已有临时仓位（按系统编码确保唯一）
    const existingLocation = findSortingGroupLocation(normalizedGroupCode);
    try {
      console.debug('[DBG][Board] addInventoryUnitToDatabase: BEFORE', {
        code: normalizedCode,
        sortingGroup,
        sortingGroupSystemCode: normalizedGroupCode,
        existingLocation,
        caller: (new Error().stack || '').split('\n')[2]?.trim()
      });
    } catch {}
    
    if (existingLocation) {
      // 分拣组已有仓位，若已有相同库存单元行，则不新增，仅保证其仓位与分拣组一致；否则再新增
      setSortingDatabase(prev => {
        const next = prev.map(item => ({ ...item, sortedQuantity: item.sortedQuantity || 0 }));
        const hitIndex = next.findIndex(it => it.inventoryUnitCode === normalizedCode);
        if (hitIndex >= 0) {
          // 已存在同 code 行，合并更新，不新增
          next[hitIndex] = {
            ...next[hitIndex],
            sortingGroup,
            sortingGroupSystemCode: normalizedGroupCode,
            temporaryLocation: existingLocation,
            inventoryUnitName: inventoryUnitName ?? next[hitIndex].inventoryUnitName,
            ...(opts || {})
          };
          try {
            console.debug('[DBG][Board] addInventoryUnitToDatabase: REUSE+MERGE', {
              code: normalizedCode,
              sortingGroupSystemCode: normalizedGroupCode,
              existingLocation,
              mergedRowIndex: hitIndex,
              totalRows: next.length
            });
          } catch {}
          return next;
        }
        // 不存在同 code 行，新增一行（复用已有仓位）
        const newItem = {
          sortingGroup,
          sortingGroupSystemCode: normalizedGroupCode,
          inventoryUnitCode: normalizedCode,
          inventoryUnitName,
          temporaryLocation: existingLocation,
          sortedQuantity: 0,
          ...(opts || {})
        };
        next.push(newItem);
        try {
          console.debug('[DBG][Board] addInventoryUnitToDatabase: REUSE+APPEND', {
            code: normalizedCode,
            sortingGroupSystemCode: normalizedGroupCode,
            existingLocation,
            totalRows: next.length
          });
        } catch {}
        return next;
      });
      try {
        console.debug('[DBG][Board] addInventoryUnitToDatabase: REUSE', {
          code: normalizedCode,
          sortingGroupSystemCode: normalizedGroupCode,
          existingLocation,
          note: 'no new row if code exists'
        });
      } catch {}
      return existingLocation;
    } else {
      // 分拣组没有仓位，优先使用可重用仓位
      let newLocation: number;
      
      if (reusableLocationPool.length > 0) {
        // 使用数字最小的可重用仓位
        newLocation = reusableLocationPool[0];
        setReusableLocationPool(prev => prev.slice(1)); // 从池中移除已使用的仓位
        console.log(`[空仓位重用] 重用空仓位: ${newLocation}`);
      } else {
        // 没有可重用仓位，分配新仓位
        newLocation = nextLocationNumber;
        setNextLocationNumber(prev => prev + 1);
        console.log(`[仓位分配] 分配新仓位: ${newLocation}`);
      }
      
      setSortingDatabase(prev => {
        const next = prev.map(item => ({ ...item, sortedQuantity: item.sortedQuantity || 0 }));
        const hitIndex = next.findIndex(it => it.inventoryUnitCode === normalizedCode);
        if (hitIndex >= 0) {
          // 已存在同 code 行，合并更新，不新增
          next[hitIndex] = {
            ...next[hitIndex],
            sortingGroup,
            sortingGroupSystemCode: normalizedGroupCode,
            temporaryLocation: newLocation,
            inventoryUnitName: inventoryUnitName ?? next[hitIndex].inventoryUnitName,
            ...(opts || {})
          };
          try {
            console.debug('[DBG][Board] addInventoryUnitToDatabase: NEW+MERGE', {
              code: normalizedCode,
              sortingGroupSystemCode: normalizedGroupCode,
              newLocation,
              mergedRowIndex: hitIndex,
              totalRows: next.length
            });
          } catch {}
          return next;
        }
        const newItem = {
          sortingGroup,
          sortingGroupSystemCode: normalizedGroupCode,
          inventoryUnitCode: normalizedCode,
          inventoryUnitName,
          temporaryLocation: newLocation,
          sortedQuantity: 0,
          ...(opts || {})
        };
        next.push(newItem);
        try {
          console.debug('[DBG][Board] addInventoryUnitToDatabase: NEW+APPEND', {
            code: normalizedCode,
            sortingGroupSystemCode: normalizedGroupCode,
            newLocation,
            totalRows: next.length
          });
        } catch {}
        return next;
      });
      
      try {
        console.debug('[DBG][Board] addInventoryUnitToDatabase: NEW', {
          code: normalizedCode,
          sortingGroupSystemCode: normalizedGroupCode,
          newLocation,
          note: 'no new row if code exists'
        });
      } catch {}
      return newLocation;
    }
  };

  // 添加扫描记录
  const addToScanHistory = (inventoryUnitCode: string, scannedValue: string, temporaryLocation: number, sortingGroup: string = '默认分拣组', sortingGroupSystemCode: string = 'SG-DEFAULT-1') => {
    // 添加调试信息
    console.log('=== 添加扫描记录调试信息 ===');
    console.log('inventoryUnitCode (库存单元系统编码):', inventoryUnitCode);
    console.log('scannedValue (实际扫描值):', scannedValue);
    console.log('temporaryLocation (临时仓位):', temporaryLocation);
    console.log('sortingGroup (分拣组):', sortingGroup);
    console.log('sortingGroupSystemCode (分拣组系统编码):', sortingGroupSystemCode);
    
    const newRecord = {
      inventoryUnitCode,
      scannedValue,
      temporaryLocation,
      quantity: 1, // 默认数量为1
      scanTime: new Date(),
      sortingGroup,
      sortingGroupSystemCode
    };
    
    console.log('创建的扫描记录对象:', newRecord);
    console.log('=== 扫描记录调试信息结束 ===');
    
    setScanHistory(prev => [newRecord, ...prev]); // 新记录添加到顶部
  };

  // 更新扫描记录数量
  const updateScanHistoryQuantity = (index: number, newQuantity: number) => {
    const validQuantity = Math.max(1, newQuantity); // 确保最小值为1
    setScanHistory(prev => {
      const newHistory = [...prev];
      newHistory[index] = {
        ...newHistory[index],
        quantity: validQuantity
      };
      return newHistory;
    });
  };

  // 删除扫描记录
  const deleteScanHistoryItem = (index: number) => {
    setScanHistory(prev => prev.filter((_, i) => i !== index));
  };
  // 处理库存单元扫描输入
  const handleInventoryUnitScanInput = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inventoryUnitScanValue.trim()) {
      const scannedValue = inventoryUnitScanValue.trim();
      // 记录本次扫描原始值
      lastScannedValueRef.current = scannedValue;
      setScanningInventoryUnit(true);
      
      // 添加详细的调试信息
      console.log('=== 库存单元扫描调试信息 ===');
    console.log('扫描值:', scannedValue);
    console.log('当前项目编码:', projectCode);
      console.log('库存单元系统编码映射表大小:', Object.keys(inventoryUnitCodeToSortingGroupMap).length);
      console.log('产品分拣条码映射表大小:', Object.keys(barcodeToSortingGroupMap).length);
      console.log('项目产品数量:', projectProducts.length);
      
      // 显示映射表内容（前5个）
      console.log('库存单元系统编码映射表内容（前5个）:');
      Object.keys(inventoryUnitCodeToSortingGroupMap).slice(0, 5).forEach(key => {
        const item = inventoryUnitCodeToSortingGroupMap[key];
        console.log(`  ${key} (原始: ${item.originalSystemBarcode}) -> ${item.sortingGroup.name}`);
      });
      
      console.log('产品分拣条码映射表内容（前5个）:');
      Object.keys(barcodeToSortingGroupMap).slice(0, 5).forEach(key => {
        const item = barcodeToSortingGroupMap[key];
        console.log(`  ${key} (原始: ${item.originalCode}) -> ${item.product.name}`);
      });
      
      // 详细显示当前产品数据
      console.log('【详细调试】=== 当前产品数据详情 ===');
      projectProducts.forEach((product, index) => {
        console.log(`【产品${index + 1}】ID: ${product.id}, 名称: ${product.name}`);
        console.log(`  - 分拣条码类型: ${typeof product.sorting_codes}`);
        console.log(`  - 分拣条码是否为数组: ${Array.isArray(product.sorting_codes)}`);
        console.log(`  - 分拣条码数量: ${Array.isArray(product.sorting_codes) ? product.sorting_codes.length : 'N/A'}`);
        console.log(`  - 分拣条码详情:`, product.sorting_codes);
        
        if (Array.isArray(product.sorting_codes) && product.sorting_codes.length > 0) {
          product.sorting_codes.forEach((codeObj, codeIndex) => {
            console.log(`    【分拣条码${codeIndex + 1}】`);
            console.log(`      - 对象类型: ${typeof codeObj}`);
            console.log(`      - 对象内容:`, codeObj);
            if (codeObj && typeof codeObj === 'object') {
              console.log(`      - ID: ${codeObj.id || 'N/A'}`);
              console.log(`      - 条码: ${codeObj.code || 'N/A'}`);
              console.log(`      - 描述: ${codeObj.description || 'N/A'}`);
            }
          });
        }
      });
      
      try {
        // 将扫描值转换为小写，确保大小写不敏感查找
        const normalizedScannedValue = scannedValue.toLowerCase();
        console.log('【调试】原始扫描值:', scannedValue);
        console.log('【调试】标准化扫描值:', normalizedScannedValue);
        
        // 第一优先级：库存单元系统编码查找（最高优先级）
        const foundBySystemCode = inventoryUnitCodeToSortingGroupMap[normalizedScannedValue];
        console.log('【第一优先级】系统编码查找结果:', foundBySystemCode ? '找到' : '未找到');
        if (foundBySystemCode) {
          console.log('【系统编码匹配】找到库存单元:', scannedValue, '分拣组:', foundBySystemCode.sortingGroup.name);
          console.log('库存单元详情:', foundBySystemCode.inventoryUnit);
          console.log('原始系统条码:', foundBySystemCode.originalSystemBarcode);
          
          // 从后端获取最新的库存单元信息，确保状态是最新的
          const latestInventoryUnit = await getLatestInventoryUnit(foundBySystemCode.inventoryUnit.system_barcode);
          console.log('【后端验证】最新库存单元信息:', latestInventoryUnit);
          
          if (latestInventoryUnit) {
            await processInventoryUnitWithSortingGroup(
              scannedValue, // 使用原始扫描值
              foundBySystemCode.sortingGroup.name,
              foundBySystemCode.sortingGroup.system_code,
              latestInventoryUnit.remarks || foundBySystemCode.inventoryUnit.remarks,
              'inventory_unit_system_code',
              latestInventoryUnit.system_barcode, // 传递库存单元系统编码
              latestInventoryUnit // 传递最新的库存单元对象
            );
          } else {
            console.error('【错误】获取最新库存单元信息失败');
            message.error('获取最新库存单元信息失败，请重试');
          }
          return;
        }
        
        // 第二优先级：产品分拣条码查找（备选方案）
        const foundByBarcodeMap = barcodeToSortingGroupMap[normalizedScannedValue];
        console.log('【第二优先级】产品分拣条码查找结果:', foundByBarcodeMap ? '找到' : '未找到');
        if (foundByBarcodeMap) {
          console.log('【分拣条码匹配】找到产品:', foundByBarcodeMap.product.name, '分拣组:', foundByBarcodeMap.sortingGroup.name);
          console.log('产品详情:', foundByBarcodeMap.product);
          console.log('库存单元详情:', foundByBarcodeMap.inventoryUnit);
          console.log('原始分拣条码:', foundByBarcodeMap.originalCode);
          
          // 从后端获取最新的库存单元信息，确保状态是最新的
          const latestInventoryUnit = await getLatestInventoryUnit(foundByBarcodeMap.inventoryUnit.system_barcode);
          console.log('【后端验证】最新库存单元信息:', latestInventoryUnit);
          
          if (latestInventoryUnit) {
            // 简单分流：若命中产品分拣条码对应的库存单元不是散货（LIU），改走"未知条码→创建散货库存单元"流程
            const unitTypeRaw = (latestInventoryUnit as any)?.unit_type || (foundByBarcodeMap.inventoryUnit as any)?.unit_type || '';
            const systemBarcode = (latestInventoryUnit as any)?.system_barcode || (foundByBarcodeMap.inventoryUnit as any)?.system_barcode || '';
            const unitType = typeof unitTypeRaw === 'string' ? unitTypeRaw.toUpperCase() : '';
            const isLoose = unitType === 'LIU' || (typeof systemBarcode === 'string' && systemBarcode.startsWith('LIU-'));

            if (!isLoose) {
              console.log('【分拣条码命中非散货容器】按未知条码处理，创建散货库存单元；unit_type:', unitType, 'system_barcode:', systemBarcode);
              setPendingDimensionsData({
                scannedValue: scannedValue,
                weight: undefined,
                hasLogisticsPackage: false,
                dimensions: {
                  length: undefined,
                  width: undefined,
                  height: undefined
                }
              });
              setDimensionsModalVisible(true);
              message.info('检测到容器型库存单元，已按未知产品条码处理，请录入重量与尺寸以创建散货库存单元');
              return;
            }
            await processInventoryUnitWithSortingGroup(
              scannedValue, // 使用原始扫描值
              foundByBarcodeMap.sortingGroup.name,
              foundByBarcodeMap.sortingGroup.system_code,
              foundByBarcodeMap.product.name,
              'product_sorting_code',
              latestInventoryUnit.system_barcode, // 传递库存单元系统编码
              latestInventoryUnit // 传递最新的库存单元对象
            );
          } else {
            console.error('【错误】获取最新库存单元信息失败');
            message.error('获取最新库存单元信息失败，请重试');
          }
          return;
        }
        
        // 第三优先级：产品列表遍历查找（兜底逻辑）
        console.log('【第三优先级】开始产品列表遍历查找...');
        const foundProduct = projectProducts.find(product =>
          Array.isArray(product.sorting_codes) &&
          product.sorting_codes.some((codeObj: any) => codeObj.code.toLowerCase() === normalizedScannedValue)
        );
        console.log('【第三优先级】产品列表遍历查找结果:', foundProduct ? '找到' : '未找到');
        
        if (foundProduct) {
          console.log('【产品遍历匹配】找到产品:', foundProduct.name, 'ID:', foundProduct.id);
          console.log('产品详情:', foundProduct);
          // 构造模拟的 inventoryUnit 对象，兼容后续处理逻辑
          const inventoryUnit = {
            ...foundProduct,
            sorting_group_info: foundProduct.sorting_group_info || null,
            remarks: foundProduct.name,
            unit_type: foundProduct.unit_type || 'LIU',
            length: foundProduct.length_cm,
            width: foundProduct.width_cm,
            height: foundProduct.height_cm,
            weight: foundProduct.weight_grams,
          };
          console.log('构造的库存单元对象:', inventoryUnit);
          
          // 兼容原有处理逻辑
          if (inventoryUnit.sorting_group_info) {
            const foundSortingGroup = inventoryUnit.sorting_group_info.name;
            const foundSortingGroupSystemCode = inventoryUnit.sorting_group_info.system_code;
            console.log('找到分拣组信息:', foundSortingGroup, foundSortingGroupSystemCode);
            
            // 从后端获取最新的库存单元信息，确保状态是最新的
            const latestInventoryUnit = await getLatestInventoryUnit(inventoryUnit.system_barcode);
            console.log('【后端验证】最新库存单元信息:', latestInventoryUnit);
            
            if (latestInventoryUnit) {
              await processInventoryUnitWithSortingGroup(
                scannedValue,
                foundSortingGroup,
                foundSortingGroupSystemCode,
                latestInventoryUnit.remarks || inventoryUnit.remarks,
                'product_sorting_code_fallback',
                latestInventoryUnit.system_barcode, // 传递库存单元系统编码
                latestInventoryUnit // 传递最新的库存单元对象
              );
            } else {
              console.error('【错误】获取最新库存单元信息失败');
              message.error('获取最新库存单元信息失败，请重试');
            }
          } else {
            console.log('【警告】产品没有分拣组信息，需要手动选择');
            setPendingInventoryUnit({
              code: scannedValue,
              inventoryUnit: inventoryUnit,
              error: undefined
            });
            setAvailableSortingGroups([
              { name: '散货分拣组', systemCode: 'SG-LOOSE-1' },
              { name: '箱装分拣组', systemCode: 'SG-BOX-1' },
              { name: '托盘分拣组', systemCode: 'SG-PALLET-1' },
              { name: '默认分拣组', systemCode: 'SG-DEFAULT-1' },
            ]);
            setSortingGroupModalVisible(true);
          }
          return;
        } else {
          // 没找到，弹出尺寸输入模态框
          console.log('【错误】所有查找方式都失败，需要手动输入');
          console.log('扫描值:', scannedValue);
          console.log('当前映射表状态:');
          console.log('  - 库存单元系统编码映射表:', Object.keys(inventoryUnitCodeToSortingGroupMap));
          console.log('  - 产品分拣条码映射表:', Object.keys(barcodeToSortingGroupMap));
          console.log('  - 项目产品列表:', projectProducts.map(p => ({ id: p.id, name: p.name, sorting_codes: p.sorting_codes })));
          
          setPendingDimensionsData({
            scannedValue: scannedValue,
            weight: undefined,
            hasLogisticsPackage: false,
            dimensions: {
              length: undefined,
              width: undefined,
              height: undefined
            }
          });
          setDimensionsModalVisible(true);
        }
      } catch (error: any) {
        console.error('【错误】查找产品失败:', error);
        console.error('错误详情:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        message.error('查找产品失败，请重试');
      } finally {
        setScanningInventoryUnit(false);
        setInventoryUnitScanValue('');
        console.log('=== 库存单元扫描调试信息结束 ===');
      }
    }
  };

  // 处理库存单元分拣组分配逻辑
  const processInventoryUnitWithSortingGroup = async (
    scannedValue: string, // 实际扫描的条码
    foundSortingGroup: string,
    foundSortingGroupSystemCode: string,
    inventoryUnitName?: string,
    matchType?: string, // 新增：匹配类型参数
    inventoryUnitSystemCode?: string, // 新增：库存单元系统编码
    inventoryUnit?: InventoryUnit // 新增：库存单元对象
  ) => {
    // 用后端返回的最新库存单元数据更新看板的内容物和备注
    if (inventoryUnit) rememberBoardUnit(inventoryUnit);
    // 如果有库存单元对象，检查重量尺寸确认状态
    if (inventoryUnit && !checkWeightDimensionsConfirmation(inventoryUnit)) {
      // 需要确认重量尺寸，等待用户确认后再继续
      return;
    }

    // 优先用传入的 systemCode，否则用 scannedValue
    const systemCode = inventoryUnitSystemCode || scannedValue;
    const existingLocation = findInventoryUnitInDatabase(systemCode);

    // 添加调试信息
    console.log('=== processInventoryUnitWithSortingGroup 调试信息 ===');
    console.log('scannedValue (实际扫描的条码):', scannedValue);
    console.log('inventoryUnitSystemCode (库存单元系统编码):', inventoryUnitSystemCode);
    console.log('systemCode (最终使用的系统编码):', systemCode);
    console.log('foundSortingGroup (找到的分拣组):', foundSortingGroup);
    console.log('foundSortingGroupSystemCode (分拣组系统编码):', foundSortingGroupSystemCode);
    console.log('matchType (匹配类型):', matchType);

    if (existingLocation) {
      console.log('找到现有仓位:', existingLocation);
      setScanResult({
        inventoryUnitCode: systemCode,
        scannedValue: scannedValue,
        temporaryLocation: existingLocation,
        isNew: false,
        matchType: matchType
      });
      console.log('调用 addToScanHistory - 现有仓位情况');
      addToScanHistory(systemCode, scannedValue, existingLocation, foundSortingGroup, foundSortingGroupSystemCode);
    } else {
      const sortingGroupLocation = findSortingGroupLocation(foundSortingGroupSystemCode);
      if (sortingGroupLocation) {
        console.log('找到分拣组仓位:', sortingGroupLocation);
        // 构造分拣条码快照
        const snapshot = (() => {
          try {
            const unit = inventoryUnit;
            const pid = unit?.content?.items?.[0]?.product as any;
            const product = Array.isArray(projectProducts) ? projectProducts.find((p: any) => p.id === pid) : null;
            const codes: string[] = Array.isArray(product?.sorting_codes) ? product!.sorting_codes.map((c: any) => c?.code).filter(Boolean) : [];
            return { productId: pid, sortingBarcodes: codes, displaySortingBarcode: codes.length > 0 ? (codes.length > 1 ? `${codes[0]} ...` : codes[0]) : undefined };
          } catch { return {}; }
        })();
        const newLocation = addInventoryUnitToDatabase(systemCode, foundSortingGroup, foundSortingGroupSystemCode, inventoryUnitName, snapshot as any);
        setScanResult({
          inventoryUnitCode: systemCode,
          scannedValue: scannedValue,
          temporaryLocation: newLocation,
          isNew: false,
          matchType: matchType
        });
        console.log('调用 addToScanHistory - 分拣组仓位情况');
        addToScanHistory(systemCode, scannedValue, newLocation, foundSortingGroup, foundSortingGroupSystemCode);
      } else {
        console.log('创建新仓位');
        const snapshot = (() => {
          try {
            const unit = inventoryUnit;
            const pid = unit?.content?.items?.[0]?.product as any;
            const product = Array.isArray(projectProducts) ? projectProducts.find((p: any) => p.id === pid) : null;
            const codes: string[] = Array.isArray(product?.sorting_codes) ? product!.sorting_codes.map((c: any) => c?.code).filter(Boolean) : [];
            return { productId: pid, sortingBarcodes: codes, displaySortingBarcode: codes.length > 0 ? (codes.length > 1 ? `${codes[0]} ...` : codes[0]) : undefined };
          } catch { return {}; }
        })();
        const newLocation = addInventoryUnitToDatabase(systemCode, foundSortingGroup, foundSortingGroupSystemCode, inventoryUnitName, snapshot as any);
        setScanResult({
          inventoryUnitCode: systemCode,
          scannedValue: scannedValue,
          temporaryLocation: newLocation,
          isNew: true,
          matchType: matchType
        });
        console.log('调用 addToScanHistory - 新仓位情况');
        addToScanHistory(systemCode, scannedValue, newLocation, foundSortingGroup, foundSortingGroupSystemCode);
      }
    }
    console.log('=== processInventoryUnitWithSortingGroup 调试信息结束 ===');
  };

  // 处理分拣组选择
  const handleSortingGroupSelect = (sortingGroup: { name: string; systemCode: string }) => {
    if (!pendingInventoryUnit) return;
    // 处理分拣组分配逻辑
    processInventoryUnitWithSortingGroup(
      pendingInventoryUnit.code, // 实际扫描值
      sortingGroup.name,
      sortingGroup.systemCode,
      pendingInventoryUnit.inventoryUnit?.remarks,
      'manual_selection',
      pendingInventoryUnit.inventoryUnit?.system_barcode, // 传递库存单元系统编码（如果有）
      pendingInventoryUnit.inventoryUnit // 传递库存单元对象（如果有）
    );
    setSortingGroupModalVisible(false);
    setPendingInventoryUnit(null);
  };

  // 处理分拣组选择取消
  const handleSortingGroupCancel = () => {
    setSortingGroupModalVisible(false);
    setPendingInventoryUnit(null);
    message.info('已取消分拣组选择');
  };
  // 处理尺寸输入确认
  const handleDimensionsConfirm = async () => {
    console.log('=== handleDimensionsConfirm 开始 ===');
    
    if (!pendingDimensionsData) {
      console.log('【错误】pendingDimensionsData 为空');
      return;
    }
    
    const { scannedValue, hasLogisticsPackage } = pendingDimensionsData;
    // 库存单元的实际重量/尺寸只存整数（克/厘米）。输入框已限制为整数，这里再取整一次作为兜底，
    // 保证提交给产品和库存单元的是同一组整数，不会出现产品存 12.9、库存单元被截断成 12 的不一致
    const roundWhole = (value?: number) => (value ? Math.round(Number(value)) : undefined);
    const weight = roundWhole(pendingDimensionsData.weight);
    const dimensions = {
      length: roundWhole(pendingDimensionsData.dimensions.length),
      width: roundWhole(pendingDimensionsData.dimensions.width),
      height: roundWhole(pendingDimensionsData.dimensions.height)
    };
    console.log('【调试】输入数据:', {
      scannedValue,
      weight,
      hasLogisticsPackage,
      dimensions
    });
    
    // 验证输入
    if (!weight || !dimensions.length || !dimensions.width || !dimensions.height) {
      console.log('【错误】输入验证失败:', {
        weight,
        length: dimensions.length,
        width: dimensions.width,
        height: dimensions.height
      });
      message.error('请填写完整的重量和长宽高尺寸（均为不小于 1 的整数）');
      return;
    }
    
    try {
      // 获取当前项目信息（当快捷入库开关打开时跳过项目编码验证）
      let currentProject;
      if (!quickInboundEnabled) {
        currentProject = projects.find(p => p.project_code === projectCode);
        if (!currentProject) {
          message.error('未找到当前项目信息');
          return;
        }
      } else {
        console.log('快捷入库模式已开启，跳过项目编码验证');
        // 在快捷入库模式下，可以尝试使用默认项目或跳过项目验证
        currentProject = projects[0]; // 使用第一个可用项目作为默认值
        if (!currentProject) {
          message.error('快捷入库模式下需要至少有一个可用项目');
          return;
        }
      }
      
      console.log('【创建未知产品】开始创建分拣条码和产品记录');
      console.log('【调试】扫描值:', scannedValue);
      console.log('【调试】项目信息:', currentProject);
      
      // 1. 创建分拣条码记录
      console.log('【步骤1】创建分拣条码记录');
      
      // 生成当前日期字符串
      const currentDate = new Date().toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      // 先尝试获取现有的分拣条码，如果不存在则创建新的
      let sortingCodeResponse;
      try {
        // 先尝试获取现有的分拣条码
        console.log('【分拣条码查询】开始查询分拣条码，code:', scannedValue);
        const existingSortingCodes = await getSortingCodeList({ code: scannedValue });
        console.log('【分拣条码查询】查询结果:', existingSortingCodes.data);
        
        if (existingSortingCodes.data.results && existingSortingCodes.data.results.length > 0) {
          // 如果分拣条码已存在，使用现有的
          sortingCodeResponse = { data: existingSortingCodes.data.results[0] };
          console.log('【调试】使用现有分拣条码:', sortingCodeResponse.data);
        } else {
          // 如果分拣条码不存在，创建新的
          console.log('【分拣条码创建】开始创建新分拣条码，code:', scannedValue);
          const sortingCodeData = {
            code: scannedValue,
            description: `未在项目配置中指定的条码 由仓库于${currentDate}创建`
          };
          console.log('【调试】分拣条码创建数据:', sortingCodeData);
          sortingCodeResponse = await createSortingCode(sortingCodeData);
          console.log('【调试】分拣条码创建成功:', sortingCodeResponse.data);
        }
      } catch (error: any) {
        console.error('【错误】处理分拣条码失败:', error);
        console.error('【错误】分拣条码错误详情:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        // 如果获取失败，尝试创建新的
        console.log('【分拣条码创建】查询失败，开始创建新分拣条码，code:', scannedValue);
        const sortingCodeData = {
          code: scannedValue,
          description: `未在项目配置中指定的条码 由仓库于${currentDate}创建`
        };
        console.log('【调试】分拣条码创建数据:', sortingCodeData);
        sortingCodeResponse = await createSortingCode(sortingCodeData);
        console.log('【调试】分拣条码创建成功:', sortingCodeResponse.data);
      }
      
      // 2. 创建产品记录（总是创建新的产品记录）
      console.log('【步骤2】创建产品记录');
      
      // 获取当前操作扫描的用户（仓库员工或管理员）
      console.log('【调试】当前操作用户:', currentUser);
      
      // 确保有当前用户信息
      if (!currentUser || !currentUser.id) {
        message.error('无法获取当前用户信息，请重新登录');
        return;
      }
      
      const productData = {
        name: '未知产品',
        custom_code: 'unknown',
        description: '仓库创建的未知条码产品',
        value_usd: 0,
        weight_grams: weight,
        length_cm: dimensions.length,
        width_cm: dimensions.width,
        height_cm: dimensions.height,
        has_logistics_package: hasLogisticsPackage || false,
        created_by: currentUser.id, // 当前操作扫描的仓库员工或管理员
        customer: currentProject.customer.id, // 项目所属的客户
        projects: [currentProject.id], // 关联当前项目
        sorting_codes: [sortingCodeResponse.data.id], // 关联分拣条码（可能是现有的或新创建的）
        creation_source: 'warehouse' // 设置创建来源为仓库创建
      };
      
      console.log('【调试】产品创建数据:', productData);
      console.log('【调试】开始调用 createProduct...');
      
      const productResponse = await createProduct(productData);
      console.log('【调试】产品创建成功:', productResponse.data);
      console.log('【调试】产品ID:', productResponse.data.id);
      
      // 3. 创建新的库存单元并添加到扫描历史
      console.log('【步骤3】创建库存单元并添加到扫描历史');
      
      // 创建库存单元、库存单元内容和库存单元内容项
      try {
        // 构建分拣组名称
        const sortingGroupName = `SYS-未分组库存分拣组-${currentProject.project_code}`;
        console.log('【调试】分拣组名称:', sortingGroupName);
        
        // 总是创建新的库存单元
        const inventoryUnitData = {
          unit_type: 'LIU',
          source_type: 'warehouse',
          weight: null, // 留空
          length: null, // 留空
          width: null, // 留空
          height: null, // 留空
          weight_dimensions_confirmed: true,
          actual_weight: weight,
          actual_length: dimensions.length,
          actual_width: dimensions.width,
          actual_height: dimensions.height,
          sorting_group_name: sortingGroupName,
          project_id: currentProject.id
        };
        
        console.log('【调试】库存单元创建数据:', inventoryUnitData);
        console.log('【调试】开始调用 createInventoryUnitWithSortingGroup...');
        
        const inventoryUnitResponse = await createInventoryUnitWithSortingGroup(inventoryUnitData);
        console.log('【调试】库存单元创建成功:', inventoryUnitResponse.data);
        console.log('【调试】库存单元ID:', inventoryUnitResponse.data.inventory_unit.id);

        // 复用"异常库存入库"策略：优先使用后端返回的分拣组 system_code，立刻更新映射
        const newSystemBarcode = inventoryUnitResponse.data.inventory_unit.system_barcode;
        const newSortingGroupId = inventoryUnitResponse.data.inventory_unit.sorting_group;
        const normalizedSystemBarcode = newSystemBarcode?.toLowerCase?.() || '';
        const sortingGroupSystemCode = inventoryUnitResponse.data.inventory_unit.sorting_group_info?.system_code || `SG-${currentProject.project_code}-${Date.now()}`;
        try {
          setInventoryUnitCodeToSortingGroupMap(prevMap => ({
            ...prevMap,
            [normalizedSystemBarcode]: {
              sortingGroup: {
                id: newSortingGroupId,
                name: sortingGroupName,
                system_code: sortingGroupSystemCode,
                group_type: 'ungrouped'
              },
              inventoryUnit: inventoryUnitResponse.data.inventory_unit,
              isDirectMatch: true,
              matchType: 'inventory_unit_system_code',
              originalSystemBarcode: newSystemBarcode
            }
          }));
          console.log(`【调试】立即更新映射表，添加新散货库存单元: ${newSystemBarcode} (标准化为: ${normalizedSystemBarcode}) -> ${sortingGroupName} (系统编码: ${sortingGroupSystemCode})`);
        } catch {}
        
        // 创建库存单元内容
        const inventoryContentData = {
          inventory_unit: inventoryUnitResponse.data.inventory_unit.id,
          remarks: `由仓库创建的未知产品库存单元，产品：${productResponse.data.name}`
        };
        
        console.log('【调试】库存单元内容创建数据:', inventoryContentData);
        console.log('【调试】开始调用 createInventoryContent...');
        
        const inventoryContentResponse = await createInventoryContent(inventoryContentData);
        console.log('【调试】库存单元内容创建成功:', inventoryContentResponse.data);
        console.log('【调试】库存单元内容ID:', inventoryContentResponse.data.id);
        
        // 创建库存单元内容项
        const inventoryContentItemData = {
          content: inventoryContentResponse.data.id,
          product: productResponse.data.id,
          quantity: 1,
          remarks: '仓库创建的未知产品库存单元内容项'
        };
        
        console.log('【调试】库存单元内容项创建数据:', inventoryContentItemData);
        console.log('【调试】开始调用 createInventoryContentItem...');
        
        const inventoryContentItemResponse = await createInventoryContentItem(inventoryContentItemData);
        console.log('【调试】库存单元内容项创建成功:', inventoryContentItemResponse.data);
        
        // 添加到扫描历史
        console.log('【调试】开始添加到扫描历史...');
        
        // 使用真实的分拣组 system_code 分配仓位（避免兜底编码导致分裂）
        const temporaryLocation = addInventoryUnitToDatabase(
          inventoryUnitResponse.data.inventory_unit.system_barcode,
          sortingGroupName,
          sortingGroupSystemCode,
          `未知产品-${scannedValue}`,
          { productId: productResponse?.data?.id, sortingBarcodes: [scannedValue], displaySortingBarcode: scannedValue }
        );
        
        console.log('【调试】扫描历史数据:', {
          inventoryUnitCode: inventoryUnitResponse.data.inventory_unit.system_barcode,
          scannedValue,
          temporaryLocation,
          sortingGroup: sortingGroupName,
          sortingGroupSystemCode
        });
        
        // 更新右侧扫描结果显示
        setScanResult({
          inventoryUnitCode: inventoryUnitResponse.data.inventory_unit.system_barcode,
          scannedValue: scannedValue,
          temporaryLocation: temporaryLocation,
          isNew: true,
          matchType: 'unknown_product_created'
        });
        
        addToScanHistory(
          inventoryUnitResponse.data.inventory_unit.system_barcode,
          scannedValue,
          temporaryLocation,
          sortingGroupName,
          sortingGroupSystemCode
        );
        
        console.log('【调试】扫描历史添加完成');
      } catch (error: any) {
        console.error('【错误】创建库存单元失败:', error);
        console.error('【错误】错误详情:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
          headers: error.response?.headers
        });
        console.error('【错误】错误堆栈:', error.stack);
        
        // 即使库存单元创建失败，也要添加到扫描历史
        const newInventoryUnitCode = `UNKNOWN-${Date.now()}`;
        console.log('【调试】创建临时库存单元编码:', newInventoryUnitCode);
        
        const temporaryLocation = addInventoryUnitToDatabase(
          newInventoryUnitCode,
          '默认分拣组',
          'SG-DEFAULT-1',
          `未知库存单元-${scannedValue}`,
          { sortingBarcodes: [scannedValue], displaySortingBarcode: scannedValue }
        );
        
        console.log('【调试】临时仓位分配:', temporaryLocation);
        
        // 更新右侧扫描结果显示
        setScanResult({
          inventoryUnitCode: newInventoryUnitCode,
          scannedValue: scannedValue,
          temporaryLocation: temporaryLocation,
          isNew: true,
          matchType: 'unknown_product_fallback'
        });
        
        addToScanHistory(
          newInventoryUnitCode,
          scannedValue,
          temporaryLocation,
          `SYS-未分组库存分拣组-${currentProject.project_code}`, // 使用我们创建的分拣组名称
          getUngroupedSortingGroupSystemCode() // 使用实际的分拣组系统编码
        );
      }
      
      // 在本地保存本次输入作为"上次数据"
      saveUnknownUnitLastData({
        weight: Number(weight),
        hasLogisticsPackage: !!hasLogisticsPackage,
        dimensions: {
          length: Number(dimensions.length),
          width: Number(dimensions.width),
          height: Number(dimensions.height)
        }
      });

      // 关闭模态框并清理状态
      console.log('【调试】开始清理状态...');
      setDimensionsModalVisible(false);
      setPendingDimensionsData(null);
      
      message.success('已成功创建未知产品并添加到扫描记录');
      console.log('【调试】成功消息已显示');
      
      // 重新获取项目产品列表，确保新创建的产品能被后续扫描找到
      if (projectCode) {
        console.log('【映射表更新】开始更新映射表...');
        console.log('【调试】项目编码:', projectCode);
        
        try {
          await fetchProjectProducts(projectCode);
          console.log('【调试】项目产品列表更新成功');
          
          // 增加延迟时间，确保后端数据已完全同步
          console.log('【调试】等待2秒确保后端数据同步...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          await fetchInventoryConfigAndBuildMap(projectCode);
          console.log('【映射表更新】映射表更新完成');
          
          // 映射表更新完成
          console.log('【映射表更新】映射表更新完成，新创建的库存单元应该已包含在映射表中');
        } catch (error: any) {
          console.error('【错误】映射表更新失败:', error);
        }
      } else {
        console.log('【调试】项目编码为空，跳过映射表更新');
      }
      
      // 清理数据状态
      console.log('【调试】最终清理状态...');
      setDimensionsModalVisible(false);
      setPendingDimensionsData(null);
      setInventoryUnitScanValue(''); // 清空扫描输入框
      console.log('=== handleDimensionsConfirm 成功完成 ===');
      
    } catch (error: any) {
      console.error('【错误】创建未知产品失败:', error);
      console.error('【错误】错误详情:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      console.error('【错误】错误堆栈:', error.stack);
      
      const errorMessage = error.response?.data?.detail || '创建未知产品失败，请重试';
      console.log('【调试】显示错误消息:', errorMessage);
      message.error(errorMessage);
      
      // 即使失败也要清理数据状态
      console.log('【调试】清理失败状态...');
      setDimensionsModalVisible(false);
      setPendingDimensionsData(null);
      setInventoryUnitScanValue(''); // 清空扫描输入框
      console.log('=== handleDimensionsConfirm 失败完成 ===');
    }
  };

  // 处理尺寸输入取消
  const handleDimensionsCancel = () => {
    setDimensionsModalVisible(false);
    setPendingDimensionsData(null);
    message.info('已取消产品信息输入');
  };
  // 处理重量尺寸确认
  const handleWeightDimensionsConfirm = async () => {
    if (!currentInventoryUnit) return;

    // 库存单元的实际重量/尺寸只存整数（克/厘米）。输入框已限制为整数，这里再取整一次作为兜底，
    // 并拦住小于 1 的值——否则后端会把小数截断、把 0.8 存成 0，单元却被标记为"已确认"
    const roundWhole = (value?: number) => Math.round(Number(value) || 0);
    const confirmedValues = {
      ...actualWeightDimensions,
      actual_weight: roundWhole(actualWeightDimensions.actual_weight),
      actual_length: roundWhole(actualWeightDimensions.actual_length),
      actual_width: roundWhole(actualWeightDimensions.actual_width),
      actual_height: roundWhole(actualWeightDimensions.actual_height)
    };
    if (
      confirmedValues.actual_weight < 1 ||
      confirmedValues.actual_length < 1 ||
      confirmedValues.actual_width < 1 ||
      confirmedValues.actual_height < 1
    ) {
      message.error('实际重量和长宽高均需填写不小于 1 的整数');
      return;
    }

    setConfirmingWeightDimensions(true);
    try {
      await confirmInventoryUnitWeightDimensions(currentInventoryUnit.id, confirmedValues);
      
      message.success('重量尺寸确认成功');
      // 清空旧结果，避免关闭弹窗瞬间误播报历史仓位
      setScanResult(null);
      
      // 关闭确认对话框
      setWeightDimensionsModalVisible(false);
      
      // 分支1：若处于待处理的快捷入库，则在用户确认容器装载内容后继续执行快捷入库
      if (pendingQuickInbound && currentInventoryUnit?.system_barcode === pendingQuickInbound.inventoryUnitBarcode) {
        const pendingContents = pendingQuickInbound.contents || [];
        quickInboundConfirmValuesRef.current = { actualQuantity: pendingContents[0]?.quantity ?? 0, remarks: '' };
        const shouldProceedQuickInbound = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: renderQuickInboundConfirmTitle(pendingQuickInbound.containerCode),
            content: <QuickInboundConfirmForm contents={pendingContents} forecastRemarks={pendingQuickInbound.forecastRemarks} />,
            centered: true,
            width: 680,
            okText: '确认入库',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!shouldProceedQuickInbound) {
          setPendingQuickInbound(null);
          setCurrentInventoryUnit(null);
          setTimeout(() => {
            if (mainScanInputRef.current) {
              mainScanInputRef.current.focus();
            }
          }, 100);
          return;
        }
        const { actualQuantity: confirmedActualQuantity, remarks: confirmedRemarks } = quickInboundConfirmValuesRef.current;

        try {
          const hideLoading = message.loading('正在处理快捷入库...', 0);
          const response = await quickInbound(pendingQuickInbound.containerCode, {
            actual_quantity: confirmedActualQuantity,
            remarks: confirmedRemarks
          });
          hideLoading();
          message.success(
            `快捷入库处理完成！\n` +
            `入库记录ID: ${response.inbound_record_id}\n` +
            `入库记录编码: ${response.inbound_record_code}\n` +
            `仓储容器编码: ${response.storage_container_code}\n` +
            `入库项目数量: ${response.inbound_items_count}\n` +
            `容器内容数量: ${response.container_contents_count}`
          );
          try { setCurrentInboundRecordId(Number(response?.inbound_record_id) || null); } catch {}
          
          if (response.storage_container_id) {
            try {
              const labelData: StorageContainerLabelData = {
                id: `storage-container-${response.storage_container_id}`,
                templateId: 'storage-container-4.25x1.25',
                containerInfo: {
                  id: response.storage_container_id,
                  systemCode: response.storage_container_code,
                  name: `仓储容器-${response.storage_container_code}`,
                  type: response.container_type || 'box',
                  typeDisplay: response.container_type_display || '箱子',
                  status: 'active',
                  statusDisplay: '正常',
                  warehouse: {
                    id: response.warehouse_id || 1,
                    name: response.warehouse_name || '未知仓库',
                    code: response.warehouse_code || 'UNKNOWN'
                  },
                  project: {
                    id: response.project_id || 1,
                    name: response.project_name || '未知项目',
                    code: response.project_code || 'UNKNOWN'
                  },
                  location: undefined,
                  createdBy: response.created_by_username || '系统用户',
                  createdAt: new Date().toLocaleString(),
                  updatedAt: new Date().toLocaleString()
                },
                contentSummary: {
                  totalItems: response.container_contents_count || 0,
                  totalQuantity: response.total_quantity || response.container_contents_count || 0,
                  itemTypes: response.container_contents?.map((item: any) => 
                    item.inventory_unit_name || '快捷入库物品'
                  ) || ['快捷入库物品'],
                  items: response.container_contents?.map((item: any) => ({
                    code: item.inventory_unit_code,
                    quantity: item.quantity
                  })) || []
                },
                printOptions: {
                  copies: 1,
                  directPrint: false
                }
              };
              setLabelPreviewData(labelData);
              setLabelPreviewVisible(true);
              message.info('仓储容器标签已生成，请查看预览');
            } catch (labelError) {
              console.error('【快捷入库】标签生成失败:', labelError);
            }
          }
          
          // 完成后清理挂起状态
          setPendingQuickInbound(null);
          setCurrentInventoryUnit(null);
          setInventoryUnitScanValue('');
          
          setTimeout(() => {
            if (scanInputRef.current) {
              scanInputRef.current.focus();
            }
          }, 300);
          return;
        } catch (error: any) {
          message.destroy();
          const errorMessage = error.response?.data?.error || '快捷入库处理失败，请重试';
          message.error(errorMessage);
          setPendingQuickInbound(null);
          setCurrentInventoryUnit(null);
          setTimeout(() => {
            if (mainScanInputRef.current) {
              mainScanInputRef.current.focus();
            }
          }, 100);
          return;
        }
      }

      // 分支2：常规流程（非快捷入库挂起）——沿用原有逻辑
      setCurrentInventoryUnit(null);
      const latestInventoryUnit = await getLatestInventoryUnit(currentInventoryUnit.system_barcode);
      if (latestInventoryUnit) {
        await processInventoryUnitWithSortingGroup(
          lastScannedValueRef.current || inventoryUnitScanValue || latestInventoryUnit.system_barcode,
          latestInventoryUnit.sorting_group_info?.name || '默认分拣组',
          latestInventoryUnit.sorting_group_info?.system_code || 'SG-DEFAULT-1',
          latestInventoryUnit.remarks,
          'weight_dimensions_confirmed',
          latestInventoryUnit.system_barcode,
          latestInventoryUnit
        );
      } else {
        message.error('获取最新库存单元信息失败，请重试');
      }
      setInventoryUnitScanValue('');
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || '确认重量尺寸失败，请重试';
      message.error(errorMessage);
    } finally {
      setConfirmingWeightDimensions(false);
    }
  };

  // 处理重量尺寸确认取消
  const handleWeightDimensionsCancel = () => {
    setWeightDimensionsModalVisible(false);
    setCurrentInventoryUnit(null);
    message.info('已取消重量尺寸确认');
  };

  // 从后端获取最新的库存单元信息
  const getLatestInventoryUnit = async (systemBarcode: string): Promise<InventoryUnit | null> => {
    try {
      console.log('【后端API】开始获取库存单元信息, systemBarcode:', systemBarcode);
      const response = await getInventoryUnitByBarcode(systemBarcode);
      console.log('【后端API】获取库存单元信息成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('【后端API】获取库存单元信息失败:', error);
      console.error('【后端API】错误详情:', {
        systemBarcode,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return null;
    }
  };

  // 待处理的快捷入库（在重量尺寸确认后继续执行）
  const [pendingQuickInbound, setPendingQuickInbound] = useState<{
    containerCode: string;
    inventoryUnitBarcode: string;
    contents: any[];
    forecastRemarks?: string;
  } | null>(null);

  // 检查库存单元重量尺寸确认状态
  const checkWeightDimensionsConfirmation = (inventoryUnit: InventoryUnit) => {
    // 异常库存单元：跳过重量尺寸确认
    if ((inventoryUnit as any)?.unit_type === 'AIU') {
      return true;
    }
    if (!inventoryUnit.weight_dimensions_confirmed) {
      // 设置实际数据为理论数据
      setActualWeightDimensions({
        actual_weight: inventoryUnit.weight || 0,
        actual_length: inventoryUnit.length || 0,
        actual_width: inventoryUnit.width || 0,
        actual_height: inventoryUnit.height || 0
      });
      
      setCurrentInventoryUnit(inventoryUnit);
      setWeightDimensionsModalVisible(true);
      return false; // 需要确认
    }
    return true; // 已确认
  };

  // 处理库存单元扫描输入变化
  const handleInventoryUnitScanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInventoryUnitScanValue(e.target.value);
  };

  // ===== 手动选择库存单元：货物没有条码时，按包装标识或产品照片手动选，选中后当作扫描了它的系统编码，走原有扫描流程 =====
  const manualPickUnits = useMemo(() => {
    // 本次入库预报里的库存单元（物流单号或预报容器标签对应的预报），排在前面
    const forecastCodes = new Set<string>();
    try {
      const addCode = (code: any) => { const c = String(code || '').trim().toLowerCase(); if (c) forecastCodes.add(c); };
      (Array.isArray((trackingForecastData as any)?.containers) ? (trackingForecastData as any).containers : []).forEach((container: any) => {
        (Array.isArray(container?.contents) ? container.contents : []).forEach((ct: any) => {
          addCode(ct?.inventory_unit_detail?.system_barcode || ct?.inventory_unit?.system_barcode || ct?.inventory_unit?.system_code);
        });
      });
      (Array.isArray(containerData?.contents) ? containerData.contents : []).forEach((ct: any) => {
        addCode(ct?.inventory_unit?.system_code || ct?.inventory_unit?.system_barcode);
      });
    } catch {}
    const productsById = new Map<number, any>();
    (projectProducts || []).forEach((p: any) => productsById.set(Number(p?.id), p));
    const seen = new Set<string>();
    const rows: any[] = [];
    Object.values(inventoryUnitCodeToSortingGroupMap || {}).forEach((entry: any) => {
      const unit = entry?.inventoryUnit;
      const code = String(entry?.originalSystemBarcode || unit?.system_barcode || '').trim();
      if (!code || seen.has(code.toLowerCase())) return;
      // 只列有产品内容物的散货/箱/托盘单元；异常库存、非上架货物、原箱/原托盘单元有各自的创建流程，不在这里选
      const unitType = String(unit?.unit_type || '').toUpperCase();
      const groupName = String(entry?.sortingGroup?.name || '');
      const items = Array.isArray(unit?.content?.items) ? unit.content.items : [];
      if (!['LIU', 'BIU', 'PIU'].includes(unitType) || items.length === 0) return;
      if (groupName.startsWith('SYS-原箱入库分拣组') || groupName.startsWith('SYS-原托盘入库分拣组')) return;
      seen.add(code.toLowerCase());
      const products = items.map((it: any) => {
        const product = productsById.get(Number(it?.product)) || {};
        const sortingCodes = (Array.isArray(product?.sorting_codes) ? product.sorting_codes : [])
          .map((s: any) => (typeof s === 'string' ? s : s?.code)).filter(Boolean);
        return {
          name: product?.name || it?.product_name || '-',
          customCode: product?.custom_code || it?.product_custom_code || '',
          photo: product?.photo || '',
          sortingCodes,
          quantity: it?.quantity,
        };
      });
      rows.push({ code, unitType, groupName, products, inForecast: forecastCodes.has(code.toLowerCase()) });
    });
    rows.sort((a, b) => (Number(b.inForecast) - Number(a.inForecast)) || a.code.localeCompare(b.code));
    return rows;
  }, [inventoryUnitCodeToSortingGroupMap, projectProducts, trackingForecastData, containerData]);

  const manualPickFilteredUnits = useMemo(() => {
    const q = manualPickKeyword.trim().toLowerCase();
    if (!q) return manualPickUnits;
    const hit = (v: any) => String(v || '').toLowerCase().includes(q);
    return manualPickUnits.filter((row: any) => hit(row.code) || row.products.some((p: any) => hit(p.name) || hit(p.customCode) || p.sortingCodes.some(hit)));
  }, [manualPickUnits, manualPickKeyword]);

  useEffect(() => { setManualPickPage(1); }, [manualPickKeyword]);

  // 重量尺寸确认弹窗里显示的内容物（库存单元详情接口自带产品名、客户编码、照片；缺的从项目产品里补）
  const confirmUnitContentLines = useMemo((): UnitContentLine[] => unitContentLinesOf(currentInventoryUnit, projectProducts), [currentInventoryUnit, projectProducts]);

  // ===== 入库分拣看板：每个库存单元的内容物和备注 =====
  // 看板只存了编码等信息；内容物和备注按系统编码缓存：扫描时用后端返回的最新数据更新，当前项目的从已加载的库存配置里取，
  // 其他的（别的项目、刷新页面后留下的）按编码批量向后端查询一次
  const [boardUnitDetails, setBoardUnitDetails] = useState<Record<string, any>>({});
  const boardUnitFetchedRef = useRef<Set<string>>(new Set());
  const rememberBoardUnit = (unit: any) => {
    const code = String(unit?.system_barcode || '').trim().toLowerCase();
    if (code) setBoardUnitDetails((prev) => ({ ...prev, [code]: unit }));
  };
  const boardUnitOf = (code: string): any => {
    const key = String(code || '').trim().toLowerCase();
    return boardUnitDetails[key] || (inventoryUnitCodeToSortingGroupMap as any)[key]?.inventoryUnit || null;
  };
  useEffect(() => {
    const missing = Array.from(new Set((sortingDatabase || []).map((row: any) => String(row?.inventoryUnitCode || '').trim()).filter(Boolean)))
      .filter((code) => {
        const key = code.toLowerCase();
        return !boardUnitDetails[key] && !(inventoryUnitCodeToSortingGroupMap as any)[key] && !boardUnitFetchedRef.current.has(key);
      });
    if (missing.length === 0) return;
    missing.forEach((code) => boardUnitFetchedRef.current.add(code.toLowerCase()));
    void (async () => {
      for (let start = 0; start < missing.length; start += 100) {
        const chunk = missing.slice(start, start + 100);
        try {
          const resp = await request.get('/api/v1/inventory/inventory-units/', { params: { system_barcodes: chunk.join(','), for_modal: 1, page_size: 100 } });
          const units = Array.isArray(resp?.data?.results) ? resp.data.results : [];
          if (units.length > 0) {
            setBoardUnitDetails((prev) => {
              const next = { ...prev };
              units.forEach((unit: any) => { const key = String(unit?.system_barcode || '').toLowerCase(); if (key) next[key] = unit; });
              return next;
            });
          }
        } catch {
          // 查不到时这些单元的内容物和备注显示"-"，不影响看板其他功能
        }
      }
    })();
  }, [sortingDatabase, inventoryUnitCodeToSortingGroupMap, boardUnitDetails]);

  const pickInventoryUnitManually = (code: string) => {
    setManualPickOpen(false);
    setInventoryUnitScanValue(code);
    setPendingManualScanCode(code);
  };

  // 扫描框的值更新成选中的系统编码之后，再调用原有的扫描处理（它读取的是扫描框当前的值）
  useEffect(() => {
    if (!pendingManualScanCode || inventoryUnitScanValue !== pendingManualScanCode) return;
    setPendingManualScanCode(null);
    void handleInventoryUnitScanInput({ key: 'Enter' } as React.KeyboardEvent<HTMLInputElement>);
  }, [pendingManualScanCode, inventoryUnitScanValue]);

  // 获取项目列表
  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      console.log('开始获取项目列表...');
      
      // 首先尝试获取所有项目（使用大分页）
      let allProjects: Project[] = [];
      let nextUrl: string | null = null;
      let page = 1;
      let totalCount = 0;
      
      do {
        console.log(`正在获取第 ${page} 页项目...`);
        const response = await getProjectList({
          page: page,
          page_size: 100, // 每页100个
          ordering: 'project_code'
        });
        
        allProjects = allProjects.concat(response.data.results);
        nextUrl = response.data.next;
        totalCount = response.data.count;
        page++;
        
        console.log(`第 ${page - 1} 页获取了 ${response.data.results.length} 个项目，总计: ${allProjects.length}`);
        
      } while (nextUrl && allProjects.length < totalCount);
      
      // 添加详细的调试信息
      console.log('=== 项目列表调试信息 ===');
      console.log('最终项目总数:', allProjects.length);
      console.log('分页获取完成');
      
      // 显示项目详情
      console.log('项目列表详情:');
      allProjects.forEach((project, index) => {
        console.log(`${index + 1}. ${project.project_code} - ${project.name} (客户: ${project.customer?.username || 'N/A'})`);
      });
      
      setProjects(allProjects);
      console.log('项目列表设置完成，当前项目数量:', allProjects.length);
      
    } catch (error: any) {
      console.error('获取项目列表失败:', error);
      console.error('错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      message.error('获取项目列表失败，请重试');
    } finally {
      setLoadingProjects(false);
    }
  };
  // 新增：获取仓库列表
  const { isWarehouseStaff, permissions } = usePermissions();
  const fetchWarehouses = async () => {
    setLoadingWarehouses(true);
    try {
      const response = await getWarehouseList({});
      const warehousesList = response.data.results || [];
      const staffMode = isWarehouseStaff();
      const assignedIds = (permissions?.assigned_warehouse_ids || []) as number[];
      const filteredList = staffMode && assignedIds.length > 0
        ? warehousesList.filter((w: any) => assignedIds.includes(Number(w.id)))
        : warehousesList;
      setWarehouses(filteredList);
      if (staffMode) {
        if (filteredList.length > 0) {
          setWarehouseCode(filteredList[0].code);
          console.log('自动选择员工归属仓库:', filteredList[0]);
        }
      } else {
        const targetWarehouse = warehousesList.find((warehouse: any) => warehouse.code === 'US-1-NH');
        if (targetWarehouse) {
          setWarehouseCode('US-1-NH');
          console.log('自动选择默认仓库:', targetWarehouse.name);
        }
      }
    } catch (error) {
      console.error('获取仓库列表失败:', error);
      message.error('获取仓库列表失败');
    } finally {
      setLoadingWarehouses(false);
    }
  };

  // 新增：处理仓库选择变化
  const handleWarehouseChange = (value: string) => {
    setWarehouseCode(value);
  };

  // 处理项目编码变化
  const handleProjectCodeChange = (value: string) => {
    setProjectCode(value);
    
    // 清空之前的搜索结果
    if (searchResults.length > 0 || searchError) {
      setSearchResults([]);
      setSearchError('');
    }
    
    // 清空映射表和相关状态，确保新项目使用干净的数据
    setBarcodeToSortingGroupMap({});
    setInventoryUnitCodeToSortingGroupMap({});
    setInventoryConfig(null);
    setProjectProducts([]); // 清空产品数据，确保重新获取
    
    console.log('【调试】项目切换，已清空所有相关状态');
    console.log('【调试】项目切换详情:', {
      新项目编码: value,
      清空映射表: true,
      清空产品数据: true,
      清空库存配置: true
    });
  };

  // 获取项目类型显示文本
  const getProjectTypeText = (project: Project) => {
    return project.project_type_display || project.project_type;
  };
  
  // 处理创建原箱/原托盘库存单元
  const handleCreateOriginalContainerUnit = async () => {
    // 防重复提交检查
    if (isCreatingInventoryUnit) {
      message.warning('正在创建中，请稍候...');
      return;
    }
    
    try {
      // 验证必要数据（当快捷入库开关打开时跳过项目编码验证）
      if (!quickInboundEnabled && !projectCode) {
        message.error('请先选择项目编码');
        return;
      }
      
      if (quickInboundEnabled && !projectCode) {
        console.log('快捷入库模式已开启，跳过项目编码验证');
      }
      
      if (!originalContainerType) {
        message.error('请选择容器类型');
        return;
      }
      
      // 验证尺寸数据
      if (!originalContainerDimensions.length || !originalContainerDimensions.width || !originalContainerDimensions.height) {
        message.error('请填写完整的尺寸信息');
        return;
      }

      // 验证重量必填：不填会按最低重量档位收费
      if (!originalContainerDimensions.weight) {
        message.error('请填写重量（必须大于0）');
        return;
      }

      // 验证数量必填（>=1）
      if (originalContainerQuantity === undefined || Number.isNaN(Number(originalContainerQuantity)) || Number(originalContainerQuantity) < 1) {
        message.error('请填写数量（至少为1）');
        return;
      }

      // 托盘重量超出常见范围时先确认
      const palletWeight = originalContainerDimensions.weight;
      if (originalContainerType === 'pallet' && (palletWeight < PALLET_WEIGHT_CONFIRM_MIN_G || palletWeight > PALLET_WEIGHT_CONFIRM_MAX_G)) {
        const confirmed = await new Promise<boolean>(resolve => {
          Modal.confirm({
            title: '请确认托盘重量',
            content: `单个托盘重量为 ${palletWeight / 1000} kg，不在常见范围（${PALLET_WEIGHT_CONFIRM_MIN_G / 1000}-${PALLET_WEIGHT_CONFIRM_MAX_G / 1000} kg）内。请确认填写的是单个托盘的重量，单位是克。`,
            okText: '确认无误，继续创建',
            cancelText: '返回修改',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!confirmed) return;
      }

      setIsCreatingInventoryUnit(true);

      // 库存单元备注：跟踪号和第三方参考号都带上标识一起保存，避免分不清是哪个号码；托盘重量为预估时也记录下来
      const unitRemarks = [
        originalContainerRemarks.trim() && `跟踪号-${originalContainerRemarks.trim()}`,
        originalContainerThirdPartyRef.trim() && `第三方参考号-${originalContainerThirdPartyRef.trim()}`,
        originalContainerType === 'pallet' && originalContainerWeightEstimated && '重量为预估'
      ].filter(Boolean).join('；');

      // 准备请求数据
      const requestData = {
        project_code: projectCode,
        container_type: originalContainerType as 'box' | 'pallet',
        actual_length: originalContainerDimensions.length,
        actual_width: originalContainerDimensions.width,
        actual_height: originalContainerDimensions.height,
        actual_weight: originalContainerDimensions.weight,
        remarks: unitRemarks
      };

      console.log('创建原箱/原托盘库存单元，请求数据:', requestData);

      // 调用API创建库存单元
      const response = await createOriginalContainerUnit(requestData);

      console.log('创建库存单元成功:', response);
      // 记录实际发生的非常规操作：原箱/原托盘入库（中文标签）
      setPerformedIrregularOps(prev => new Set(Array.from(prev).concat(getIrregularOperationLabel('original_box_pallet_inbound'))));

      // 记录本次尺寸和重量，供下次"使用上次数据"按钮回填
      saveLastDimensions(ORIGINAL_CONTAINER_LAST_DIMENSIONS_KEY, {
        length: originalContainerDimensions.length,
        width: originalContainerDimensions.width,
        height: originalContainerDimensions.height,
        weight: originalContainerDimensions.weight,
        weightEstimated: originalContainerType === 'pallet' ? originalContainerWeightEstimated : undefined,
      });

      message.success(`库存单元创建成功！系统条码：${response.data.inventory_unit.system_barcode}`);

      // 1. 立即更新映射表，添加新创建的库存单元
      const newSystemBarcode = response.data.inventory_unit.system_barcode;
      const newSortingGroupId = response.data.inventory_unit.sorting_group;
      
      if (newSystemBarcode && newSortingGroupId) {
        // 由于后端返回的sorting_group是ID，我们需要构造一个临时的分拣组对象
        // 这里使用已知的分拣组名称，因为我们在创建时使用了包含项目编码的名称格式
        const sortingGroupName = originalContainerType === 'box' ? `SYS-原箱入库分拣组-${projectCode}` : `SYS-原托盘入库分拣组-${projectCode}`;
        
        // 使用标准化的小写键值，确保与查找逻辑一致
        const normalizedSystemBarcode = newSystemBarcode.toLowerCase();
        
        // 优先使用后端返回的sorting_group_info中的system_code，如果没有则使用临时编码
        const sortingGroupSystemCode = response.data.inventory_unit.sorting_group_info?.system_code || `SG-${projectCode}-${Date.now()}`;
        
        setInventoryUnitCodeToSortingGroupMap(prevMap => ({
          ...prevMap,
          [normalizedSystemBarcode]: {
            sortingGroup: {
              id: newSortingGroupId,
              name: sortingGroupName,
              system_code: sortingGroupSystemCode,
              group_type: originalContainerType === 'box' ? 'box' : 'pallet'
            },
            inventoryUnit: response.data.inventory_unit,
            isDirectMatch: true,
            matchType: 'inventory_unit_system_code',
            originalSystemBarcode: newSystemBarcode // 保留原始大小写
          }
        }));
        
        console.log(`【调试】立即更新映射表，添加新库存单元: ${newSystemBarcode} (标准化为: ${normalizedSystemBarcode}) -> ${sortingGroupName} (系统编码: ${sortingGroupSystemCode})`);
      }
      
              // 2. 设置扫描值
        setInventoryUnitScanValue(newSystemBarcode);
        
        // 3. 直接添加到分拣数据库，实现空仓位重用
        const sortingGroupName = originalContainerType === 'box' ? `SYS-原箱入库分拣组-${projectCode}` : `SYS-原托盘入库分拣组-${projectCode}`;
        const sortingGroupSystemCode = response.data.inventory_unit.sorting_group_info?.system_code || `SG-${projectCode}-${Date.now()}`;
        
        // 使用空仓位重用逻辑分配临时仓位
        const temporaryLocation = addInventoryUnitToDatabase(
          newSystemBarcode,
          sortingGroupName,
          sortingGroupSystemCode,
          originalContainerRemarks || '原箱/原托盘入库'
        );
        
        console.log(`【调试】已分配临时仓位: ${temporaryLocation}，分拣组: ${sortingGroupName}`);
        
        // 4. 自动触发扫描和提交逻辑
        setTimeout(async () => {
          console.log('【调试】自动触发扫描逻辑，扫描值:', newSystemBarcode);
          try {
            // 直接触发扫描按钮点击事件
            const scanButton = document.querySelector('[data-testid="scan-inventory-unit-button"]') as HTMLButtonElement;
            if (scanButton && !scanButton.disabled) {
              scanButton.click();
              console.log('【调试】自动点击扫描按钮成功');
              
              // 5. 延迟更新扫描记录中的数量为按钮前面的数量输入框中的数字
              setTimeout(() => {
                console.log('【调试】开始更新扫描记录数量，目标数量:', originalContainerQuantity);
                setScanHistory(prevHistory => {
                  if (prevHistory.length > 0) {
                    // 找到最新添加的扫描记录（第一条）并更新数量
                    const updatedHistory = [...prevHistory];
                  if (updatedHistory[0] && updatedHistory[0].inventoryUnitCode === newSystemBarcode) {
                    updatedHistory[0] = {
                      ...updatedHistory[0],
                      quantity: originalContainerQuantity
                    };
                    console.log('【调试】成功更新扫描记录数量为:', originalContainerQuantity);
                  }
                  return updatedHistory;
                }
                return prevHistory;
              });
              
              // 5. 重置数量输入框为1
              setTimeout(() => {
                setOriginalContainerQuantity(1);
                console.log('【调试】已重置原箱/原托盘数量输入框为1');
              }, 500); // 延迟0.5秒，确保数量更新完成
            }, 1500); // 延迟1.5秒，确保扫描记录已添加
          } else {
            console.log('【调试】扫描按钮不可用，跳过自动扫描');
          }
        } catch (error: any) {
          console.error('【调试】自动扫描失败:', error);
        }
        
        // 6. 再延时，生成库存单元标签预览（箱/托盘 4x6）
        setTimeout(() => {
          try {
            const isBox = originalContainerType === 'box';
            const unitType = isBox ? 'BIU' : 'PIU';
            const trackingText = (originalContainerRemarks || currentScanValue || '').toString().trim();
            const thirdPartyRefText = originalContainerThirdPartyRef.trim();
            const source = {
              id: newSystemBarcode,
              name: newSystemBarcode,
              system_code: newSystemBarcode,
              text: newSystemBarcode,
              unit_type: unitType,
              // 先传占位，后续通过 transform 后覆盖 values.content_items 与 values.quantity
              content_items: []
            } as any;
            const adapter = new InventoryUnitLabelAdapter();
            const data = adapter.transform(source);
            // 覆盖内容物（以换行拼接为单个字符串）：第一行（括号）原箱/原托盘入库，下面是跟踪号和参考号，没有的不打印；不在内容物里显示数量
            data.values.content_items = [
              '（原箱/原托盘入库）',
              trackingText && `跟踪号: ${trackingText}`,
              thirdPartyRefText && `参考号: ${thirdPartyRefText}`
            ].filter(Boolean).join('\n');
            // 按数量生成多份标签
            data.values.quantity = Number(originalContainerQuantity || 1);
            setInventoryUnitLabelPreviewData(data);
            setInventoryUnitLabelPreviewVisible(true);
            console.log('【标签预览】已生成并打开');
          } catch (e) {
            console.warn('【标签预览】生成失败', e);
          }
        }, 2300); // 确保晚于数量回填
      }, 1000); // 延迟1秒，确保状态更新完成
      
      // 5. 异步重新构建映射表，确保数据一致性
      setTimeout(() => {
        console.log('【调试】异步重新构建映射表...');
        fetchInventoryConfigAndBuildMap(projectCode);
      }, 2000);
      
      // 清空相关状态
      setOriginalContainerType('');
      setOriginalContainerDimensions({
        length: undefined,
        width: undefined,
        height: undefined
      });
      // 把本次的第三方参考号追加到入库备注后面（空格间隔），不覆盖已有内容；已有相同参考号（不区分大小写）则不再追加
      const thirdPartyRefForRemarks = originalContainerThirdPartyRef.trim();
      if (thirdPartyRefForRemarks) {
        setInboundRemarks(prev => {
          const normalize = (text: string) => ` ${text.trim().split(/\s+/).join(' ').toLowerCase()} `;
          if (normalize(prev).includes(normalize(thirdPartyRefForRemarks))) return prev;
          return prev.trim() ? `${prev.trimEnd()} ${thirdPartyRefForRemarks}` : thirdPartyRefForRemarks;
        });
      }
      // 跟踪号恢复为自动填入并禁用的状态，和重新选择"原箱/原托盘入库"一样
      const scannedTracking = (currentScanValue || '').trim();
      setOriginalContainerRemarks(scannedTracking);
      if (scannedTracking) setOriginalRemarksLocked(true);
      setOriginalContainerThirdPartyRef('');
      setOriginalContainerNoThirdPartyRef(false);
      setOriginalContainerWeightEstimated(true);

    } catch (error: any) {
      console.error('创建库存单元失败:', error);
      const errorMessage = error.response?.data?.detail || '创建库存单元失败';
      message.error(errorMessage);
    } finally {
      setIsCreatingInventoryUnit(false);
    }
  };
  // 创建配件/包材/标签等非上架产品库存单元
  const handleCreateAccessoriesInventoryUnit = async () => {
    // 防重复提交检查
    if (isCreatingAccessoriesInventoryUnit) {
      message.warning('正在创建中，请稍候...');
      return;
    }
    
    try {
      // 验证必要数据（当快捷入库开关打开时跳过项目编码验证）
      if (!quickInboundEnabled && !projectCode) {
        message.error('请先选择项目编码');
        return;
      }
      
      if (quickInboundEnabled && !projectCode) {
        console.log('快捷入库模式已开启，跳过项目编码验证');
      }
      
      if (!accessoriesItemType) {
        message.error('请选择货品类型');
        return;
      }
      
      // 验证重量必填：上架费按重量档位收取，不填会按最低档收费
      if (!accessoriesItemDimensions.weight) {
        message.error('请填写重量（必须大于0）');
        return;
      }

      // 验证数量必填（>=1）
      if (accessoriesItemQuantity === undefined || Number.isNaN(Number(accessoriesItemQuantity)) || Number(accessoriesItemQuantity) < 1) {
        message.error('请填写数量（至少为1）');
        return;
      }

      setIsCreatingAccessoriesInventoryUnit(true);

      // 合并货品类型和备注作为remarks
      const itemTypeText = accessoriesItemType === 'accessories' ? '配件' :
                          accessoriesItemType === 'materials' ? '包材' :
                          accessoriesItemType === 'labels' ? '标签' : '其他';
      const combinedRemarks = `${itemTypeText}${accessoriesItemRemarks ? ` - ${accessoriesItemRemarks}` : ''}`;

      // 准备请求数据
      const requestData = {
        project_code: projectCode,
        item_type: accessoriesItemType,
        actual_weight: accessoriesItemDimensions.weight,
        remarks: combinedRemarks
      };

      console.log('创建配件库存单元，请求数据:', requestData);

      // 调用API创建库存单元
      const response = await createAccessoriesInventoryUnit(requestData);

      console.log('创建配件库存单元成功:', response);
      // 记录实际发生的非常规操作：配件/包材/标签等非上架货物入库（中文标签）
      setPerformedIrregularOps(prev => new Set(Array.from(prev).concat(getIrregularOperationLabel('accessories_materials_labels_inbound'))));

      // 记录本次重量，供下次"使用上次数据"按钮回填
      saveLastDimensions(ACCESSORIES_ITEM_LAST_DIMENSIONS_KEY, {
        weight: accessoriesItemDimensions.weight,
      });

      message.success(`库存单元创建成功！系统条码：${response.data.inventory_unit.system_barcode}`);

      // 1. 立即更新映射表，添加新创建的库存单元
      const newSystemBarcode = response.data.inventory_unit.system_barcode;
      const newSortingGroupId = response.data.inventory_unit.sorting_group;
      
      if (newSystemBarcode && newSortingGroupId) {
        // 由于后端返回的sorting_group是ID，我们需要构造一个临时的分拣组对象
        // 这里使用已知的分拣组名称，因为我们在创建时使用了包含项目编码的名称格式
        const sortingGroupName = `SYS-非产品库存分拣组-${projectCode}`;
        
        // 使用标准化的小写键值，确保与查找逻辑一致
        const normalizedSystemBarcode = newSystemBarcode.toLowerCase();
        
        // 优先使用后端返回的sorting_group_info中的system_code，如果没有则使用临时编码
        const sortingGroupSystemCode = response.data.inventory_unit.sorting_group_info?.system_code || `SG-${projectCode}-${Date.now()}`;
        
        setInventoryUnitCodeToSortingGroupMap(prevMap => ({
          ...prevMap,
          [normalizedSystemBarcode]: {
            sortingGroup: {
              id: newSortingGroupId,
              name: sortingGroupName,
              system_code: sortingGroupSystemCode,
              group_type: 'non_product'
            },
            inventoryUnit: response.data.inventory_unit,
            isDirectMatch: true,
            matchType: 'inventory_unit_system_code',
            originalSystemBarcode: newSystemBarcode // 保留原始大小写
          }
        }));
        
        console.log(`【调试】立即更新映射表，添加新库存单元: ${newSystemBarcode} (标准化为: ${normalizedSystemBarcode}) -> ${sortingGroupName} (系统编码: ${sortingGroupSystemCode})`);
      }
      
      // 2. 设置扫描值
      setInventoryUnitScanValue(newSystemBarcode);
      
      // 3. 直接添加到分拣数据库，实现空仓位重用
      const sortingGroupName = `SYS-非产品库存分拣组-${projectCode}`;
      const sortingGroupSystemCode = response.data.inventory_unit.sorting_group_info?.system_code || `SG-${projectCode}-${Date.now()}`;
      
      // 使用空仓位重用逻辑分配临时仓位
      const temporaryLocation = addInventoryUnitToDatabase(
        newSystemBarcode,
        sortingGroupName,
        sortingGroupSystemCode,
        combinedRemarks
      );
      
      console.log(`【调试】已分配临时仓位: ${temporaryLocation}，分拣组: ${sortingGroupName}`);
      
      // 4. 自动触发扫描和提交逻辑
      setTimeout(async () => {
        console.log('【调试】自动触发扫描逻辑，扫描值:', newSystemBarcode);
        try {
          // 直接触发扫描按钮点击事件
          const scanButton = document.querySelector('[data-testid="scan-inventory-unit-button"]') as HTMLButtonElement;
          if (scanButton && !scanButton.disabled) {
            scanButton.click();
            console.log('【调试】自动点击扫描按钮成功');
            
            // 5. 延迟更新扫描记录中的数量为按钮前面的数量输入框中的数字
            setTimeout(() => {
              console.log('【调试】开始更新扫描记录数量，目标数量:', accessoriesItemQuantity);
              setScanHistory(prevHistory => {
                if (prevHistory.length > 0) {
                  // 找到最新添加的扫描记录（第一条）并更新数量
                  const updatedHistory = [...prevHistory];
                  if (updatedHistory[0] && updatedHistory[0].inventoryUnitCode === newSystemBarcode) {
                    updatedHistory[0] = {
                      ...updatedHistory[0],
                      quantity: accessoriesItemQuantity
                    };
                    console.log('【调试】成功更新扫描记录数量为:', accessoriesItemQuantity);
                  }
                  return updatedHistory;
                }
                return prevHistory;
              });
              
              // 5. 重置数量输入框为1
              setTimeout(() => {
                setAccessoriesItemQuantity(1);
                console.log('【调试】已重置配件/包材/标签数量输入框为1');
              }, 500); // 延迟0.5秒，确保数量更新完成
            }, 1500); // 延迟1.5秒，确保扫描记录已添加
          } else {
            console.log('【调试】扫描按钮不可用，跳过自动扫描');
          }
          } catch (error: any) {
          console.error('【调试】自动扫描失败:', error);
        }
      }, 1000); // 延迟1秒，确保状态更新完成

      // 5.5 再延时，生成"非上架货品"库存单元标签预览（与原箱BIU同布局与尺寸 4x6）
      setTimeout(() => {
        try {
          const remarkText = (combinedRemarks || currentScanValue || '').toString();
          const source = {
            id: newSystemBarcode,
            // NIU 不拼接"（箱）"，name 使用系统码或备注
            name: newSystemBarcode,
            system_code: newSystemBarcode,
            text: newSystemBarcode,
            // 直接标记为 NIU，适配器会选择 4×6 模板并使用 NIU 文案
            unit_type: 'NIU',
            content_items: []
          } as any;
          const adapter = new InventoryUnitLabelAdapter();
          const data = adapter.transform(source);
          // 上下两行：第一行固定"（非上架货品入库）"，第二行使用备注栏
          data.values.content_items = ['（非上架货品入库）', remarkText].join('\n');
          // 覆盖类型显示为 NIU 文案（确保后端与模板使用完整标题）
          (data.values as any).unit_type_display = 'Non-Inventory Unit（非产品库存单元）';
          // 按数量生成多份标签
          data.values.quantity = Number(accessoriesItemQuantity || 1);
          setInventoryUnitLabelPreviewData(data);
          setInventoryUnitLabelPreviewVisible(true);
          console.log('【标签预览】已生成并打开（非上架货品 NIU，BIU模板 4x6）');
        } catch (e) {
          console.warn('【标签预览】生成失败（非上架货品 NIU）', e);
        }
      }, 2300); // 晚于数量回填（1500ms）

      //（移除重复的BIU预览，NIU仅使用上面的 5.5 预览生成）
      
      // 5. 异步重新构建映射表，确保数据一致性
      setTimeout(() => {
        console.log('【调试】异步重新构建映射表...');
        fetchInventoryConfigAndBuildMap(projectCode);
      }, 2000);
      
      // 清空相关状态
      setAccessoriesItemType('');
      setAccessoriesItemDimensions({
        length: undefined,
        width: undefined,
        height: undefined
      });
      setAccessoriesItemRemarks('');
      
    } catch (error: any) {
      console.error('创建配件库存单元失败:', error);
      message.error(error.response?.data?.detail || '创建库存单元失败，请重试');
    } finally {
      setIsCreatingAccessoriesInventoryUnit(false);
    }
  };
  // 创建异常库存单元
  const handleCreateExceptionInventoryUnit = async () => {
    // 防重复提交检查
    if (isCreatingExceptionInventoryUnit) {
      message.warning('正在创建中，请稍候...');
      return;
    }
    
    try {
      // 验证必要数据（当快捷入库开关打开时跳过项目编码验证）
      if (!quickInboundEnabled && !projectCode) {
        message.error('请先选择项目编码');
        return;
      }
      
      if (quickInboundEnabled && !projectCode) {
        console.log('快捷入库模式已开启，跳过项目编码验证');
      }
      
      // 异常库存入库：不再要求填写尺寸信息
      // 但要求总重量与数量字段必须有值（重量可为0，数量>=1）
      if (exceptionTotalWeightG === undefined || exceptionTotalWeightG === null || Number(exceptionTotalWeightG) <= 0) {
        message.error('请填写总重量（必须大于0）');
        return;
      }
      if (exceptionInventoryQuantity === undefined || Number.isNaN(Number(exceptionInventoryQuantity)) || Number(exceptionInventoryQuantity) < 1) {
        message.error('请填写数量（至少为1）');
        return;
      }
      
      setIsCreatingExceptionInventoryUnit(true);
      
      // 准备请求数据
      const requestData = {
        project_code: projectCode,
        // 尺寸留空，后端已放宽
        actual_length: undefined,
        actual_width: undefined,
        actual_height: undefined,
        // 此处不再在创建阶段更新 actual_weight，整单提交时统一加权
      };
      
      console.log('创建异常库存单元，请求数据:', requestData);
      
      // 调用API创建库存单元
      const response = await createExceptionInventoryUnit(requestData);
      
      console.log('创建异常库存单元成功:', response);
      // 记录实际发生的非常规操作：异常库存入库（中文标签）
      setPerformedIrregularOps(prev => new Set(Array.from(prev).concat(getIrregularOperationLabel('exception_inventory_inbound'))));
      
      message.success(`异常库存单元创建成功！系统条码：${response.data.inventory_unit.system_barcode}`);

      // 累计会话内异常库存总重量（同一入库单多次异常录入时用于整单加权）
      try {
        const w = Number(exceptionTotalWeightG || 0);
        if (!Number.isNaN(w) && w > 0) {
          setExceptionSessionTotalWeightG(prev => (prev || 0) + w);
          console.log('[DEBUG][AIU] 会话累计总重量更新:', (prev => (prev || 0) + w));
        }
      } catch {}
      
      // 1. 立即更新映射表，添加新创建的库存单元
      const newSystemBarcode = response.data.inventory_unit.system_barcode;
      const newSortingGroupId = response.data.inventory_unit.sorting_group;
      
      if (newSystemBarcode && newSortingGroupId) {
        // 由于后端返回的sorting_group是ID，我们需要构造一个临时的分拣组对象
        // 这里使用已知的分拣组名称，因为我们在创建时使用了包含项目编码的名称格式
        const sortingGroupName = `SYS-异常库存分拣组-${projectCode}`;
        
        // 使用标准化的小写键值，确保与查找逻辑一致
        const normalizedSystemBarcode = newSystemBarcode.toLowerCase();
        
        // 优先使用后端返回的sorting_group_info中的system_code，如果没有则使用临时编码
        const sortingGroupSystemCode = response.data.inventory_unit.sorting_group_info?.system_code || `SG-${projectCode}-${Date.now()}`;
        
        setInventoryUnitCodeToSortingGroupMap(prevMap => ({
          ...prevMap,
          [normalizedSystemBarcode]: {
            sortingGroup: {
              id: newSortingGroupId,
              name: sortingGroupName,
              system_code: sortingGroupSystemCode,
              group_type: 'abnormal'
            },
            inventoryUnit: response.data.inventory_unit,
            isDirectMatch: true,
            matchType: 'inventory_unit_system_code',
            originalSystemBarcode: newSystemBarcode // 保留原始大小写
          }
        }));
        
        console.log(`【调试】立即更新映射表，添加新异常库存单元: ${newSystemBarcode} (标准化为: ${normalizedSystemBarcode}) -> ${sortingGroupName} (系统编码: ${sortingGroupSystemCode})`);
      }
      
      // 2. 设置扫描值
      setInventoryUnitScanValue(newSystemBarcode);
      
      // 3. 直接添加到分拣数据库，实现空仓位重用
      const sortingGroupName = `SYS-异常库存分拣组-${projectCode}`;
      const sortingGroupSystemCode = response.data.inventory_unit.sorting_group_info?.system_code || `SG-${projectCode}-${Date.now()}`;
      
      // 使用空仓位重用逻辑分配临时仓位
      const temporaryLocation = addInventoryUnitToDatabase(
        newSystemBarcode,
        sortingGroupName,
        sortingGroupSystemCode,
        '异常库存入库'
      );
      
      console.log(`【调试】已分配临时仓位: ${temporaryLocation}，分拣组: ${sortingGroupName}`);
      
      // 4. 自动触发扫描和提交逻辑
      setTimeout(async () => {
        console.log('【调试】自动触发扫描逻辑，扫描值:', newSystemBarcode);
        try {
          // 直接触发扫描按钮点击事件
          const scanButton = document.querySelector('[data-testid="scan-inventory-unit-button"]') as HTMLButtonElement;
          if (scanButton && !scanButton.disabled) {
            scanButton.click();
            console.log('【调试】自动点击扫描按钮成功');
            
            // 5. 延迟更新扫描记录中的数量为按钮前面的数量输入框中的数字
            setTimeout(() => {
              console.log('【调试】开始更新扫描记录数量，目标数量:', exceptionInventoryQuantity);
              setScanHistory(prevHistory => {
                if (prevHistory.length > 0) {
                  // 找到最新添加的扫描记录（第一条）并更新数量
                  const updatedHistory = [...prevHistory];
                  if (updatedHistory[0] && updatedHistory[0].inventoryUnitCode === newSystemBarcode) {
                    updatedHistory[0] = {
                      ...updatedHistory[0],
                      quantity: exceptionInventoryQuantity
                    };
                    console.log('【调试】成功更新扫描记录数量为:', exceptionInventoryQuantity);
                  }
                  return updatedHistory;
                }
                return prevHistory;
              });
              
              // 5. 重置数量输入框为1
              setTimeout(() => {
                setExceptionInventoryQuantity(1);
                console.log('【调试】已重置异常库存数量输入框为1');
              }, 500); // 延迟0.5秒，确保数量更新完成
            }, 1500); // 延迟1.5秒，确保扫描记录已添加
          } else {
            console.log('【调试】扫描按钮不可用，跳过自动扫描');
          }
        } catch (error) {
          console.error('【调试】自动扫描失败:', error);
        }
      }, 1000); // 延迟1秒，确保状态更新完成
      
      // 6. 异步重新构建映射表，确保数据一致性
      setTimeout(() => {
        console.log('【调试】异步重新构建映射表...');
        fetchInventoryConfigAndBuildMap(projectCode);
      }, 2000);
      
      // 清空相关状态
      setExceptionInventoryDimensions({
        length: undefined,
        width: undefined,
        height: undefined
      });

    } catch (error: any) {
      console.error('创建异常库存单元失败:', error);
      const errorMessage = error.response?.data?.detail || '创建异常库存单元失败';
      message.error(errorMessage);
    } finally {
      setIsCreatingExceptionInventoryUnit(false);
    }
  };

  // 获取项目下所有产品
  const fetchProjectProducts = async (projectCode: string) => {
    try {
      console.log('【接口调试】开始获取项目产品列表, projectCode:', projectCode);
      
      // 分页获取所有产品数据
      let allProducts: any[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        console.log(`【接口调试】正在获取第 ${page} 页产品数据...`);
        const response = await getProductList({ project_code: projectCode, page, page_size: 1000 });
        
        console.log(`【接口调试】第 ${page} 页响应:`, response);
        console.log(`【接口调试】第 ${page} 页产品数量:`, response.data.results?.length || 0);
        console.log(`【接口调试】第 ${page} 页总数:`, response.data.count);
        console.log(`【接口调试】第 ${page} 页是否有下一页:`, !!response.data.next);
        
        if (response.data.results && response.data.results.length > 0) {
          allProducts = allProducts.concat(response.data.results);
          console.log(`【接口调试】累计产品数量:`, allProducts.length);
        }
        
        // 检查是否还有下一页
        hasMore = !!response.data.next && allProducts.length < response.data.count;
        page += 1;
        
        // 防止无限循环
        if (page > 10) {
          console.warn('【警告】分页获取超过10页，停止获取');
          break;
        }
      }
      
      console.log('【接口调试】所有产品数据获取完成，总数量:', allProducts.length);
      
      setProjectProducts(allProducts);
      
      // 详细调试信息：打印所有产品及其分拣条码
      console.log('【详细调试】=== 项目产品及分拣条码详细信息 ===');
      console.log('项目编码:', projectCode);
      console.log('产品总数:', allProducts.length);
      
      if (allProducts.length > 0) {
        allProducts.forEach((product, index) => {
          console.log(`【产品${index + 1}详细信息】`);
          console.log(`  - ID: ${product.id}`);
          console.log(`  - 名称: ${product.name}`);
          console.log(`  - 客户编码: ${product.custom_code}`);
          console.log(`  - 系统编码: ${product.system_code}`);
          console.log(`  - 分拣条码类型: ${typeof product.sorting_codes}`);
          console.log(`  - 分拣条码是否为数组: ${Array.isArray(product.sorting_codes)}`);
          console.log(`  - 分拣条码数量: ${Array.isArray(product.sorting_codes) ? product.sorting_codes.length : 'N/A'}`);
          console.log(`  - 分拣条码详情:`, product.sorting_codes);
          
          // 检查分拣条码的具体内容
          if (Array.isArray(product.sorting_codes) && product.sorting_codes.length > 0) {
            product.sorting_codes.forEach((codeObj, codeIndex) => {
              console.log(`    【分拣条码${codeIndex + 1}】`);
              console.log(`      - 对象类型: ${typeof codeObj}`);
              console.log(`      - 对象内容:`, codeObj);
              if (codeObj && typeof codeObj === 'object') {
                console.log(`      - ID: ${codeObj.id || 'N/A'}`);
                console.log(`      - 条码: ${codeObj.code || 'N/A'}`);
                console.log(`      - 描述: ${codeObj.description || 'N/A'}`);
              }
            });
          } else {
            console.log(`  - 【警告】产品${product.id}缺少分拣条码数据`);
          }
          console.log('  - 其他字段:', {
            value_usd: product.value_usd,
            weight_grams: product.weight_grams,
            length_cm: product.length_cm,
            width_cm: product.width_cm,
            height_cm: product.height_cm,
            has_logistics_package: product.has_logistics_package,
            created_at: product.created_at,
            updated_at: product.updated_at
          });
          console.log('  - 项目关联:', product.projects);
          console.log('  - 创建者:', product.created_by);
          console.log('  ================================');
        });
      } else {
        console.log('【警告】未获取到任何产品数据');
      }
      
      console.log('【接口调试】产品列表获取完成');
      
    } catch (error: any) {
      console.error('【接口调试】获取项目产品列表失败:', error);
      console.error('【接口调试】错误详情:', {
        projectCode,
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
              url: error.config?.url,
              method: error.config?.method
            });
      setProjectProducts([]);
    }
  };

  // 拉取库存配置并构建分拣条码到分拣组映射
  const fetchInventoryConfigAndBuildMap = async (projectCode: string) => {
    console.log('【接口调试】开始获取库存配置, projectCode:', projectCode, 'projectProducts:', projectProducts);
    
    // 总是重新获取最新的产品数据，确保数据同步
    let currentProjectProducts: any[] = [];
    console.log('【调试】重新获取产品数据以确保数据同步');
    try {
      // 分页获取所有产品数据
      let allProducts: any[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        console.log(`【调试】正在获取第 ${page} 页产品数据...`);
        const response = await getProductList({ project_code: projectCode, page, page_size: 1000 });
        
        console.log(`【调试】第 ${page} 页产品数量:`, response.data.results?.length || 0);
        console.log(`【调试】第 ${page} 页总数:`, response.data.count);
        console.log(`【调试】第 ${page} 页是否有下一页:`, !!response.data.next);
        
        if (response.data.results && response.data.results.length > 0) {
          allProducts = allProducts.concat(response.data.results);
          console.log(`【调试】累计产品数量:`, allProducts.length);
        }
        
        // 检查是否还有下一页
        hasMore = !!response.data.next && allProducts.length < response.data.count;
        page += 1;
        
        // 防止无限循环
        if (page > 10) {
          console.warn('【警告】分页获取超过10页，停止获取');
          break;
        }
      }
      
      currentProjectProducts = allProducts;
      console.log('【调试】获取到的最新产品数据:', currentProjectProducts);
      console.log('【调试】最终产品总数:', currentProjectProducts.length);
      
      // 详细验证产品数据完整性
      console.log('【详细调试】=== 产品数据完整性验证 ===');
      console.log('项目编码:', projectCode);
      console.log('产品总数:', currentProjectProducts.length);
      
      currentProjectProducts.forEach((product, index) => {
        console.log(`【产品${index + 1}数据验证】`);
        console.log(`  - ID: ${product.id}`);
        console.log(`  - 名称: ${product.name}`);
        console.log(`  - 客户编码: ${product.custom_code}`);
        console.log(`  - 系统编码: ${product.system_code}`);
        console.log(`  - 分拣条码类型: ${typeof product.sorting_codes}`);
        console.log(`  - 分拣条码是否为数组: ${Array.isArray(product.sorting_codes)}`);
        console.log(`  - 分拣条码数量: ${Array.isArray(product.sorting_codes) ? product.sorting_codes.length : 'N/A'}`);
        console.log(`  - 分拣条码详情:`, product.sorting_codes);
        
        // 检查分拣条码的具体内容
        if (Array.isArray(product.sorting_codes) && product.sorting_codes.length > 0) {
          product.sorting_codes.forEach((codeObj, codeIndex) => {
            console.log(`    【分拣条码${codeIndex + 1}验证】`);
            console.log(`      - 对象类型: ${typeof codeObj}`);
            console.log(`      - 对象内容:`, codeObj);
            if (codeObj && typeof codeObj === 'object') {
              console.log(`      - ID: ${codeObj.id || 'N/A'}`);
              console.log(`      - 条码: ${codeObj.code || 'N/A'}`);
              console.log(`      - 描述: ${codeObj.description || 'N/A'}`);
            } else {
              console.warn(`      - 【警告】分拣条码对象格式不正确:`, codeObj);
            }
          });
        } else {
          console.warn(`  - 【警告】产品${product.id}缺少分拣条码数据或格式不正确:`, product.sorting_codes);
        }
        console.log('  ================================');
      });
      
      // 更新产品数据状态，确保后续使用
      setProjectProducts(currentProjectProducts);
    } catch (error: any) {
      console.error('【调试】重新获取产品数据失败:', error);
      // 如果获取失败，使用现有的产品数据作为备选
      currentProjectProducts = projectProducts;
    }
    
    // 即使没有产品数据，也继续构建映射表（主要依赖库存单元系统编码映射）
    console.log('【调试】使用产品数据构建映射表，产品数量:', currentProjectProducts.length);
    
    try {
      let page = 1;
      let configs: any[] = [];
      let hasMore = true;
      
      console.log('【接口调试】开始分页获取库存配置列表...');
      // 拉取所有分页
      while (hasMore) {
        console.log(`【接口调试】正在获取第 ${page} 页库存配置...`);
        const listResp = await getInventoryConfigList({ project_code: projectCode, page, page_size: 50 });
        console.log(`【接口调试】第 ${page} 页响应:`, listResp);
        
        const results = listResp.data.results || [];
        configs = configs.concat(results);
        hasMore = !!listResp.data.next;
        page += 1;
        
        console.log(`【接口调试】第 ${page - 1} 页获取了 ${results.length} 个配置，总计: ${configs.length}`);
      }
      console.log('【接口调试】获取到的库存配置列表:', configs);
      
      // 精确匹配
      const config = configs.find((cfg: any) => cfg.project_code === projectCode);
      if (!config) {
        console.log('【接口调试】未找到精确匹配的库存配置');
        setInventoryConfig(null);
        setBarcodeToSortingGroupMap({});
        setInventoryUnitCodeToSortingGroupMap({});
        return;
      }
      console.log('【接口调试】找到匹配的库存配置:', config);
      
      const configId = config.id;
      console.log('【接口调试】开始获取库存配置详情, configId:', configId);
      const detailResp = await getInventoryConfigDetail(configId);
      console.log('【接口调试】库存配置详情响应:', detailResp);
      
      setInventoryConfig(detailResp.data);
      // 调试输出：打印库存配置详情
      console.log('【调试】选定项目', projectCode, '的库存配置详情:', detailResp.data);
      console.log('【调试】projectProducts:', currentProjectProducts);
      console.log('【调试】库存配置详情.sorting_groups:', detailResp.data.sorting_groups);
      
      // 详细打印每个分拣组和库存单元信息
      detailResp.data.sorting_groups.forEach((group: any, gi: number) => {
        console.log(`【调试】分拣组${gi}:`, group);
        console.log(`【调试】分拣组${gi}的库存单元数量:`, group.inventory_units?.length || 0);
        group.inventory_units.forEach((unit: any, ui: number) => {
          console.log(`【调试】分拣组${gi}的库存单元${ui}:`, unit);
          console.log(`【调试】库存单元${ui}的系统条码:`, unit.system_barcode);
          if (unit.content && unit.content.items) {
            console.log(`【调试】分拣组${gi}的库存单元${ui}的内容项:`, unit.content.items);
            unit.content.items.forEach((item: any, ii: number) => {
              console.log(`【调试】内容项${ii}:`, item);
              const product = currentProjectProducts.find((p: any) => p.id === item.product);
              console.log(`【调试】内容项${ii}对应的产品:`, product);
              if (product) {
                console.log(`【调试】产品${product.id}的分拣条码:`, product.sorting_codes);
              }
            });
          }
        });
      });
      
      // 3. 构建库存单元系统编码到分拣组映射（第一优先级）
      const inventoryUnitMap: {[systemCode: string]: any} = {};
      console.log('【调试】开始构建库存单元系统编码映射...');
      detailResp.data.sorting_groups.forEach((group: any) => {
        console.log(`【调试】处理分拣组: ${group.name}`);
        group.inventory_units.forEach((unit: any) => {
          if (unit.system_barcode) {
            // 将系统条码转换为小写作为键值，确保大小写不敏感
            const normalizedSystemBarcode = unit.system_barcode.toLowerCase();
            inventoryUnitMap[normalizedSystemBarcode] = {
              sortingGroup: group,
              inventoryUnit: unit,
              isDirectMatch: true,
              matchType: 'inventory_unit_system_code',
              originalSystemBarcode: unit.system_barcode // 保留原始大小写
            };
            console.log(`【调试】添加库存单元系统编码映射: ${unit.system_barcode} (标准化为: ${normalizedSystemBarcode}) -> ${group.name}`);
          } else {
            console.log(`【调试】库存单元缺少系统条码:`, unit);
          }
        });
      });
      setInventoryUnitCodeToSortingGroupMap(inventoryUnitMap);
      console.log('【调试】库存单元系统编码到分拣组映射:', inventoryUnitMap);
      console.log('【调试】库存单元系统编码映射表大小:', Object.keys(inventoryUnitMap).length);
      
      // 4. 构建产品分拣条码到分拣组映射（第二优先级）
      const productBarcodeMap: {[barcode: string]: any} = {};
      console.log('【调试】开始构建产品分拣条码映射...');
      detailResp.data.sorting_groups.forEach((group: any) => {
        console.log(`【调试】处理分拣组: ${group.name}`);
        group.inventory_units.forEach((unit: any) => {
          // 产品分拣条码仅用于散货库存单元(LIU)匹配，避免误命中BIU/PIU
          const unitTypeRaw = typeof unit?.unit_type === 'string' ? unit.unit_type : '';
          const systemBarcodeRaw = typeof unit?.system_barcode === 'string' ? unit.system_barcode : '';
          const isLooseUnit = unitTypeRaw.toUpperCase() === 'LIU' || systemBarcodeRaw.toUpperCase().startsWith('LIU-');
          if (!isLooseUnit) {
            console.log(`【调试】跳过非LIU库存单元的分拣条码映射: ${unit.system_barcode || '-'} (${unit.unit_type || '-'})`);
            return;
          }
          if (unit.content && unit.content.items) {
            unit.content.items.forEach((item: any) => {
              const product = currentProjectProducts.find((p: any) => p.id === item.product);
              if (product && product.sorting_codes) {
                product.sorting_codes.forEach((codeObj: any) => {
                  // 将分拣条码转换为小写作为键值，确保大小写不敏感
                  const normalizedCode = codeObj.code.toLowerCase();
                  productBarcodeMap[normalizedCode] = {
                    sortingGroup: group,
                    inventoryUnit: unit,
                    product: product,
                    isDirectMatch: false,
                    matchType: 'product_sorting_code',
                    originalCode: codeObj.code // 保留原始大小写
                  };
                  console.log(`【调试】添加产品分拣条码映射: ${codeObj.code} (标准化为: ${normalizedCode}) -> ${product.name} -> ${group.name}`);
                });
              } else {
                console.log(`【调试】产品${item.product}未找到或缺少分拣条码:`, product);
              }
            });
          } else {
            console.log(`【调试】库存单元${unit.system_barcode}缺少内容项:`, unit);
          }
        });
      });
      setBarcodeToSortingGroupMap(productBarcodeMap);
      console.log('【调试】产品分拣条码到分拣组映射:', productBarcodeMap);
      console.log('【调试】产品分拣条码映射表大小:', Object.keys(productBarcodeMap).length);
      
      console.log('【调试】映射表构建完成');
      console.log('【调试】最终映射表状态:');
      console.log('  - 库存单元系统编码映射表大小:', Object.keys(inventoryUnitMap).length);
      console.log('  - 产品分拣条码映射表大小:', Object.keys(productBarcodeMap).length);
      console.log('  - 项目产品数量:', currentProjectProducts.length);
    } catch (error: any) {
      console.error('【错误】构建映射表失败:', error);
      setInventoryConfig(null);
      setBarcodeToSortingGroupMap({});
      setInventoryUnitCodeToSortingGroupMap({});
    }
  };

  // 监听项目变化，先拉取产品列表
  useEffect(() => {
    if (projectCode) {
      fetchProjectProducts(projectCode);
    }
  }, [projectCode]);

  // 监听项目变化，直接拉取库存配置（不依赖产品数据）
  useEffect(() => {
    if (projectCode) {
      fetchInventoryConfigAndBuildMap(projectCode);
    }
  }, [projectCode]);

  // 组件加载时获取项目列表
  useEffect(() => {
    console.log('=== 组件初始化调试信息 ===');
    console.log('当前用户信息:', {
      // 这里可以添加用户信息，如果有的话
    });
    console.log('开始获取项目列表...');
    fetchProjects();
    console.log('开始获取仓库列表...');
    fetchWarehouses();
  }, []);

  // ====== 入库拆分（前端编辑）状态与工具，不影响看板localStorage ======
  const [splitModalVisible, setSplitModalVisible] = useState(false);
  const [splitSubmitting, setSplitSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState<'create' | 'add' | null>(null);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [splitDraft, setSplitDraft] = useState<Array<{ inventoryUnitCode: string; quantity: number; splits: Array<{ inbound_record_id?: number; qty?: number }> }>>([]);
  const [inboundRecordOptions, setInboundRecordOptions] = useState<Array<{ value: number; label: string }>>([]);

  // 预载可选入库记录（仅作为选择器选项，不影响看板localStorage）
  const preloadInboundRecordOptions = async () => {
    try {
      const page = 1;
      const pageSize = 50;
      const filters: any = {};
      // 优先从 pendingPayload/group_system_code 推导项目编码，再映射为项目ID
      let ctxGroupSystemCode: string | undefined = undefined;
      try { ctxGroupSystemCode = String(pendingPayload?.group_system_code || createContainerForm.groupSystemCode || addToContainerForm.groupInfo.groupSystemCode || '').trim(); } catch {}
      const ctxProjectCode = ctxGroupSystemCode ? extractProjectCodeFromGroupSystemCode(ctxGroupSystemCode) : (projectCode || '');
      if (ctxProjectCode) {
        try {
          const prj = (projects || []).find((p:any)=> String(p?.project_code||'') === String(ctxProjectCode));
          if (prj && Number.isFinite(Number(prj.id))) filters.project = Number(prj.id);
        } catch {}
      }
      // 将仓库编码映射为仓库ID，后端列表过滤的参数为 warehouse(=id)
      if (warehouseCode) {
        try {
          const wh = (warehouses || []).find((w:any)=> String(w?.code||'') === String(warehouseCode));
          if (wh && Number.isFinite(Number(wh.id))) filters.warehouse = Number(wh.id);
        } catch {}
      }
      const resp = await getInboundRecordListPaginated(page, pageSize, filters);
      const rows = Array.isArray(resp?.results) ? resp.results : [];
      const opts = rows.map((r: any) => ({
        value: Number(r?.id),
        label: `${r?.system_code || r?.id}（${r?.created_at || ''}）`
      }));
      setInboundRecordOptions(opts);
    } catch {
      setInboundRecordOptions([]);
    }
  };

  const buildDraftFromAuto = (auto: any, originalItems: Array<{ inventory_unit_code: string; quantity: number }>) => {
    const byCode = new Map<string, any>();
    (auto?.mapped || []).forEach((m: any) => byCode.set(String(m.inventory_unit_code), m));
    const draft = (originalItems || []).map(it => {
      const code = String(it.inventory_unit_code);
      const m = byCode.get(code) || { splits: [] };
      const sum = (m.splits || []).reduce((s: number, sp: any) => s + Number(sp?.qty || 0), 0);
      const remain = Math.max(Number(it.quantity || 0) - sum, 0);
      const splits = [...(m.splits || [])];
      if (remain > 0) {
        splits.push({ inbound_record_id: undefined, qty: remain });
      }
      return { inventoryUnitCode: code, quantity: Number(it.quantity || 0), splits };
    });
    return draft;
  };

  const changeSplitCell = (rowIdx: number, splitIdx: number, field: 'inbound_record_id' | 'qty', value: any) => {
    setSplitDraft(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx] };
      const splits = [...row.splits];
      const sp = { ...splits[splitIdx] };
      sp[field] = field === 'qty' ? (typeof value === 'number' ? value : Number(value || 0)) : value;
      splits[splitIdx] = sp;
      row.splits = splits;
      next[rowIdx] = row;
      return next;
    });
  };

  const addSplitRow = (rowIdx: number) => {
    setSplitDraft(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx] };
      row.splits = [...row.splits, { inbound_record_id: undefined, qty: 0 }];
      next[rowIdx] = row;
      return next;
    });
  };

  const removeSplitRow = (rowIdx: number, splitIdx: number) => {
    setSplitDraft(prev => {
      const next = [...prev];
      const row = { ...next[rowIdx] };
      const splits = [...row.splits];
      splits.splice(splitIdx, 1);
      row.splits = splits;
      next[rowIdx] = row;
      return next;
    });
  };

  const submitWithSplits = async () => {
    try {
      setSplitSubmitting(true);
      // 合成 payload
      const payload = { ...(pendingPayload || {}) };
      const draftMap = new Map(splitDraft.map(d => [d.inventoryUnitCode, d]));
      payload.items = (payload.items || []).map((it: any) => {
        const d = draftMap.get(String(it.inventory_unit_code));
        if (!d) return it;
        return {
          ...it,
          splits: (d.splits || [])
            .filter((sp: any) => Number(sp.qty) > 0 && Number(sp.inbound_record_id) > 0)
            .map((sp: any) => ({ inbound_record_id: Number(sp.inbound_record_id), qty: Number(sp.qty) }))
        };
      });
      // 简单校验：sum==quantity
      let valid = true;
      payload.items.forEach((it: any) => {
        const sum = (it.splits || []).reduce((s: number, sp: any) => s + Number(sp.qty || 0), 0);
        if (Number(it.quantity || 0) > 0 && sum !== Number(it.quantity || 0)) valid = false;
      });
      if (!valid) {
        message.error('拆分校验失败：存在"拆分之和 ≠ 行总量"的记录');
        setSplitSubmitting(false);
        return;
      }

      if (pendingAction === 'create') {
        const response = await createContainerFromSortingBoard(payload);
        message.success('创建容器成功');
        // 简化：刷新容器列表并关闭弹窗
        try { sessionStorage.setItem('refresh_storage_containers', '1'); } catch {}
        fetchRecentContainers();
        setCreateContainerModalOpen(false);
      } else if (pendingAction === 'add') {
        const response = await addToExistingContainer(payload);
        message.success('添加到容器成功');
        handleAddToContainerCancel();
      }
      setSplitModalVisible(false);
      setPendingAction(null);
      setPendingPayload(null);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '提交失败');
    } finally {
      setSplitSubmitting(false);
    }
  };
  // ====== 入库拆分（前端编辑）结束 ======

  // 获取状态显示文本
  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      submitted: '已提交',
      receiving: '收货中',
      completed: '已完成收货',
      cancelled: '取消',
      exception: '异常',
    };
    return statusMap[status] || status;
  };

  // 获取入库记录列表
  const fetchInboundRecords = async (params: any = {}) => {
    setLoadingInboundRecords(true);
    try {
      // 基础参数
      const baseParams: any = {
        page: 1,
        page_size: 10,
        ordering: '-created_at',
        ...params
      };

      // 员工账号：仅查询分配仓库
      try {
        const staffMode = isWarehouseStaff ? isWarehouseStaff() : false;
        const assignedIds = (permissions?.assigned_warehouse_ids || []) as number[];
        if (staffMode) {
          if (Array.isArray(assignedIds) && assignedIds.length > 0) {
            // 优先尝试后端支持 __in，多值用逗号分隔
            baseParams['warehouse__in'] = assignedIds.join(',');
          } else {
            // 没有被分配任何仓库——返回空集合的等价行为：传一个永不匹配的值
            baseParams['warehouse'] = -1;
          }
        }
      } catch {}

      const response = await getInboundRecordList(baseParams);
      
      setInboundRecords(response.results);
      setInboundRecordsPagination({
        ...inboundRecordsPagination,
        total: response.count,
        current: 1,
        pageSize: 10
      });
    } catch (error) {
      console.error('获取入库记录失败:', error);
      message.error('获取入库记录失败');
    } finally {
      setLoadingInboundRecords(false);
    }
  };

  // 获取最近仓储容器（最近10条）
  const buildContainerContentsSummary = (rows: any[]) => {
    const qtyByCode = new Map<string, number>();
    for (const row of (Array.isArray(rows) ? rows : [])) {
      const code = String(row?.inventory_unit_code || '').trim().toUpperCase();
      const qty = Number(row?.quantity || 0);
      if (!code || !Number.isFinite(qty) || qty <= 0) continue;
      qtyByCode.set(code, (qtyByCode.get(code) || 0) + qty);
    }
    const kinds = qtyByCode.size;
    const totalQty = Array.from(qtyByCode.values()).reduce((acc, n) => acc + n, 0);
    if (kinds <= 0 || totalQty <= 0) {
      return {
        content_kind: '',
        content_kind_display: '',
        contents_kinds: 0,
        contents_total_qty: 0,
      };
    }
    const kind = kinds <= 1 ? 'single' : 'mixed';
    return {
      content_kind: kind,
      content_kind_display: kind === 'single' ? '单装' : '混装',
      contents_kinds: kinds,
      contents_total_qty: totalQty,
    };
  };

  const fetchRecentContainers = async () => {
    setLoadingRecentContainers(true);
    try {
      const baseParams: any = { page: 1, page_size: 10, ordering: '-created_at' };
      try {
        const staffMode = isWarehouseStaff ? isWarehouseStaff() : false;
        const assignedIds = (permissions?.assigned_warehouse_ids || []) as number[];
        if (staffMode) {
          if (Array.isArray(assignedIds) && assignedIds.length > 0) {
            baseParams['warehouse__in'] = assignedIds.join(',');
          } else {
            baseParams['warehouse'] = -1;
          }
        }
      } catch {}
      const resp = await fetchStorageContainers(baseParams as any);
      const rows = Array.isArray((resp as any)?.data?.results) ? (resp as any).data.results : [];
      if (rows.length <= 0) {
        setRecentContainers([]);
        return;
      }
      const settled = await Promise.allSettled(
        rows.map(async (container: any) => {
          const detailResp = await fetchStorageContainerDetailWithContents(container.id, { c_page_size: 200 } as any);
          const detailRows = Array.isArray(detailResp?.data?.contents?.results) ? detailResp.data.contents.results : [];
          return {
            id: container.id,
            ...buildContainerContentsSummary(detailRows),
          };
        }),
      );
      const summaryById = new Map<number, any>();
      settled.forEach((it) => {
        if (it.status !== 'fulfilled' || !it.value) return;
        summaryById.set(Number(it.value.id), it.value);
      });
      const mergedRows = rows.map((container: any) => {
        const summary = summaryById.get(Number(container.id));
        if (!summary) return container;
        return { ...container, ...summary };
      });
      setRecentContainers(mergedRows);
    } catch (e) {
      console.warn('获取最近仓储容器失败', e);
    } finally {
      setLoadingRecentContainers(false);
    }
  };

  useEffect(() => {
    fetchInboundRecords();
    fetchRecentContainers();
  }, []);
  // 页面重新获得焦点时，如存在刷新标记则自动刷新"最近15条记录"
  useEffect(() => {
    const onFocus = () => {
      try {
        const need = sessionStorage.getItem('refresh_inbound_records') === '1';
        if (need) {
          fetchInboundRecords();
          sessionStorage.removeItem('refresh_inbound_records');
        }
        const needContainers = sessionStorage.getItem('refresh_storage_containers') === '1';
        if (needContainers) {
          fetchRecentContainers();
          sessionStorage.removeItem('refresh_storage_containers');
        }
      } catch {}
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  // 最近入库记录 - 删除（作废）
  const handleVoid = (record: any) => {
    Modal.confirm({
      title: '确定要作废此入库记录吗？',
      content: `入库单号：${record.system_code}`,
      okText: '作废',
      okButtonProps: { danger: true, loading: voidConfirming },
      cancelText: '取消',
      onOk: async () => {
        try {
          setVoidConfirming(true);
          await voidInboundRecord(record.id, { reason: '前端操作作废' });
          message.success('作废成功');
          // 本地看板回滚
          try {
            const rawBefore = localStorage.getItem('inbound_scan_board_v1') || '';
            const idxBefore = localStorage.getItem('inbound_scan_board_record_index_global_v1') || '';
            console.log('[LS-Debug][Void][Before] boardLen=', rawBefore.length, ' indexLen=', idxBefore.length);
          } catch {}
          try {
            rollbackSortingBoardForRecord({ recordId: Number(record.id) });
            console.log('[SortingBoardSync] 本地看板已回滚作废记录增量', { recordId: record.id });
          } catch (e) {
            console.warn('[SortingBoardSync] 作废后回滚失败（忽略）', e);
          }
          try {
            const rawAfter = localStorage.getItem('inbound_scan_board_v1') || '';
            const idxAfter = localStorage.getItem('inbound_scan_board_record_index_global_v1') || '';
            console.log('[LS-Debug][Void][After] boardLen=', rawAfter.length, ' indexLen=', idxAfter.length);
          } catch {}
          // 刷新"最近入库记录"
          fetchInboundRecords();
        } catch (e: any) {
          message.error(e?.response?.data?.detail || '作废失败');
        } finally {
          setVoidConfirming(false);
        }
      }
    });
  };

  // 最近入库记录 - 修改（差额补正）
  const handleModify = (record: any) => {
    setCurrentRecord(record);
    try {
      const quantities: Record<string, number> = {};
      (record.items || []).forEach((it: any) => {
        quantities[String(it.inventory_unit)] = Number(it.quantity || 0);
      });
      modifyForm.setFieldsValue({ quantities, remarks: '' });
    } catch {
      modifyForm.setFieldsValue({ quantities: {}, remarks: '' });
    }
    setModifyVisible(true);
  };

  const submitModify = async () => {
    try {
      const values = await modifyForm.validateFields();
      setModifySubmitting(true);
      const quantities = values.quantities || {};
      const items = (currentRecord?.items || []).map((it: any) => ({
        inventory_unit: Number(it.inventory_unit),
        new_quantity: Number(quantities[String(it.inventory_unit)] ?? it.quantity ?? 0),
        remarks: values.remarks || ''
      }));
      await modifyInboundRecord(currentRecord.id, { items, reason: values.remarks || '' });
      message.success('修改成功');
      setModifyVisible(false);
      // 本地看板同步差量
      try {
        const rawBefore = localStorage.getItem('inbound_scan_board_v1') || '';
        const idxBefore = localStorage.getItem('inbound_scan_board_record_index_global_v1') || '';
        console.log('[LS-Debug][Modify][Before] boardLen=', rawBefore.length, ' indexLen=', idxBefore.length);
      } catch {}
      try {
        const newTotalsByUnit: Record<string, number> = {};
        (currentRecord?.items || []).forEach((it: any) => {
          const unitCode = it?.inventory_unit_detail?.system_barcode || String(it.inventory_unit);
          const newQty = Number(quantities[String(it.inventory_unit)] ?? it.quantity ?? 0);
          newTotalsByUnit[unitCode] = newQty;
        });
        updateSortingBoardForModify({
          recordId: Number(currentRecord.id),
          newTotalsByUnit
        });
        console.log('[SortingBoardSync] 本地看板已同步修改差量', { recordId: currentRecord.id });
      } catch (e) {
        console.warn('[SortingBoardSync] 修改后同步失败（忽略）', e);
      }
      try {
        const rawAfter = localStorage.getItem('inbound_scan_board_v1') || '';
        const idxAfter = localStorage.getItem('inbound_scan_board_record_index_global_v1') || '';
        console.log('[LS-Debug][Modify][After] boardLen=', rawAfter.length, ' indexLen=', idxAfter.length);
      } catch {}
      // 刷新"最近入库记录"
      fetchInboundRecords();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '修改失败');
    } finally {
      setModifySubmitting(false);
    }
  };
  // 打开批量标签预览
  const openLabelBatchPreview = async (record: InboundRecord) => {
    try {
      const items = (record.items || []) as any[];
      if (!items || items.length === 0) {
        message.warning('该入库记录没有库存单元');
        return;
      }
      // 初始化列表（占位，先选中全部）
      const initial = items.map((it: any) => ({
        unitId: Number(it.inventory_unit),
        systemBarcode: it?.inventory_unit_detail?.system_barcode || String(it.inventory_unit),
        unitType: it?.inventory_unit_detail?.unit_type || '',
        nameResolved: '',
        contentItems: [],
        labelQuantity: Math.max(Number(it.quantity) || 1, 1),
        selected: true,
        loading: true as boolean
      }));
      setLabelBatchItems(initial as any);
      setLabelBatchVisible(true);

      setLabelBatchLoading(true);
      const adapter = new InventoryUnitLabelAdapter();

      const enriched = await Promise.all(initial.map(async (row) => {
        try {
          // 获取详情以构造内容与名称（复用当前库存页逻辑）
          let resolvedName = '' as string;
          let contentItems: Array<{ product_name: string; quantity: number }> = [];
          let unitRemarks: string | null | undefined;
          let sortingGroupName: string | undefined;
          const unitType = (row.unitType || '') as string;
          try {
            const detail = await getInventoryUnitDetail(row.unitId);
            const d = (detail as any)?.data || detail;
            unitRemarks = d?.remarks;
            sortingGroupName = d?.sorting_group_info?.name;
            // 内容项
            const citems = d?.content?.items || d?.content_items || [];
            if (Array.isArray(citems) && (unitType === 'BIU' || unitType === 'PIU' || unitType === 'NIU')) {
              contentItems = citems.map((ci: any) => ({
                product_name: ci?.product_name || ci?.product?.name || '-',
                quantity: Number(ci?.quantity || 0)
              }));
            }
            // 名称解析
            if (unitType === 'AIU') {
              resolvedName = 'Abnormal Inventory Unit';
            } else if (unitType === 'LIU') {
              // 本地字段优先
              resolvedName = d?.name || d?.label_name || '';
              if (!resolvedName && Array.isArray(citems) && citems.length > 0) {
                const it0 = citems[0];
                resolvedName = it0?.product_name || it0?.product?.name || '';
              }
              if (!resolvedName) resolvedName = '';
            } else {
              resolvedName = row.systemBarcode || '';
            }
          } catch {
            // 详情失败兜底：名称与内容空
            resolvedName = (unitType === 'AIU') ? 'Abnormal Inventory Unit' : (unitType === 'LIU' ? '' : (row.systemBarcode || ''));
            contentItems = [];
          }

          const dataForLabel = adapter.transform({
            id: String(row.unitId),
            name: resolvedName,
            system_code: row.systemBarcode,
            text: row.systemBarcode,
            unit_type: unitType as any,
            content_items: contentItems,
            remarks: unitRemarks,
            sorting_group_name: sortingGroupName
          } as any);
          // 按入库数量生成多页标签
          dataForLabel.values.quantity = row.labelQuantity;

          // 生成预览
          let previewUrl: string | undefined;
          let isPDF: boolean | undefined;
          try {
            const preview = await inventoryLabelService.generatePreview(dataForLabel);
            previewUrl = preview.url;
            isPDF = !!preview.isPDF;
          } catch (e) {
            return { ...row, nameResolved: resolvedName, contentItems, labelData: dataForLabel, loading: false, error: '预览生成失败' };
          }

          return { ...row, nameResolved: resolvedName, contentItems, labelData: dataForLabel, previewUrl, isPDF, loading: false };
        } catch (e: any) {
          return { ...row, loading: false, error: e?.message || '生成失败' };
        }
      }));

      setLabelBatchItems(enriched as any);
    } finally {
      setLabelBatchLoading(false);
    }
  };

  const closeLabelBatchPreview = () => {
    try {
      // 释放URL资源
      (labelBatchItems || []).forEach(it => {
        if (it.previewUrl && it.previewUrl.startsWith('blob:')) {
          try { URL.revokeObjectURL(it.previewUrl); } catch {}
        }
      });
    } catch {}
    removeLabelPrintFrame();
    setLabelBatchVisible(false);
    setLabelBatchItems([]);
  };

  const toggleSelectAllLabels = (checked: boolean) => {
    setLabelBatchItems(prev => prev.map(it => ({ ...it, selected: checked })));
  };

  const toggleSelectOneLabel = (unitId: number, checked: boolean) => {
    setLabelBatchItems(prev => prev.map(it => it.unitId === unitId ? ({ ...it, selected: checked }) : it));
  };

  const downloadSelectedPDF = async () => {
    try {
      const selected = labelBatchItems.filter(it => it.selected && it.labelData);
      if (selected.length === 0) {
        message.info('请先选择需要下载的标签');
        return;
      }
      const hide = message.loading('正在生成PDF...', 0);
      for (const it of selected) {
        try {
          const blob = await inventoryLabelService.generatePDF(it.labelData);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${it.systemBarcode || 'label'}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } catch {}
      }
      hide();
      message.success('PDF已开始下载');
    } catch {
      message.error('下载失败');
    }
  };

  const printSelectedLabels = async () => {
    const selected = labelBatchItems.filter(it => it.selected);
    if (selected.length === 0) {
      message.info('请先选择需要打印的标签');
      return;
    }
    const printable = selected.filter(it => it.previewUrl && it.isPDF);
    const failed = selected.filter(it => !(it.previewUrl && it.isPDF));
    if (failed.length > 0) {
      message.warning(`以下标签预览失败，不会打印：${failed.map(it => it.systemBarcode).join('、')}`);
    }
    if (printable.length === 0) return;

    // 同尺寸的标签排在一起：散货、异常是小标签，其他是 4×6 标签
    const isSmallLabel = (it: typeof printable[number]) => it.labelData?.templateId === 'default-unit-label';
    const firstIsSmall = isSmallLabel(printable[0]);
    const ordered = [
      ...printable.filter(it => isSmallLabel(it) === firstIsSmall),
      ...printable.filter(it => isSmallLabel(it) !== firstIsSmall)
    ];
    const totalLabels = ordered.reduce((sum, it) => sum + it.labelQuantity, 0);

    const notices: string[] = [];
    if (ordered.some(it => isSmallLabel(it) !== firstIsSmall)) {
      notices.push('本次包含两种尺寸的标签（2.25×1.25 英寸和 4×6 英寸），同尺寸的已排在一起。如果打印机只装了一种纸张，请分两次勾选打印。');
    }
    if (totalLabels > LABEL_BATCH_PRINT_CONFIRM_THRESHOLD) {
      notices.push(`本次将打印 ${totalLabels} 张标签。`);
    }
    if (notices.length > 0) {
      const confirmed = await new Promise<boolean>(resolve => {
        Modal.confirm({
          title: '确认打印',
          content: <div className="label-batch-print-confirm">{notices.map(text => <div key={text}>{text}</div>)}</div>,
          okText: '继续打印',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      if (!confirmed) return;
    }

    setLabelBatchPrinting(true);
    try {
      // 把选中单元已生成的 PDF 合并成一个多页文件，只弹出一次打印窗口
      const merged = await PDFDocument.create();
      for (const it of ordered) {
        const response = await fetch(it.previewUrl as string);
        const source = await PDFDocument.load(await response.arrayBuffer());
        const pages = await merged.copyPages(source, source.getPageIndices());
        pages.forEach(page => merged.addPage(page));
      }
      const url = URL.createObjectURL(new Blob([await merged.save()], { type: 'application/pdf' }));

      removeLabelPrintFrame();
      const iframe = document.createElement('iframe');
      iframe.className = 'label-batch-print-frame';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      iframe.onload = () => {
        // 稍等 PDF 查看器准备好再打印，否则可能打印出空白页
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          } catch {
            message.error('打印失败，请尝试下载PDF后打印');
          }
        }, 300);
      };
      iframe.src = url;
      labelPrintFrameRef.current = { iframe, url };
      document.body.appendChild(iframe);
    } catch (error) {
      console.error('打印标签失败:', error);
      message.error('打印失败，请尝试下载PDF后打印');
    } finally {
      setLabelBatchPrinting(false);
    }
  };
  return (
    <div style={{ padding: '24px', margin: 0, maxWidth: '100%', fontSize: 16 }}>
      {/* 入库层创建状态 + 调试面板：均默认隐藏，需要时手动打开；后台轮询/成功失败提示与面板显隐无关 */}
      <div style={{ marginBottom: '12px', textAlign: 'right' }}>
        <Space>
          <Button size="small" onClick={() => setLayerStatusVisible(v => !v)}>
            {layerStatusVisible ? '隐藏入库层状态' : '显示入库层状态'}
          </Button>
          <Button size="small" onClick={() => setDebugPanelVisible(v => !v)}>
            {debugPanelVisible ? '隐藏调试信息' : '显示调试信息'}
          </Button>
        </Space>
      </div>
      {layerStatusVisible && (
        <div style={{ marginBottom: 8 }}>
          <Alert
            type={layerPollInProgress ? 'info' : 'warning'}
            message={layerStatusText || '暂无入库层状态信息'}
            action={
              <Space>
                <Button size="small" onClick={() => setLayerStatusVisible(false)}>隐藏</Button>
              </Space>
            }
            showIcon
          />
        </div>
      )}
      {debugPanelVisible && (
      <div style={{ marginBottom: '12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: 12 }}>
        <div style={{ fontWeight: 600, color: '#ad6800', marginBottom: 6 }}>调试 - 分拣看板快照</div>
        <div style={{ fontSize: 12, color: '#874d00' }}>
          <div>记录数: {debugBoardSnapshot.totalRecords} | 下一临时仓位号: {debugBoardSnapshot.nextLocationNumber} | 可重用仓位数: {debugBoardSnapshot.reusableLocationPoolCount}</div>
          {debugBoardSnapshot.reusableLocationPoolPreview && debugBoardSnapshot.reusableLocationPoolPreview.length > 0 && (
            <div>可重用仓位预览: {debugBoardSnapshot.reusableLocationPoolPreview.join(', ')}</div>
          )}
          <div style={{ marginTop: 6 }}>
            {Array.isArray(debugBoardSnapshot.locations) && debugBoardSnapshot.locations.length > 0 ? (
              debugBoardSnapshot.locations.map((loc: any) => (
                <div key={`loc-${loc.temporaryLocation}`} style={{ marginBottom: 6 }}>
                  <Tag color="orange">临时仓位 {loc.temporaryLocation}</Tag>
                  <div style={{ paddingLeft: 8 }}>
                    {loc.items.map((it: any, idx: number) => (
                      <div key={`loc-${loc.temporaryLocation}-item-${idx}`} style={{ lineHeight: '20px' }}>
                        <span style={{ color: '#333' }}>{it.inventoryUnitName || '-'}</span>
                        <span style={{ color: '#999', marginLeft: 6 }}>({it.inventoryUnitCode})</span>
                        <span style={{ color: '#666', marginLeft: 8 }}>组: {it.sortingGroup}</span>
                        <span style={{ color: '#666', marginLeft: 8 }}>数量: {it.sortedQuantity}</span>
                        {it.displaySortingBarcode && (
                          <span style={{ color: '#8c8c8c', marginLeft: 8 }}>条码: {it.displaySortingBarcode}</span>
                        )}
                        {it.sortingBarcodes && it.sortingBarcodes.length > 0 && (
                          <span style={{ color: '#8c8c8c', marginLeft: 8 }}>多码: {it.sortingBarcodes.join('/')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: '#8c8c8c' }}>暂无记录</div>
            )}
          </div>
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #ffd591' }}>
            <div style={{ fontWeight: 600, color: '#ad6800', marginBottom: 4 }}>LocalStorage 调试日志</div>
            <div style={{ maxHeight: 160, overflowY: 'auto', background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4, padding: 8, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
              {lsDebugLogs.length > 0 ? lsDebugLogs.map((line, i) => (
                <div key={`lslog-${i}`} style={{ fontSize: 12, color: '#5c3c00' }}>{line}</div>
              )) : <div style={{ fontSize: 12, color: '#5c3c00' }}>暂无日志</div>}
            </div>
          </div>
        </div>
      </div>
      )}
      {/* 页面标题 */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <Title level={2}>扫描入库</Title>
        <Text type="secondary">请扫描物流单号/入库预报容器编码执行入库操作</Text>
      </div>

      {/* 项目编码和仓库选择区域 */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Text strong style={{ minWidth: '100px' }}>项目编码：</Text>
          <Select
            placeholder="请选择项目编码（可选）"
            value={projectCode}
            onChange={handleProjectCodeChange}
            style={{ minWidth: '300px' }}
            allowClear
            loading={loadingProjects}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={projects.map(project => ({
              value: project.project_code,
              label: `${project.project_code} - ${project.name} (${getProjectTypeText(project)})`,
            }))}
          />
          
          <Text strong style={{ minWidth: '100px', marginLeft: '20px' }}>仓库：</Text>
          <Select
            placeholder="请选择入库仓库"
            value={warehouseCode}
            onChange={handleWarehouseChange}
            style={{ minWidth: '250px' }}
            allowClear
            loading={loadingWarehouses}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={warehouses.map(warehouse => ({
              value: warehouse.code,
              label: `${warehouse.code} - ${warehouse.name} (${warehouse.city})`,
            }))}
          />
          
          {projectCode && (
            <div style={{ 
              marginLeft: 'auto',
              padding: '8px 16px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                当前项目编码：
              </Text>
              <Text strong style={{ color: '#52c41a', fontSize: '18px' }}>
                {projectCode}
              </Text>
              <Tag color="green" style={{ fontSize: '11px' }}>
                项目验证已启用
              </Tag>
            </div>
          )}
          
          
        </div>
      </Card>

      {/* 扫描输入区域 */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <Input
                ref={mainScanInputRef}
                size="large"
                placeholder="请扫描入库预报容器标签或入库物流单号"
                value={scanValue}
                onChange={handleScanChange}
                onKeyPress={handleScanInput}
                prefix={<SearchOutlined />}
                style={{ textAlign: 'left' }}
                autoFocus
              />
            </div>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                if (scanValue.trim()) {
                  handleScanInput({ key: 'Enter' } as React.KeyboardEvent<HTMLInputElement>);
                } else {
                  message.warning('请先输入或扫描条码');
                }
              }}
              disabled={!scanValue.trim()}
            >
              提交
            </Button>
            
            <div style={{ marginLeft: 'auto' }}>
              <Button
                style={{
                  backgroundColor: '#ff7a45',
                  borderColor: '#ff7a45',
                  color: 'white',
                  fontWeight: '500'
                }}
                size="large"
                onClick={() => {
                  // 校验是否选择了项目和仓库
                  if (!projectCode) {
                    message.warning('请先选择项目编码后再进行无单号入库');
                    return;
                  }
                  if (!warehouseCode) {
                    message.warning('请先选择入库仓库后再进行无单号入库');
                    return;
                  }
                   // 设置虚拟的物流单号扫描值
                   setScanValue('NO TRACKING');
                   setCurrentScanValue('NO TRACKING');
                   setScanType('物流单号');
                   
                   // 直接打开扫描结果模态框
                   setIsModalVisible(true);
                   
                   // 模态框打开后，聚焦到库存单元扫描框
                   // 增加延迟时间，确保模态框完全渲染后再聚焦
                   setTimeout(() => {
                     if (scanInputRef.current) {
                       scanInputRef.current.focus();
                       console.log('[DEBUG] 无单号入库模态框库存单元扫描框自动聚焦成功');
                     } else {
                       console.warn('[DEBUG] 无单号入库模态框库存单元扫描框ref未找到，无法自动聚焦');
                     }
                   }, 300);
                 }}
              >
                无单号入库
              </Button>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">按回车键开始搜索</Text>
          </div>
        </div>
      </Card>

      {/* 分拣数据库 */}
      <Card 
        title={
          <Space>
            <Text strong>入库分拣看板</Text>
            <Tag color="geekblue" style={{ fontSize: '12px' }}>
              库存单元种类：{(() => (new Set(sortedSortingDatabase.map(item => item.inventoryUnitCode))).size)()}
            </Tag>
            <Tag color="green" style={{ fontSize: '12px' }}>
              已分拣总数：{(() => sortedSortingDatabase.reduce((sum, it) => sum + (it.sortedQuantity || 0), 0))()}
            </Tag>
            <Tag color="blue" style={{ fontSize: '12px' }}>
              已分配 {(() => {
                const uniqueLocations = new Set(sortedSortingDatabase.map(item => item.temporaryLocation));
                return uniqueLocations.size;
              })()} 个仓位
            </Tag>
          </Space>
        }
        extra={
          <Space>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>项目:</Text>
              <Select
                size="small"
                showSearch
                allowClear
                placeholder="项目编码"
                style={{ width: 220 }}
                value={boardProjectFilter}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label ?? '').toLowerCase();
                  const value = String(option?.value ?? '').toLowerCase();
                  const needle = String(input || '').toLowerCase();
                  return label.includes(needle) || value.includes(needle);
                }}
                onSearch={(val) => { 
                  setBoardProjectSearchText(val || ''); 
                  setBoardPage(1);
                }}
                onChange={(v) => { 
                  setBoardProjectFilter((v as string) || undefined); 
                  // 选择后清空搜索文本，避免叠加筛选
                  setBoardProjectSearchText(''); 
                  setBoardPage(1); 
                }}
                options={boardProjectOptions}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>临时分拣仓位:</Text>
              <InputNumber 
                size="small"
                min={1}
                placeholder="编号"
                value={boardLocationFilter ?? null}
                onChange={(v) => {
                  const n = typeof v === 'number' ? v : undefined;
                  setBoardLocationFilter(n);
                  setBoardPage(1);
                }}
                style={{ width: 100 }}
              />
              <Button size="small" onClick={() => {
                // 同时清空项目筛选和临时分拣仓位筛选
                setBoardProjectFilter(undefined);
                setBoardProjectSearchText('');
                setBoardLocationFilter(undefined);
                setBoardPage(1);
              }}>重置</Button>
            </div>
                         <Button size="small" onClick={() => { 
                           // 同时保存到localStorage和标签页管理器
                           const boardState = { sortingDatabase, nextLocationNumber, reusableLocationPool };
                           saveSortingBoardStateLocal(boardState);
                                                       saveSortingBoardData(boardState);
                           message.success('已保存分拣看板');
                         }}>保存看板</Button>
            {(() => {
              const reclaimable = computeReclaimableLocations(sortingDatabase, scanHistory);
              return (
                <Button
                  size="small"
                  disabled={reclaimable.length === 0 || isReclaimBlocked}
                  title={isReclaimBlocked ? '入库弹窗处理中，暂不能回收仓位' : '回收已分拣数量为 0 的临时分拣仓位，放入可复用池'}
                  onClick={() => confirmReclaimLocations(reclaimable)}
                >
                  回收空仓位({reclaimable.length})
                </Button>
              );
            })()}
            <Button size="small" danger onClick={() => {
              Modal.confirm({
                title: '清空分拣看板数据？',
                content: '该操作将删除本地保存的看板数据且无法恢复。',
                onOk: () => { 
                  // 同时清理localStorage和标签页管理器中的状态
                  clearSortingBoardStateLocal(); 
                  clearSortingBoardData();
                  setSortingDatabase([]); 
                  setNextLocationNumber(1); 
                  setReusableLocationPool([]); // 清空可重用仓位池
                  message.success('已清空'); 
                }
              });
            }}>清空看板</Button>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
        <div>
          
          {sortedSortingDatabase.length > 0 ? (
            <div style={{ 
              border: '1px solid #e8e8e8',
              borderRadius: '4px',
              backgroundColor: '#fff',
              padding: '8px'
            }}>
              {/* 按分拣组分组显示 */}
              {(() => {
                // 按分拣组分组数据（分页后的条目）
                const groupedData = boardPageItems.reduce((groups, item) => {
                  if (!groups[item.sortingGroupSystemCode]) {
                    groups[item.sortingGroupSystemCode] = [];
                  }
                  groups[item.sortingGroupSystemCode].push(item);
                  return groups;
                }, {} as Record<string, typeof sortedSortingDatabase>);

                // 数据已经按临时仓位排序，无需再次排序

                const reclaimableLocationSet = new Set(computeReclaimableLocations(sortingDatabase, scanHistory));

                return Object.entries(groupedData).map(([groupName, items]) => (
                  <div key={groupName} style={{ marginBottom: '16px' }}>
                    {/* 分拣组标题 */}
                    <div style={{ 
                      backgroundColor: '#f0f0f0',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      border: '1px solid #d9d9d9'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* 最左：临时分拣仓位 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            临时分拣仓位
                          </Text>
                          <Text strong style={{
                            color: (() => {
                              const firstItem = items[0];
                              return firstItem?.isEmptyLocation ? '#389e0d' : '#cf1322';
                            })(),
                            fontSize: '16px',
                            backgroundColor: (() => {
                              const firstItem = items[0];
                              // 非空：亮黄色背景；空位：保持绿色背景
                              return firstItem?.isEmptyLocation ? '#f6ffed' : '#fff566';
                            })(),
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: (() => {
                              const firstItem = items[0];
                              // 非空：亮黄边框；空位：保持绿色边框
                              return firstItem?.isEmptyLocation ? '1px solid #b7eb8f' : '1px solid #ffec3d';
                            })(),
                            minWidth: '40px',
                            textAlign: 'center',
                            marginRight: '40px'
                          }}>
                            {(() => {
                              const firstItem = items[0];
                              return firstItem?.temporaryLocation || 0;
                            })()}
                          </Text>
                        </div>

                        {/* 左：分拣组系统编码 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Text strong style={{ fontSize: '14px' }}>
                            {(() => {
                              const firstItem = items[0];
                              return firstItem?.sortingGroupSystemCode || groupName;
                            })()}
                          </Text>
                        </div>

                        {/* 中：统计信息 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginLeft: '24px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>库存单元种类数量</Text>
                            <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                              {(() => (new Set(items.map(it => it.inventoryUnitCode))).size)()}
                            </Text>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>已分拣总数</Text>
                            <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                              {(() => items.reduce((sum, it) => sum + (it.sortedQuantity || 0), 0))()}
                            </Text>
                          </div>
                        </div>

                        {/* 中右：操作按钮（与临时仓位一起右侧对齐） */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                          {reclaimableLocationSet.has(Number(items[0]?.temporaryLocation)) && (
                            <Button
                              size="small"
                              disabled={isReclaimBlocked}
                              title={isReclaimBlocked ? '入库弹窗处理中，暂不能回收仓位' : '该仓位已分拣数量为 0，回收后放入可复用池'}
                              onClick={() => confirmReclaimLocations([Number(items[0]?.temporaryLocation)])}
                            >
                              回收此仓位
                            </Button>
                          )}
                          <Button
                            size="small"
                            style={{
                              backgroundColor: '#52c41a',
                              borderColor: '#52c41a',
                              color: '#ffffff'
                            }}
                            onClick={() => {
                              try {
                                console.log('[分拣看板] 点击添加到已有仓储容器按钮');
                                const firstItem = items[0];
                                const groupName = (firstItem && firstItem.sortingGroup) || '默认分拣组';
                                const groupSystemCode = (firstItem && firstItem.sortingGroupSystemCode) || 'SG-DEFAULT-1';
                                const groupItems = items.map(it => ({
                                  inventoryUnitCode: it.inventoryUnitCode,
                                  inventoryUnitName: it.inventoryUnitName,
                                  quantity: it.sortedQuantity || 0,
                                  displaySortingBarcode: it.displaySortingBarcode,
                                  sortingBarcodes: it.sortingBarcodes,
                                }));
                                console.log('[分拣看板] 打开添加到容器模态参数:', { groupName, groupSystemCode, firstItem, groupItems });
                                openAddToContainerModal({
                                  groupName,
                                  groupSystemCode,
                                  temporaryLocation: (firstItem && firstItem.temporaryLocation) || 0,
                                  items: groupItems,
                                });
                              } catch (err) {
                                console.error(err);
                                message.error('无法打开添加到容器对话框');
                              }
                            }}
                          >
                            添加到已有仓储容器
                          </Button>
                          <Button
                            size="small"
                            style={{
                              backgroundColor: '#1677ff',
                              borderColor: '#1677ff',
                              color: '#ffffff'
                            }}
                            onClick={() => {
                              try {
                                console.log('[分拣看板] 点击生成-打印 新仓储容器按钮');
                                const firstItem = items[0];
                                const groupName = (firstItem && firstItem.sortingGroup) || '默认分拣组';
                                const groupSystemCode = (firstItem && firstItem.sortingGroupSystemCode) || 'SG-DEFAULT-1';
                                const groupItems = items.map(it => ({
                                  inventoryUnitCode: it.inventoryUnitCode,
                                  inventoryUnitName: it.inventoryUnitName,
                                  quantity: it.sortedQuantity || 0,
                                  displaySortingBarcode: it.displaySortingBarcode,
                                  sortingBarcodes: it.sortingBarcodes,
                                }));
                                console.log('[分拣看板] 打开模态参数:', { groupName, groupSystemCode, firstItem, groupItems, now: new Date().toISOString() });
                                openCreateContainerModal({
                                  groupName,
                                  groupSystemCode,
                                  temporaryLocation: (firstItem && firstItem.temporaryLocation) || 0,
                                  items: groupItems,
                                });
                                console.log('[分拣看板] 已触发打开模态');
                                setTimeout(() => {
                                  // 检查状态与 DOM
                                  // @ts-ignore
                                  console.log('[分拣看板][post-open] state.dbg.createOpen=', (window as any).dbg?.createOpen);
                                  const modalEls = document.querySelectorAll('.ant-modal');
                                  console.log('[分拣看板][post-open] modal.count=', modalEls.length);
                                }, 50);
                              } catch (err) {
                                console.error(err);
                                message.error('无法打开创建仓储容器对话框');
                              }
                            }}
                          >
                            生成-打印 新仓储容器
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* 该分拣组的库存单元列表 */}
                    <div style={{ paddingLeft: '12px' }}>
                      <Table
                        dataSource={items}
                        columns={[
                          {
                            title: '库存单元系统编码',
                            dataIndex: 'inventoryUnitCode',
                            key: 'inventoryUnitCode',
                            render: (text) => (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                <Text code style={{ fontSize: '16px', color: '#1890ff', fontWeight: 'bold' }}>{text}</Text>
                                <Tooltip title="复制">
                                  <Button type="link" size="small" className="board-unit-copy" icon={<CopyOutlined />} onClick={() => { try { navigator.clipboard.writeText(String(text || '')); message.success('已复制'); } catch { message.error('复制失败'); } }} />
                                </Tooltip>
                              </span>
                            ),
                            width: '22%',
                          },
                          {
                            title: '库存单元内容物',
                            key: 'unitContents',
                            width: '38%',
                            render: (_, record) => {
                              const lines = unitContentLinesOf(boardUnitOf(record.inventoryUnitCode), projectProducts);
                              return lines.length > 0
                                ? <div className="board-unit-contents"><UnitContentLines items={lines} showPhotoPlaceholder /></div>
                                : <Text type="secondary" className="board-unit-contents">-</Text>;
                            },
                          },
                          {
                            title: '备注',
                            key: 'unitRemarks',
                            render: (_, record) => <div className="board-unit-remarks"><UnitRemarksLines remarks={boardUnitOf(record.inventoryUnitCode)?.remarks} /></div>,
                            width: '26%',
                          },
                          {
                            title: '已分拣数量',
                            key: 'sortedQuantity',
                            render: (_, record) => (
                              <Text style={{ 
                                color: '#1890ff', 
                                fontSize: '14px',
                                backgroundColor: '#f0f8ff',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                border: '1px solid #91d5ff',
                                minWidth: '30px',
                                textAlign: 'center'
                              }}>
                                {record.sortedQuantity || 0}
                              </Text>
                            ),
                            width: '14%',
                          },
                          // 取消"操作-移除"按钮列
                        ]}
                        pagination={false}
                        size="small"
                        rowKey="inventoryUnitCode"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  </div>
                ));
              })()}
              {/* 分页器 */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <Pagination
                  size="small"
                  current={boardPage}
                  pageSize={1}
                  total={boardTotalPages}
                  showSizeChanger={false}
                  onChange={(p) => setBoardPage(p)}
                  showTotal={() => `共 ${boardTotalPages} 页，当前第 ${boardPage} 页（每页按临时仓位分组，组内不拆分）`}
                />
                {/* 每页记录数：分页按"整组不拆"计算，不能用分页器自带的条数切换，单独放一个选择框 */}
                <Select
                  size="small"
                  className="board-page-size"
                  value={boardPageSize}
                  style={{ width: 100 }}
                  onChange={(size) => { setBoardPageSize(Number(size)); setBoardPage(1); }}
                  options={[10, 20, 50, 100].map((size) => ({ value: size, label: `${size} 条/页` }))}
                />
              </div>
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '200px',
              color: '#999',
              border: '1px dashed #d9d9d9',
              borderRadius: '4px',
              backgroundColor: '#fff'
            }}>
              <Text type="secondary">暂无分拣数据</Text>
            </div>
          )}
        </div>
      </Card>
      {/* 入库记录 - 可折叠 */}
      <Collapse 
        style={{ background: 'transparent', marginBottom: '24px' }}
        activeKey={lowerActiveKeys}
        onChange={(keys) => {
          const arr = Array.isArray(keys) ? (keys as string[]) : (keys ? [keys as string] : []);
          setLowerActiveKeys(arr);
        }}
      >
        <Collapse.Panel 
          header={
            <Space>
              <Text strong>入库记录</Text>
              <Tag color="blue" style={{ fontSize: '12px' }}>
                最近 10 条记录
              </Tag>
              <Button 
                size="small" 
                type="link" 
                onClick={(e) => { 
                  try { e.stopPropagation(); } catch {} 
                  ensureLowerPanelOpen('recent-inbound'); 
                  fetchInboundRecords(); 
                }}
                loading={loadingInboundRecords}
                onMouseDown={(e) => e.stopPropagation()}
              >
                刷新
              </Button>
            </Space>
          }
          key="recent-inbound"
        >
        {loadingInboundRecords ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '200px'
          }}>
            <Spin size="large" />
            <Text style={{ marginLeft: '12px' }}>正在加载入库记录...</Text>
          </div>
        ) : inboundRecords.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '200px',
            color: '#999'
          }}>
            <Empty 
              description="暂无入库记录" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : (
           <Table
            dataSource={inboundRecords}
            columns={[
              {
                title: '系统编码',
                dataIndex: 'system_code',
                key: 'system_code',
                width: 200,
                render: (text: string, record: InboundRecord) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Text code style={{ fontSize: '14px' }}>{text}</Text>
                    <Tooltip title="复制系统编码">
                      <Button 
                        type="link" 
                        size="small" 
                        icon={<CopyOutlined />} 
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(text);
                            message.success('已复制');
                          } catch {
                            message.error('复制失败');
                          }
                        }}
                      />
                    </Tooltip>
                    {(record as any)?.is_voided ? (
                      <Tag color="red" style={{ marginLeft: 8 }}>已作废</Tag>
                    ) : null}
                  </span>
                ),
              },
              {
                title: '项目编码',
                dataIndex: ['project_detail', 'project_code'],
                key: 'project_code',
                width: 120,
                 render: (text: string) => (
                   <Text strong style={{ fontSize: '14px' }}>
                    {text}
                  </Text>
                ),
              },
              {
                title: '物流单号/容器编码',
                key: 'tracking_info',
                width: 200,
                render: (record: InboundRecord) => {
                  const trackingNumber = record.tracking_number;
                  const containerCode = record.inbound_forecast_container;
                  return (
                    <div>
                      {trackingNumber && (
                        <div>
                           <Text type="secondary" style={{ fontSize: '13px' }}>物流单号:</Text>
                          <br />
                           <Text code style={{ fontSize: '14px' }}>{trackingNumber}</Text>
                        </div>
                      )}
                      {containerCode && (
                        <div style={{ marginTop: trackingNumber ? '4px' : 0 }}>
                           <Text type="secondary" style={{ fontSize: '13px' }}>容器编码:</Text>
                          <br />
                           <Text code style={{ fontSize: '14px' }}>{containerCode}</Text>
                        </div>
                      )}
                    </div>
                  );
                },
              },
              {
                title: '快捷入库',
                dataIndex: 'is_quick_inbound',
                key: 'is_quick_inbound',
                width: 80,
                render: (isQuick: boolean) => (
                   <Tag color={isQuick ? 'green' : 'default'} style={{ fontSize: '12px' }}>
                    {isQuick ? '是' : '否'}
                  </Tag>
                ),
              },
              {
                title: '非常规操作',
                dataIndex: 'irregular_operation',
                key: 'irregular_operation',
                width: 200,
                render: (operations: string[] | string) => {
                  if (!operations || (Array.isArray(operations) && operations.length === 0)) {
                    return <Text type="secondary" style={{ fontSize: '13px' }}>-</Text>;
                  }
                  
                  // 处理数组格式
                  const operationArray = Array.isArray(operations) ? operations : [operations];
                  
                  // 将操作用两个空格连接
                  const displayText = operationArray.join('  ');
                  
                  return (
                    <Text style={{ fontSize: '12px', color: '#fa8c16' }}>
                      {displayText}
                    </Text>
                  );
                },
              },
              {
                title: '库存单元数量',
                key: 'inventory_unit_count',
                width: 80,
                render: (record: InboundRecord) => (
                   <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                    {record.inventory_unit_count}
                  </Text>
                ),
              },
              {
                title: '产品总数量',
                key: 'total_quantity',
                width: 80,
                render: (record: InboundRecord) => (
                   <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                    {record.total_quantity}
                  </Text>
                ),
              },
              {
                title: '创建时间',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 150,
                render: (text: string) => (
                   <Text style={{ fontSize: '13px' }}>
                    {new Date(text).toLocaleString('zh-CN', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Text>
                ),
              },
              {
                title: '操作',
                key: 'actions',
                fixed: 'right' as const,
                width: 220,
                align: 'center' as const,
                render: (_: any, record: InboundRecord) => (
                  <Space size="small" wrap>
                    <Button 
                      type="link" 
                      size="small" 
                      danger 
                      icon={<DeleteOutlined />} 
                      disabled={(record as any)?.is_voided}
                      onClick={() => handleVoid(record)}
                    >
                      删除
                    </Button>
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<EditOutlined />} 
                      disabled={(record as any)?.is_voided}
                      onClick={() => handleModify(record)}
                    >
                      修改
                    </Button>
                    <Button type="link" size="small" icon={<PrinterOutlined />} onClick={() => openLabelBatchPreview(record)}>打印标签</Button>
                  </Space>
                )
              }
            ]}
            pagination={false}
             size="middle"
            scroll={{ x: 1000 }}
            rowKey="id"
          />
        )}
        </Collapse.Panel>
        <Collapse.Panel
          header={
            <Space>
              <Text strong>仓储容器</Text>
              <Tag color="blue" style={{ fontSize: '12px' }}>
                最近 10 条记录
              </Tag>
              <Button
                size="small"
                type="link"
                onClick={(e) => { 
                  try { e.stopPropagation(); } catch {} 
                  ensureLowerPanelOpen('recent-containers'); 
                  fetchRecentContainers(); 
                }}
                loading={loadingRecentContainers}
                onMouseDown={(e) => e.stopPropagation()}
              >
                刷新
              </Button>
            </Space>
          }
          key="recent-containers"
        >
          {loadingRecentContainers ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
              <Spin size="large" />
              <Text style={{ marginLeft: '12px' }}>正在加载仓储容器...</Text>
            </div>
          ) : recentContainers.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#999' }}>
              <Empty description="暂无仓储容器" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <Table
              dataSource={recentContainers}
              rowKey={(r: any) => r.id}
              pagination={false}
              size="middle"
              scroll={{ x: 1080 }}
              columns={[
                { title: '系统编码', dataIndex: 'system_code', key: 'system_code', width: 160, render: (text: string) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Text code style={{ fontSize: '14px' }}>{text}</Text>
                    <Tooltip title="复制系统编码">
                      <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => { try { navigator.clipboard.writeText(text); message.success('已复制'); } catch { message.error('复制失败'); } }} />
                    </Tooltip>
                  </span>
                ) },
                { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 120, render: (v: string) => <Tag color="blue">{v}</Tag> },
                {
                  title: '所含内容物',
                  dataIndex: 'content_kind_display',
                  key: 'content_kind_display',
                  width: 180,
                  render: (v: string, r: any) => {
                    const kind = String(r?.content_kind || '').trim().toLowerCase();
                    const kindsRaw = Number(r?.contents_kinds ?? NaN);
                    const totalRaw = Number(r?.contents_total_qty ?? NaN);
                    const hasKinds = Number.isFinite(kindsRaw) && kindsRaw >= 0;
                    const hasTotal = Number.isFinite(totalRaw) && totalRaw >= 0;
                    if (kind === 'single') {
                      const txt = hasTotal ? `单装${Math.trunc(totalRaw)}件` : (v || '单装');
                      return (
                        <Tag
                          style={{
                            marginInlineEnd: 0,
                            borderRadius: 999,
                            background: '#f6ffed',
                            borderColor: '#b7eb8f',
                            color: '#389e0d',
                            fontWeight: 700,
                          }}
                        >
                          {txt}
                        </Tag>
                      );
                    }
                    if (kind === 'mixed') {
                      const txt = (hasKinds && hasTotal)
                        ? `混装 ${Math.trunc(kindsRaw)}款${Math.trunc(totalRaw)}件`
                        : (v || '混装');
                      return (
                        <Tag
                          style={{
                            marginInlineEnd: 0,
                            borderRadius: 999,
                            background: '#fff1f0',
                            borderColor: '#ffa39e',
                            color: '#cf1322',
                            fontWeight: 700,
                          }}
                        >
                          {txt}
                        </Tag>
                      );
                    }
                    return <Text type="secondary">-</Text>;
                  },
                },
                { title: '所属仓库', dataIndex: 'warehouse_name', key: 'warehouse_name', width: 180 },
                { title: '当前仓位', dataIndex: 'current_location_code', key: 'current_location_code', width: 140, render: (v: string) => v ? <Tag>{v}</Tag> : <Text type="secondary">-</Text> },
                { title: '项目', key: 'project', width: 180, render: (_: any, r: any) => <span>{r.project_code} - {r.project_name}</span> },
                { title: '状态', dataIndex: 'status_display', key: 'status_display', width: 120, render: (v: string) => <Tag color={v === '在用' || v === 'ACTIVE' ? 'green' : 'default'}>{v}</Tag> },
                { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, render: (v: string) => (
                  <Text>{new Date(v).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</Text>
                ) },
                { title: '操作', key: 'action', fixed: 'right' as const, width: 140, render: (_: any, rec: any) => (
                  <Space size="small">
                    <Button
                      type="text"
                      size="small"
                      icon={<TagsOutlined />}
                      onClick={async () => {
                        try {
                          // 拉取容器详情构造内容摘要
                          let contentItems: Array<{ name: string; quantity: number; type?: string }> = [];
                          try {
                            const resp = await fetchStorageContainerDetailWithContents(rec.id, { c_page_size: 200 } as any);
                            const items = Array.isArray(resp?.data?.contents?.results) ? resp.data.contents.results : [];
                            contentItems = items.map((it: any) => ({ name: it.inventory_unit_code, quantity: Number(it.quantity || 0) }));
                          } catch {}
                          const contentSummary = storageContainerAdapter.createContentSummary(contentItems);
                          if (contentItems.length > 0) {
                            (contentSummary as any).items = contentItems.map(ci => ({ code: ci.name, quantity: ci.quantity }));
                          }
                          // 适配为标签数据（与仓位表/扫描页一致）
                          const labelData = storageContainerAdapter.transform(rec, {
                            contentSummary,
                            printOptions: { copies: 1, directPrint: false },
                            templateId: storageContainerAdapter.getDefaultTemplateId(rec.container_type)
                          });
                          setLabelPreviewData(labelData);
                          setLabelPreviewVisible(true);
                        } catch (error) {
                          const info: any = handleLabelError ? handleLabelError(error, '预览') : { details: (error as any)?.message || '未知错误' };
                          message.error(`生成标签预览失败：${info.details}`);
                        }
                      }}
                    >打印标签</Button>
                  </Space>
                ) }
              ]}
            />
          )}
        </Collapse.Panel>
      </Collapse>
      {/* 修改弹窗（简化示例，与列表页一致） */}
      <Modal
        title="修改入库数量"
        open={modifyVisible}
        onCancel={() => setModifyVisible(false)}
        onOk={submitModify}
        confirmLoading={modifySubmitting}
        okText="提交"
        cancelText="取消"
      >
        <Form form={modifyForm} layout="vertical">
          <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 8 }}>
            {(currentRecord?.items || []).map((it: any, idx: number) => {
              const barcode = it?.inventory_unit_detail?.system_barcode || String(it.inventory_unit);
              const codes = (it?.inventory_unit_detail?.product_sorting_codes || []).map((c: any) => c.code);
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px dashed #f0f0f0' }}>
                  <div style={{ minWidth: 220 }}>
                    <div className="font-mono" style={{ fontWeight: 500 }}>{barcode}</div>
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {codes && codes.length > 0 ? codes.map((c: string, i: number) => (
                        <Tag key={i} color="blue">{c}</Tag>
                      )) : <span style={{ fontSize: 12, color: '#999' }}>无分拣条码</span>}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      label={idx === 0 ? '新数量' : ' '}
                      name={['quantities', String(it.inventory_unit)]}
                      rules={[{ required: true, message: '请输入新数量' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <InputNumber min={0} style={{ width: 160 }} />
                    </Form.Item>
                  </div>
                </div>
              );
            })}
          </div>
          <Form.Item label="原因/备注" name="remarks">
            <Input.TextArea rows={3} placeholder="变更原因（选填）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 手动选择库存单元弹窗 */}
      <Modal
        title="手动选择库存单元"
        open={manualPickOpen}
        onCancel={() => setManualPickOpen(false)}
        footer={null}
        width={960}
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 12 }}>
          货物没有条码时，按包装上的标识或产品照片选择对应的库存单元；选择后和扫描它的系统编码一样处理
        </div>
        <Input
          className="manual-pick-search"
          allowClear
          autoFocus
          placeholder="搜索系统编码 / 产品名 / 客户编码 / 分拣条码"
          value={manualPickKeyword}
          onChange={(e) => setManualPickKeyword(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        {manualPickUnits.length === 0 ? (
          <Empty description="没有可选择的库存单元，请先选择项目编码" />
        ) : (
          <Table
            size="small"
            rowKey={(row: any) => row.code}
            dataSource={manualPickFilteredUnits}
            pagination={{
              current: Math.min(manualPickPage, Math.max(1, Math.ceil(manualPickFilteredUnits.length / manualPickPageSize))),
              pageSize: manualPickPageSize,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              onChange: (page: number, size: number) => {
                setManualPickPage(size !== manualPickPageSize ? 1 : page);
                setManualPickPageSize(size);
              },
            }}
            columns={[
              {
                title: '库存单元',
                key: 'code',
                width: 190,
                render: (_: any, row: any) => (
                  <Space size={4} wrap>
                    <Text code>{row.code}</Text>
                    {row.inForecast && <Tag color="gold" className="manual-pick-in-forecast">本预报</Tag>}
                  </Space>
                ),
              },
              {
                title: '类型',
                key: 'unitType',
                width: 130,
                render: (_: any, row: any) => (
                  <Tag color={row.unitType === 'LIU' ? 'blue' : row.unitType === 'BIU' ? 'green' : 'orange'}>
                    {row.unitType === 'LIU' ? '散货库存单元' : row.unitType === 'BIU' ? '箱库存单元' : '托盘库存单元'}
                  </Tag>
                ),
              },
              {
                title: '库存单元内容物',
                key: 'products',
                // 每条内容物前面显示它自己的产品照片，混装的箱子也能逐个对照实物
                render: (_: any, row: any) => <UnitContentLines items={row.products} showPhotoPlaceholder />,
              },
              {
                title: '操作',
                key: 'action',
                width: 80,
                render: (_: any, row: any) => (
                  <Button type="primary" size="small" className="manual-pick-select" onClick={() => pickInventoryUnitManually(row.code)}>选择</Button>
                ),
              },
            ]}
          />
        )}
      </Modal>

      {/* 批量标签预览弹窗 */}
      <Modal
        title="打印标签"
        open={labelBatchVisible}
        onCancel={closeLabelBatchPreview}
        width={900}
        footer={[
          <span key="summary" className="label-batch-summary" style={{ marginRight: 16, color: '#666' }}>
            已选 {labelBatchItems.filter(it => it.selected).length} 个单元，共 {labelBatchItems.filter(it => it.selected).reduce((sum, it) => sum + it.labelQuantity, 0)} 张
          </span>,
          <Checkbox
            key="selectall"
            checked={labelBatchItems.length > 0 && labelBatchItems.every(it => it.selected)}
            indeterminate={labelBatchItems.some(it => it.selected) && !labelBatchItems.every(it => it.selected)}
            onChange={(e) => toggleSelectAllLabels(e.target.checked)}
            style={{ marginRight: 16 }}
          >全选</Checkbox>,
          <Button key="download" onClick={downloadSelectedPDF} disabled={labelBatchItems.filter(it => it.selected).length === 0}>下载PDF</Button>,
          <Button key="print" type="primary" onClick={printSelectedLabels} loading={labelBatchPrinting} disabled={labelBatchLoading || labelBatchItems.filter(it => it.selected).length === 0}>打印所选</Button>
        ]}
      >
        {labelBatchLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
            <Spin />
            <span style={{ marginLeft: 8 }}>正在生成预览...</span>
          </div>
        ) : labelBatchItems.length === 0 ? (
          <Empty description="暂无预览" />
        ) : (
          <div style={{ maxHeight: 520, overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              {labelBatchItems.map(it => (
                <Col span={12} key={it.unitId}>
                  <Card size="small" bodyStyle={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Checkbox checked={it.selected} onChange={(e) => toggleSelectOneLabel(it.unitId, e.target.checked)}>
                        <span className="font-mono">{it.systemBarcode}</span>
                      </Checkbox>
                      <Tag color={it.unitType === 'LIU' ? 'blue' : it.unitType === 'BIU' ? 'green' : it.unitType === 'PIU' ? 'orange' : it.unitType === 'AIU' ? 'red' : 'purple'}>
                        {it.unitType}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>名称：{it.nameResolved || '-'}</div>
                    <div className="label-batch-quantity" style={{ fontSize: 12, color: '#666', marginTop: 4 }}>入库数量 {it.labelQuantity}，共 {it.labelQuantity} 张</div>
                    {it.loading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
                        <Spin size="small" />
                      </div>
                    ) : it.error ? (
                      <Alert type="error" message={it.error} showIcon />
                    ) : (
                      <div style={{ marginTop: 8, textAlign: 'center' }}>
                        {it.isPDF ? (
                          <iframe title={`label-pdf-${it.unitId}`} src={it.previewUrl} style={{ width: '100%', height: 260, border: 0 }} />
                        ) : (
                          <img src={it.previewUrl} alt="label-preview" style={{ maxWidth: '100%', maxHeight: 260 }} />
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>

      {/* 分拣组选择模态框 */}
      <Modal
        title="选择分拣组"
        open={sortingGroupModalVisible}
        onCancel={handleSortingGroupCancel}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div style={{ padding: '20px 0' }}>
          {pendingInventoryUnit && (
            <div style={{ marginBottom: '20px' }}>
              <Card size="small" title="库存单元信息">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>库存单元编码：</Text>
                    <Text code>{pendingInventoryUnit.code}</Text>
                  </div>
                  {pendingInventoryUnit.inventoryUnit && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>单元类型：</Text>
                        <Tag color="blue">{pendingInventoryUnit.inventoryUnit.unit_type}</Tag>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>尺寸：</Text>
                        <Text>{pendingInventoryUnit.inventoryUnit.length}×{pendingInventoryUnit.inventoryUnit.width}×{pendingInventoryUnit.inventoryUnit.height} cm</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>重量：</Text>
                        <Text>{pendingInventoryUnit.inventoryUnit.weight} g</Text>
                      </div>
                    </>
                  )}
                  {pendingInventoryUnit.error && (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#fff2f0', 
                      border: '1px solid #ffccc7', 
                      borderRadius: '4px',
                      marginTop: '8px'
                    }}>
                      <Text type="danger">{pendingInventoryUnit.error}</Text>
                    </div>
                  )}
                </Space>
              </Card>
            </div>
          )}

          <Card size="small" title="可用分拣组">
            <div style={{ 
              maxHeight: '300px', 
              overflowY: 'auto'
            }}>
              {availableSortingGroups.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {availableSortingGroups.map((group, index) => (
                    <Card
                      key={index}
                      size="small"
                      hoverable
                      style={{ 
                        cursor: 'pointer',
                        border: '1px solid #d9d9d9'
                      }}
                      onClick={() => handleSortingGroupSelect(group)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong style={{ fontSize: '14px' }}>
                            {group.name}
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            系统编码: {group.systemCode}
                          </Text>
                        </div>
                        <Button type="primary" size="small">
                          选择
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100px',
                  color: '#999'
                }}>
                  <Text type="secondary">暂无可用的分拣组</Text>
                </div>
              )}
            </div>
          </Card>

          <div style={{ 
            marginTop: '20px', 
            textAlign: 'center',
            padding: '16px',
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '4px'
          }}>
            <Text type="success">
              请为库存单元选择一个合适的分拣组，这将决定其临时分拣仓位的分配。
            </Text>
          </div>
        </div>
      </Modal>

      {/* 尺寸输入模态框 */}
      <Modal
        title="输入未知库存单元物理信息"
        open={dimensionsModalVisible}
        onCancel={handleDimensionsCancel}
        footer={[
          <Button key="cancel" onClick={handleDimensionsCancel}>
            取消
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            onClick={handleDimensionsConfirm}
          >
            确认并创建库存单元
          </Button>
        ]}
        width={500}
        destroyOnClose
      >
        {pendingDimensionsData && (
          <div style={{ padding: '20px 0' }}>
            <Card size="small" title="扫描信息" style={{ marginBottom: '16px' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>扫描值：</Text>
                  <Text code>{pendingDimensionsData.scannedValue}</Text>
                </div>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fff2f0', 
                  border: '1px solid #ffccc7', 
                  borderRadius: '4px',
                  marginTop: '8px'
                }}>
                  <Text type="warning">
                    未找到该条码对应的库存单元，请手动输入产品信息创建新的库存单元。
                  </Text>
                </div>
              </Space>
            </Card>

            <Card 
              size="small" 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>库存单元信息</span>
                  <Button size="small" onClick={() => {
                    const last = loadUnknownUnitLastData();
                    if (!last) {
                      message.info('暂无可用的上次数据');
                      return;
                    }
                    try {
                      setPendingDimensionsData(prev => ({
                        ...(prev || { scannedValue: '' }),
                        // 库存单元只存整数（克/厘米），早先保存的带小数的旧数据取整后再填
                        weight: Math.round(last.weight),
                        hasLogisticsPackage: !!last.hasLogisticsPackage,
                        dimensions: {
                          length: Math.round(last.dimensions.length),
                          width: Math.round(last.dimensions.width),
                          height: Math.round(last.dimensions.height)
                        }
                      }));
                      message.success('已填充上次数据');
                    } catch (e) {
                      message.warning('填充上次数据失败');
                    }
                  }}>
                    使用上次数据
                  </Button>
                </div>
              }
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* 第一行：重量和物流包装 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text style={{ width: '60px' }}>重量：</Text>
                    <InputNumber
                      placeholder="请输入重量"
                      min={1}
                      precision={0}
                      style={{ width: '120px' }}
                      addonAfter="g"
                      value={pendingDimensionsData.weight}
                      onChange={(value) => setPendingDimensionsData({
                        ...pendingDimensionsData,
                        weight: value || undefined
                      })}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text style={{ width: '120px' }}>自带物流包装：</Text>
                    <Switch
                      checked={pendingDimensionsData.hasLogisticsPackage}
                      onChange={(checked) => setPendingDimensionsData({
                        ...pendingDimensionsData,
                        hasLogisticsPackage: checked
                      })}
                    />
                  </div>
                </div>
                
                {/* 第二行：尺寸输入 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <InputNumber
                    placeholder="长度 (cm)"
                    min={1}
                    precision={0}
                    style={{ width: '120px' }}
                    addonAfter="cm"
                    value={pendingDimensionsData.dimensions.length}
                    onChange={(value) => setPendingDimensionsData({
                      ...pendingDimensionsData,
                      dimensions: {
                        ...pendingDimensionsData.dimensions,
                        length: value || undefined
                      }
                    })}
                  />
                  <InputNumber
                    placeholder="宽度 (cm)"
                    min={1}
                    precision={0}
                    style={{ width: '120px' }}
                    addonAfter="cm"
                    value={pendingDimensionsData.dimensions.width}
                    onChange={(value) => setPendingDimensionsData({
                      ...pendingDimensionsData,
                      dimensions: {
                        ...pendingDimensionsData.dimensions,
                        width: value || undefined
                      }
                    })}
                  />
                  <InputNumber
                    placeholder="高度 (cm)"
                    min={1}
                    precision={0}
                    style={{ width: '120px' }}
                    addonAfter="cm"
                    value={pendingDimensionsData.dimensions.height}
                    onChange={(value) => setPendingDimensionsData({
                      ...pendingDimensionsData,
                      dimensions: {
                        ...pendingDimensionsData.dimensions,
                        height: value || undefined
                      }
                    })}
                  />
                </div>
              </Space>
            </Card>

            <div style={{ 
              marginTop: '16px', 
              textAlign: 'center',
              padding: '12px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '4px'
            }}>
              <Text type="success">
                输入重量和尺寸后将创建一个新的库存单元并添加到扫描记录中。
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 单个仓储容器标签预览（与仓位页一致） */}
      <StorageLabelPreview
        visible={labelPreviewVisible}
        data={labelPreviewData}
        // 只有"生成-打印 新仓储容器"打开的这个容器才显示来源仓位；其他地方打开的预览不显示
        temporaryLocation={labelPreviewLocation && labelPreviewData && labelPreviewLocation.containerCode === String(labelPreviewData.containerInfo?.systemCode || '') ? labelPreviewLocation.location : undefined}
        onClose={() => {
          setLabelPreviewVisible(false);
          setLabelPreviewData(undefined);
          setLabelPreviewLocation(null);
        }}
        onPrint={async (data) => {
          try {
            const printResult = await storageLabelService.printLabel(data);
            if ((printResult as any)?.success) {
              message.success(`标签打印成功！任务ID：${(printResult as any).printJobId || (printResult as any).print_job_id || ''}`);
            } else {
              message.error(`标签打印失败：${(printResult as any)?.message || ''}`);
            }
          } catch (error) {
            const errorInfo: any = handleLabelError ? handleLabelError(error, '打印') : { details: (error as any)?.message || '未知错误' };
            message.error(`标签打印失败：${errorInfo.details}`);
          }
        }}
        onDownload={async (data) => {
          try {
            const blob = await storageLabelService.downloadLabel(data);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `仓储容器标签_${(data as any)?.containerInfo?.systemCode || 'label'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('标签下载成功！');
          } catch (error) {
            const errorInfo: any = handleLabelError ? handleLabelError(error, '下载') : { details: (error as any)?.message || '未知错误' };
            message.error(`标签下载失败：${errorInfo.details}`);
          }
        }}
      />

      {/* 非常规操作「创建-扫描-打印 库存单元」生成的库存单元标签预览（与项目配置标签列表同一组件） */}
      <InventoryLabelPreview
        visible={inventoryUnitLabelPreviewVisible}
        data={inventoryUnitLabelPreviewData}
        onClose={() => {
          setInventoryUnitLabelPreviewVisible(false);
          setInventoryUnitLabelPreviewData(undefined);
        }}
        showPrintFooter
        zIndex={1100}
      />

      {/* 重量尺寸确认对话框 */}
      <Modal
        title={(
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', width: '100%' }}>
            <span style={{ justifySelf: 'start' }}>重量尺寸确认</span>
            <span style={{ justifySelf: 'center', color: '#1677ff' }}>
              {`扫描值: ${String(lastScannedValueRef.current || inventoryUnitScanValue || currentInventoryUnit?.system_barcode || '-').trim() || '-'}`}
            </span>
            <span />
          </div>
        )}
        open={weightDimensionsModalVisible}
        onCancel={handleWeightDimensionsCancel}
        footer={[
          <Button key="cancel" onClick={handleWeightDimensionsCancel} disabled={confirmingWeightDimensions}>
            取消
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            onClick={handleWeightDimensionsConfirm}
            loading={confirmingWeightDimensions}
            disabled={confirmingWeightDimensions}
          >
            {confirmingWeightDimensions ? '确认中...' : '确认重量尺寸'}
          </Button>
        ]}
        width={600}
        destroyOnClose
      >
        {currentInventoryUnit && (
          <div style={{ padding: '20px 0' }}>
            <Card size="small" title="库存单元信息" style={{ marginBottom: '16px' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>库存单元编码：</Text>
                  <Text code>{currentInventoryUnit.system_barcode}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>单元类型：</Text>
                  <Tag color="blue">{(currentInventoryUnit as any).unit_type_display || (currentInventoryUnit as any).unit_type}</Tag>
                </div>
                <div className="confirm-unit-contents" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <Text style={{ whiteSpace: 'nowrap' }}>库存单元内容物：</Text>
                  {confirmUnitContentLines.length > 0 ? (
                    <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                      <UnitContentLines items={confirmUnitContentLines} />
                    </div>
                  ) : (
                    <Text>-</Text>
                  )}
                </div>
              </Space>
            </Card>

            <Card size="small" title="理论重量尺寸" style={{ marginBottom: '16px' }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>重量：</Text>
                  <Text strong>{(currentInventoryUnit as any).weight} 克</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>长度：</Text>
                  <Text strong>{(currentInventoryUnit as any).length} 厘米</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>宽度：</Text>
                  <Text strong>{(currentInventoryUnit as any).width} 厘米</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>高度：</Text>
                  <Text strong>{(currentInventoryUnit as any).height} 厘米</Text>
                </div>
              </Space>
            </Card>

            <Card size="small" title="实际重量尺寸（可修改）">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>重量：</Text>
                  <InputNumber
                    value={actualWeightDimensions.actual_weight}
                    onChange={(value) => setActualWeightDimensions(prev => ({ ...prev, actual_weight: (value as number) || 0 }))}
                    min={1}
                    precision={0}
                    addonAfter="克"
                    style={{ width: '150px' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>长度：</Text>
                  <InputNumber
                    value={actualWeightDimensions.actual_length}
                    onChange={(value) => setActualWeightDimensions(prev => ({ ...prev, actual_length: (value as number) || 0 }))}
                    min={1}
                    precision={0}
                    addonAfter="厘米"
                    style={{ width: '150px' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>宽度：</Text>
                  <InputNumber
                    value={actualWeightDimensions.actual_width}
                    onChange={(value) => setActualWeightDimensions(prev => ({ ...prev, actual_width: (value as number) || 0 }))}
                    min={1}
                    precision={0}
                    addonAfter="厘米"
                    style={{ width: '150px' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>高度：</Text>
                  <InputNumber
                    value={actualWeightDimensions.actual_height}
                    onChange={(value) => setActualWeightDimensions(prev => ({ ...prev, actual_height: (value as number) || 0 }))}
                    min={1}
                    precision={0}
                    addonAfter="厘米"
                    style={{ width: '150px' }}
                  />
                </div>
              </Space>
            </Card>

            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              backgroundColor: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              borderRadius: '4px' 
            }}>
              <Text type="success">
                请确认此库存单元的实际重量尺寸信息。确认后，该库存单元将标记为已确认，后续扫描将直接进入入库流程。
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 创建仓储容器弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>创建仓储容器</span>
            <span style={{ marginLeft: 12, color: '#faad14', fontSize: 12 }}>
              请尽量将所有产品一次性创建入新容器/添加入已有容器，若需指定数量，务必确保输入的数量正确
            </span>
          </div>
        }
        open={createContainerModalOpen}
        onCancel={handleCreateContainerCancel}
        onOk={handleCreateContainerConfirm}
        okText="创建"
        cancelText="取消"
        width={900}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Text strong>分拣组：</Text>
            <Tag color="purple">{createContainerForm.groupName || '-'}</Tag>
            <Text code>{createContainerForm.groupSystemCode || '-'}</Text>
            <Tag color="blue">临时仓位：{createContainerForm.temporaryLocation || 0}</Tag>
          </Space>
          <Space align="center" wrap>
            <Text>容器类型：</Text>
            <Select
              style={{ width: 240 }}
              value={createContainerForm.containerType}
              onChange={(val) => setCreateContainerForm(prev => ({ ...prev, containerType: val }))}
              options={[
                { label: '箱子-Box(B)', value: 'box' },
                { label: '托盘-Pallet(P)', value: 'pallet' },
                { label: '仓库周转袋-Tote Bag(T)', value: 'tote_bag' },
                { label: '手推车-Cart(C)', value: 'cart' },
                { label: '直接存储-Direct Storage(D)', value: 'direct' },
                // 新增：物流袋（放在“直接存储”后面）
                { label: '物流袋-Shipping Bag(SB)', value: 'shipping_bag' },
                { label: '其他-Other(O)', value: 'other' },
              ]}
            />
            {createContainerForm.containerType === 'direct' && (
              <>
                <Text>直接存储仓位：</Text>
                <Input
                  style={{ width: 180 }}
                  placeholder="请输入仓位编码"
                  value={(createContainerForm as any).storageLocationCode || ''}
                  onChange={(e) => setCreateContainerForm(prev => ({ ...(prev as any), storageLocationCode: e.target.value } as any))}
                />
              </>
            )}
            <Text>备注：</Text>
            <Input
              style={{ width: 260 }}
              placeholder="可填写备注"
              value={createContainerForm.remarks}
              onChange={(e) => setCreateContainerForm(prev => ({ ...prev, remarks: e.target.value }))}
              maxLength={100}
              allowClear
            />
          </Space>
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: '8px 12px', background: '#fafafa' }}>
            <Space wrap align="center">
              <Text strong>最近入库记录：</Text>
              {lastRecordSnapshot ? (
                <>
                  <Text code>{lastRecordSnapshot.systemCode || `ID ${lastRecordSnapshot.recordId}`}</Text>
                  <Text type="secondary">
                    记录ID {lastRecordSnapshot.recordId} · 提交于 {new Date(lastRecordSnapshot.submittedAt).toLocaleString('zh-CN', { hour12: false })}
                    {' · '}共 {new Set(lastRecordSnapshot.items.map(it => it.inventoryUnitCode.toLowerCase())).size} 种 {lastRecordSnapshot.items.reduce((sum, it) => sum + it.quantity, 0)} 件
                  </Text>
                </>
              ) : (
                <Text type="secondary">暂无（提交一次入库记录后会保存在本机）</Text>
              )}
              <Button size="small" type="primary" ghost loading={fillingLastRecord} disabled={!lastRecordSnapshot} onClick={handleFillFromLastRecord}>
                填入最近入库记录
              </Button>
            </Space>
            {lastRecordFill && (
              <div style={{ marginTop: 8 }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="inventoryUnitCode"
                  dataSource={lastRecordFill.rows}
                  columns={[
                    { title: '库存单元系统编码', dataIndex: 'inventoryUnitCode', render: (t: string) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <Text code>{t}</Text>
                  <Tooltip title="复制">
                    <Button type="link" size="small" className="container-dialog-copy" icon={<CopyOutlined />} onClick={() => { try { navigator.clipboard.writeText(String(t || '')); message.success('已复制'); } catch { message.error('复制失败'); } }} />
                  </Tooltip>
                </span>
              ), width: 200 },
                    {
                title: '库存单元内容物',
                key: 'unitContents',
                // 和入库分拣看板一样：每条内容物带产品照片，"产品名（客户编码 分拣条码 ...）× 数量"
                render: (_: any, record: any) => {
                  const lines = unitContentLinesOf(boardUnitOf(record.inventoryUnitCode), projectProducts);
                  return lines.length > 0
                    ? <div className="container-dialog-contents"><UnitContentLines items={lines} showPhotoPlaceholder /></div>
                    : <Text type="secondary" className="container-dialog-contents">-</Text>;
                },
              },
                    { title: '入库记录数量', dataIndex: 'recordQuantity', width: 110 },
                    { title: '看板现有数量', dataIndex: 'boardQuantity', width: 110 },
                    { title: '本次填入数量', dataIndex: 'fillQuantity', width: 110, render: (t: number) => <Text strong>{t}</Text> },
                    { title: '说明', dataIndex: 'note', render: (t: string) => (t ? <Text type="warning">{t}</Text> : '-') },
                  ]}
                />
                {lastRecordFill.ignoredCodes.length > 0 && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                    该入库记录另有 {lastRecordFill.ignoredCodes.length} 个库存单元不在本分拣组的看板行中，已忽略：{lastRecordFill.ignoredCodes.join('、')}
                  </Text>
                )}
              </div>
            )}
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey={(r, i) => `${r.inventoryUnitCode}-${i}`}
            dataSource={createContainerForm.items}
            columns={[
              {
                title: '选择',
                width: 80,
                render: (_: any, __: any, index: number) => (
                  <Checkbox
                    checked={createContainerForm.items[index].selected}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setCreateContainerForm(prev => {
                        const items = [...prev.items];
                        items[index] = { ...items[index], selected: checked };
                        return { ...prev, items };
                      });
                    }}
                  />
                )
              },
              { title: '库存单元系统编码', dataIndex: 'inventoryUnitCode', render: (t: string) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <Text code>{t}</Text>
                  <Tooltip title="复制">
                    <Button type="link" size="small" className="container-dialog-copy" icon={<CopyOutlined />} onClick={() => { try { navigator.clipboard.writeText(String(t || '')); message.success('已复制'); } catch { message.error('复制失败'); } }} />
                  </Tooltip>
                </span>
              ), width: 220 },
              {
                title: '库存单元内容物',
                key: 'unitContents',
                width: 240,
                // 和入库分拣看板一样：每条内容物带产品照片，"产品名（客户编码 分拣条码 ...）× 数量"
                render: (_: any, record: any) => {
                  const lines = unitContentLinesOf(boardUnitOf(record.inventoryUnitCode), projectProducts);
                  return lines.length > 0
                    ? <div className="container-dialog-contents"><UnitContentLines items={lines} showPhotoPlaceholder /></div>
                    : <Text type="secondary" className="container-dialog-contents">-</Text>;
                },
              },
              {
                title: '数量',
                width: 140,
                render: (_: any, __: any, index: number) => (
                  <InputNumber
                    min={0}
                    step={1}
                    value={createContainerForm.items[index].quantity}
                    onChange={(val) => {
                      const value = (val as number) || 0;
                      setCreateContainerForm(prev => {
                        const items = [...prev.items];
                        items[index] = { ...items[index], quantity: value, selected: value > 0 ? true : items[index].selected };
                        return { ...prev, items };
                      });
                    }}
                  />
                )
              },
            ]}
          />
        </Space>
      </Modal>

      {/* 添加到已有容器弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>添加到已有容器</span>
            <span style={{ marginLeft: 12, color: '#faad14', fontSize: 12 }}>
              请尽量将所有产品一次性创建入新容器/添加入已有容器，若需指定数量，务必确保输入的数量正确
            </span>
          </div>
        }
        open={addToContainerModalOpen}
        onCancel={handleAddToContainerCancel}
        onOk={handleAddToContainerConfirm}
        okText="添加"
        cancelText="取消"
        width={920}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space align="center" wrap>
            <Text>目标容器：</Text>
            <Select
              style={{ width: 420 }}
              value={addToContainerForm.selectedContainerCode || undefined}
              onChange={handleContainerSelect}
              loading={loadingContainers}
              showSearch
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}
              notFoundContent={loadingContainers ? '加载中...' : '未找到可用容器'}
            >
              {availableContainers.map(c => (
                <Select.Option key={c.system_code} value={c.system_code}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'monospace' }}>{c.system_code}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {c.current_location_code ? <Tag color="purple">{c.current_location_code}</Tag> : <Tag>未上架</Tag>}
                      <Tag color="blue">{c.type}</Tag>
                      <Tag color="green">{c.status}</Tag>
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
            <Text>备注：</Text>
            <Input
              placeholder="可填写添加备注等信息"
              style={{ width: 200 }}
              value={addToContainerRemarks}
              onChange={(e) => setAddToContainerRemarks(e.target.value)}
              maxLength={100}
              allowClear
            />
          </Space>
          {containerSelectionError && (
            <Alert
              message={containerSelectionError}
              type="error"
              showIcon
              closable
              onClose={() => setContainerSelectionError('')}
            />
          )}
          <Table
            dataSource={addToContainerForm.items.map((it, idx) => ({ ...it, key: idx }))}
            pagination={false}
            size="small"
            columns={[
              {
                title: '选择',
                width: 80,
                render: (_: any, __: any, index: number) => (
                  <Checkbox
                    checked={addToContainerForm.items[index].selected}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAddToContainerForm(prev => {
                        const items = [...prev.items];
                        items[index] = { ...items[index], selected: checked, selectedQuantity: checked ? (items[index].selectedQuantity || 1) : 0 };
                        return { ...prev, items };
                      });
                    }}
                  />
                )
              },
              { title: '库存单元系统编码', dataIndex: 'inventoryUnitCode', render: (t: string) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <Text code>{t}</Text>
                  <Tooltip title="复制">
                    <Button type="link" size="small" className="container-dialog-copy" icon={<CopyOutlined />} onClick={() => { try { navigator.clipboard.writeText(String(t || '')); message.success('已复制'); } catch { message.error('复制失败'); } }} />
                  </Tooltip>
                </span>
              ), width: 220 },
              {
                title: '库存单元内容物',
                key: 'unitContents',
                width: 260,
                // 和入库分拣看板一样：每条内容物带产品照片，"产品名（客户编码 分拣条码 ...）× 数量"
                render: (_: any, record: any) => {
                  const lines = unitContentLinesOf(boardUnitOf(record.inventoryUnitCode), projectProducts);
                  return lines.length > 0
                    ? <div className="container-dialog-contents"><UnitContentLines items={lines} showPhotoPlaceholder /></div>
                    : <Text type="secondary" className="container-dialog-contents">-</Text>;
                },
              },
              {
                title: '已分拣数量',
                dataIndex: 'maxQuantity',
                align: 'center',
                width: 120,
              },
              {
                title: '添加数量',
                width: 140,
                render: (_: any, __: any, index: number) => (
                  <InputNumber
                    min={0}
                    step={1}
                    value={addToContainerForm.items[index].selectedQuantity}
                    onChange={(val) => {
                      const value = (val as number) || 0;
                      setAddToContainerForm(prev => {
                        const items = [...prev.items];
                        const newSelected = value > 0;
                        items[index] = { ...items[index], selectedQuantity: value, selected: newSelected };
                        return { ...prev, items };
                      });
                    }}
                  />
                )
              },
            ]}
          />
        </Space>
      </Modal>

      {/* 扫描结果模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
             <span>
               扫描结果 - {scanType} - <span style={{ color: '#1677ff' }}>{currentScanValue}</span>
             </span>
             {projectCode && (
              <span style={{ fontSize: 16, color: '#888', marginLeft: 'auto', marginRight: 56 }}>
                 当前项目编码：
                 <span style={{ fontWeight: 700, fontSize: 20, color: '#1677ff', marginLeft: 6 }}>{projectCode}</span>
               </span>
             )}
           </div>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={isReturnReceiving ? [
          <Button key="cancel-return" onClick={handleModalClose}>
            关闭
          </Button>
        ] : [
          <Button key="cancel" onClick={handleModalClose} disabled={isSubmitting}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            style={{ marginLeft: '8px' }} 
            onClick={handleSubmitInboundData}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? '提交中...' : '提交记录并保持项目选择'}
          </Button>,
          <Button
            key="submit-new-project"
            style={{ marginLeft: '8px', backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }}
            disabled={isSubmitting}
            onClick={handleSubmitInboundAndResetProject}
          >
            提交记录并选择新项目
          </Button>
        ]}
        width={1060}
        destroyOnClose
        style={{ top: 20 }}
      >
        <div style={{ padding: '20px 0' }}>
          {/* 上部分：容器内容显示；物流单号确认未匹配到任何预报时，直接不渲染这个区块 */}
          {!(scanType === '物流单号' && !loadingTrackingForecast && !trackingForecastData) && (
          <Card
            title={scanType === '入库预报容器' ? '容器内容' : '物流单号对应预报信息'}
            style={{ marginBottom: '20px' }}
            extra={
              <Tag color="blue" style={{ fontSize: '12px' }}>
                {scanType === '入库预报容器' ? '预报容器信息' : '物流单号信息'}
              </Tag>
            }
          >
            {loadingContainer ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '200px'
              }}>
                <Spin size="large" />
                <Text style={{ marginLeft: '12px' }}>正在加载容器信息...</Text>
              </div>
            ) : scanType === '入库预报容器' && containerData ? (
              <div>
                {/* 容器基本信息（默认收起） */}
                <Collapse
                  size="small"
                  style={{ marginBottom: '16px' }}
                  items={[
                    {
                      key: 'container-basic',
                      label: (
                        <Space>
                          <ContainerOutlined />
                          <Text strong>容器基本信息</Text>
                        </Space>
                      ),
                      children: (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>容器编码：</Text>
                            <Text code>{currentScanValue}</Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>容器名称：</Text>
                            <Text>{containerData.container.name}</Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>容器类型：</Text>
                            <Tag color="blue">{containerData.container.container_type_display}</Tag>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>容器角色：</Text>
                            <Tag color={containerData.container.container_role === 'structure' ? 'green' : 'orange'}>
                              {containerData.container.container_role === 'structure' ? '结构容器' : '内容容器'}
                            </Tag>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>尺寸：</Text>
                            <Text>{containerData.container.length ?? '-'}×{containerData.container.width ?? '-'}×{containerData.container.height ?? '-'} cm</Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>重量：</Text>
                            <Text>{containerData.container.weight ?? '-'} kg</Text>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>数量：</Text>
                            <Text>{containerData.container.quantity}</Text>
                          </div>
                          {containerData.container.parent_container && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text>父容器：</Text>
                              <Text code>{containerData.container.parent_container.system_code}</Text>
                            </div>
                          )}
                          {containerData.container.remarks && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Text>备注：</Text>
                              <Text type="secondary">{containerData.container.remarks}</Text>
                            </div>
                          )}
                        </Space>
                      )
                    }
                  ]}
                />

                {/* 预报信息（默认收起；存在非常规预报/特殊指令或备注信息时默认展开） */}
                {(() => {
                  const specialInstructionsMap: Record<string, string> = {
                    'accessories_materials_labels': '配件/包材/标签等非上架货物',
                    'original_box_pallet_storage': '原箱/原托盘暂存',
                    'original_package_direct_outbound': '原包装免上架直接出库',
                    'return_inspection': '退货入库初检',
                    'other': '其他'
                  };
                  const containerForecast = containerData.container.inbound_forecast;
                  const specialInstructionText = containerForecast.forecast_type === 'irregular'
                    ? (containerForecast.special_instructions
                        ? (specialInstructionsMap[containerForecast.special_instructions] || containerForecast.special_instructions)
                        : '无')
                    : '无';
                  return (
                    <Collapse
                      size="small"
                      style={{ marginBottom: '16px' }}
                      defaultActiveKey={(specialInstructionText !== '无' || containerForecast.remarks) ? ['forecast-info'] : []}
                      items={[
                        {
                          key: 'forecast-info',
                          label: (
                            <Space>
                              <InboxOutlined />
                              <Text strong>预报信息</Text>
                            </Space>
                          ),
                          children: (
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              {/* 特殊指令信息 - 放在第一行；不存在时直接不显示这个字段 */}
                              {specialInstructionText !== '无' && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Text>非常规预报/场景：</Text>
                                  <Text
                                    style={{
                                      color: '#cf1322',
                                      fontSize: '20px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {specialInstructionText}
                                  </Text>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>预报编号：</Text>
                                <Text code>{containerForecast.system_code}</Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>预报名称：</Text>
                                <Text>{containerForecast.name || '未设置'}</Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>项目：</Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Text>{containerForecast.project.project_code} - {containerForecast.project.name}</Text>
                                  {projectCode && containerForecast.project.project_code === projectCode ? (
                                    <Tag color="green" style={{ fontSize: '11px' }}>✓ 项目匹配</Tag>
                                  ) : projectCode ? (
                                    <Tag color="orange" style={{ fontSize: '11px' }}>⚠ 项目不匹配</Tag>
                                  ) : (
                                    <Tag color="blue" style={{ fontSize: '11px' }}>未选择项目</Tag>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>仓库：</Text>
                                <Text>{containerForecast.warehouse.name}</Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>备注信息：</Text>
                                <Text style={{ color: '#f5222d' }}>{containerForecast.remarks || '-'}</Text>
                              </div>
                            </Space>
                          )
                        }
                      ]}
                    />
                  );
                })()}

                {/* 子容器列表（默认收起） */}
                {containerData.child_containers && containerData.child_containers.length > 0 && (
                  <Collapse
                    size="small"
                    style={{ marginBottom: '16px' }}
                    items={[
                      {
                        key: 'child-containers',
                        label: (
                          <Space>
                            <ContainerOutlined />
                            <Text strong>子容器列表 ({containerData.child_containers.length})</Text>
                          </Space>
                        ),
                        children: (
                          <Table
                            dataSource={containerData.child_containers}
                            columns={[
                              {
                                title: '容器编码',
                                dataIndex: 'system_code',
                                key: 'system_code',
                                render: (text) => <Text code>{text}</Text>,
                              },
                              {
                                title: '容器名称',
                                dataIndex: 'name',
                                key: 'name',
                              },
                              {
                                title: '尺寸',
                                key: 'dimensions',
                                render: (_, record: any) => (
                                  <Text>{record.length}×{record.width}×{record.height}</Text>
                                ),
                              },
                              {
                                title: '重量',
                                dataIndex: 'weight',
                                key: 'weight',
                                render: (weight) => <Text>{weight} kg</Text>,
                              },
                              {
                                title: '容器数量',
                                dataIndex: 'quantity',
                                key: 'quantity',
                              },
                              {
                                title: '内容物',
                                key: 'content_summary',
                                render: (_, record: any) => {
                                  const contents = record.contents || [];
                                  const typeCount = contents.length;
                                  const totalQuantity = contents.reduce(
                                    (sum: number, c: any) => sum + (c.quantity || 0),
                                    0
                                  );
                                  return <Text>{typeCount}种|{totalQuantity}件</Text>;
                                },
                              },
                            ]}
                            pagination={false}
                            size="small"
                            rowKey="system_code"
                          />
                        )
                      }
                    ]}
                  />
                )}

                {/* 直接装载内容（默认展开） */}
                {containerData.contents && containerData.contents.length > 0 && (
                  <Collapse
                    size="small"
                    defaultActiveKey={['direct-contents']}
                    items={[
                      {
                        key: 'direct-contents',
                        label: (
                          <Space>
                            <InboxOutlined />
                            <Text strong>直接装载内容 ({containerData.contents.length})</Text>
                          </Space>
                        ),
                        children: (
                          <>
                          {containerData.container?.inbound_forecast?.content_is_estimated_split && (
                            <Alert
                              type="warning"
                              showIcon
                              style={{ marginBottom: 8 }}
                              message="该容器由批量预报创建，以下内容物数量为按总量平均分配的预估结果，不代表真实的每箱/每托盘内容，请以实际清点为准"
                            />
                          )}
                          <Table
                            dataSource={containerData.contents}
                            columns={[
                              {
                                title: '库存单元编码',
                                dataIndex: ['inventory_unit', 'system_code'],
                                key: 'system_code',
                                render: (text) => <Text code>{text}</Text>,
                              },
                              {
                                title: '库存单元内容项',
                                key: 'content_items',
                                render: (_, record: any) => {
                                  const contentItems = record.inventory_unit.content_items;
                                  if (contentItems && contentItems.length > 0) {
                                    return (
                                      <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                        {contentItems.map((item: any, index: number) => (
                                          <div key={index} style={{
                                            marginBottom: '4px',
                                            padding: '2px 4px',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '2px',
                                            fontSize: '11px'
                                          }}>
                                            <Text strong>{item.product_name}</Text>
                                            <Text type="secondary">({item.sorting_code || '-'})</Text>
                                            <Text type="secondary"> × {item.quantity}</Text>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  } else {
                                    return <Text type="secondary" style={{ fontSize: '11px' }}>无内容项</Text>;
                                  }
                                },
                              },
                              {
                                title: '单元类型',
                                dataIndex: ['inventory_unit', 'unit_type_display'],
                                key: 'unit_type_display',
                                render: (text) => <Tag color="purple">{text}</Tag>,
                              },
                              {
                                title: '重量',
                                dataIndex: ['inventory_unit', 'weight'],
                                key: 'weight',
                                render: (weight) => <Text>{weight} g</Text>,
                              },
                              {
                                title: '尺寸',
                                key: 'dimensions',
                                render: (_, record: any) => (
                                  <Text>{record.inventory_unit.length}×{record.inventory_unit.width}×{record.inventory_unit.height} cm</Text>
                                ),
                              },
                              {
                                title: '数量',
                                dataIndex: 'quantity',
                                key: 'quantity',
                                render: (text) => <Text strong>{text}</Text>,
                              },
                              {
                                title: '备注',
                                dataIndex: 'remarks',
                                key: 'remarks',
                                render: (text) => text ? <Text type="secondary">{text}</Text> : '-',
                              },
                            ]}
                            pagination={false}
                            size="small"
                            rowKey={(record: any) => record.inventory_unit.system_code}
                          />
                          </>
                        )
                      }
                    ]}
                  />
                )}

                {(!containerData.child_containers || containerData.child_containers.length === 0) && 
                 (!containerData.contents || containerData.contents.length === 0) && (
                  <Empty description="该容器暂无内容" />
                )}
              </div>
            ) : scanType === '物流单号' && trackingForecastData ? (
              <div>
                {/* 物流单号对应预报信息（默认收起；存在非常规预报/特殊指令时默认展开） */}
                {(() => {
                  const specialInstructionsMap: Record<string, string> = {
                    'accessories_materials_labels': '配件/包材/标签等非上架货物',
                    'original_box_pallet_storage': '原箱/原托盘暂存',
                    'original_package_direct_outbound': '原包装免上架直接出库',
                    'return_inspection': '退货入库初检',
                    'other': '其他'
                  };
                  const specialInstructionText = trackingForecastData.forecast_type === 'irregular'
                    ? (trackingForecastData.special_instructions
                        ? (specialInstructionsMap[trackingForecastData.special_instructions] || trackingForecastData.special_instructions)
                        : '无')
                    : '无';
                  return (
                    <Collapse
                      size="small"
                      defaultActiveKey={(specialInstructionText !== '无' || trackingForecastData.remarks) ? ['forecast-info'] : []}
                      items={[
                        {
                          key: 'forecast-info',
                          label: (
                            <Space>
                              <InboxOutlined />
                              <Text strong>预报信息</Text>
                            </Space>
                          ),
                          children: (
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                              {/* 特殊指令信息 - 放在第一行；不存在时直接不显示这个字段 */}
                              {specialInstructionText !== '无' && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Text>非常规预报/场景：</Text>
                                  <Text
                                    style={{
                                      color: '#cf1322',
                                      fontSize: '20px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {specialInstructionText}
                                  </Text>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>物流单号：</Text>
                                <Text code>{currentScanValue}</Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>预报编号：</Text>
                                <Text code>{trackingForecastData.system_code}</Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>预报名称：</Text>
                                <Text>{trackingForecastData.name || '未设置'}</Text>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>项目：</Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Text>{trackingForecastData.project_detail?.project_code} - {trackingForecastData.project_detail?.name}</Text>
                                  {projectCode && trackingForecastData.project_detail?.project_code === projectCode ? (
                                    <Tag color="green" style={{ fontSize: '11px' }}>✓ 项目匹配</Tag>
                                  ) : projectCode ? (
                                    <Tag color="orange" style={{ fontSize: '11px' }}>⚠ 项目不匹配</Tag>
                                  ) : (
                                    <Tag color="blue" style={{ fontSize: '11px' }}>未选择项目</Tag>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>仓库：</Text>
                                <Text>{trackingForecastData.warehouse_detail?.name}</Text>
                              </div>
                              {trackingForecastData.remarks && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Text>备注：</Text>
                                  <Text style={{ color: '#f5222d' }}>{trackingForecastData.remarks}</Text>
                                </div>
                              )}
                            </Space>
                          )
                        }
                      ]}
                    />
                  );
                })()}
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '200px',
                color: '#999',
                border: '1px dashed #d9d9d9',
                borderRadius: '6px',
                backgroundColor: '#fafafa'
              }}>
                <Text type="secondary">
                  {scanType === '入库预报容器' 
                    ? '容器内容信息待加载...' 
                    : '物流单号对应的预报信息待加载...'
                  }
                </Text>
              </div>
            )}
          </Card>
          )}

          {/* 下部分：处理操作 */}
          <Card 
            title={(
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span>处理操作</span>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>播报语言</span>
                  <Select
                    size="small"
                    value={speechLang}
                    style={{ width: 120 }}
                    onChange={(v) => setSpeechLang(v as LocalSpeechLang)}
                    options={[
                      { value: 'zh-CN', label: '中文' },
                      { value: 'en-US', label: 'English' },
                    ]}
                    disabled={!speechSupported}
                  />
                </div>
              </div>
            )} 
            style={{ marginBottom: '20px' }}
            extra={
              <Tag color="green" style={{ fontSize: '12px' }}>
                操作面板
              </Tag>
            }
          >
            <div style={{ padding: '16px 0' }}>
              {/* 非常规操作模块 */}
              <div style={{ 
                marginBottom: '20px',
                padding: '16px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                    非常规操作
                  </Text>
                  <Tag color="orange" style={{ fontSize: '12px' }}>
                    可选
                  </Tag>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  maxWidth: '100%'
                }}>
                  <Text style={{ minWidth: '80px' }}>操作类型:</Text>
                  <Select
                    placeholder="请选择非常规操作类型（可选）"
                    style={{ flex: 1, maxWidth: 320 }}
                    allowClear
                    value={irregularOperation}
                    onChange={(value) => {
                      console.log('[DEBUG] 非常规操作切换:');
                      console.log('  切换前的操作:', irregularOperation);
                      console.log('  切换后的操作:', value);
                      
                      setIrregularOperation(value);
                      
                      
                      // 当选择"原箱/原托盘入库"时，清空原箱/原托盘类型
                      if (value !== 'original_box_pallet_inbound') {
                        setOriginalContainerType('');
                        setOriginalContainerDimensions({
                          length: undefined,
                          width: undefined,
                          height: undefined
                        });
                        setOriginalContainerRemarks('');
                        setOriginalContainerThirdPartyRef('');
                        setOriginalContainerNoThirdPartyRef(false);
                        setOriginalContainerWeightEstimated(true);
                        setOriginalContainerQuantity(1);
                      }
                      // 当选择"配件/包材/标签等非上架货物入库"时，清空配件相关状态
                      if (value !== 'accessories_materials_labels_inbound') {
                        setAccessoriesItemType('');
                        setAccessoriesItemDimensions({
                          length: undefined,
                          width: undefined,
                          height: undefined
                        });
                        setAccessoriesItemRemarks('');
                        setAccessoriesItemQuantity(1);
                      }
                      // 当选择"异常库存入库"时，清空异常库存相关状态
                      if (value !== 'exception_inventory_inbound') {
                        setExceptionInventoryDimensions({
                          length: undefined,
                          width: undefined,
                          height: undefined
                        });
                        setExceptionInventoryQuantity(1);
                      }
                      // 当选择非"原包装免上架直接出库"时，清空其重量
                      if (value !== 'original_package_direct_outbound') {
                        setDirectOutboundWeightG(undefined);
                      }
                    }}
                    options={irregularOperationOptions}
                  />
                  {(irregularOperation === 'accessories_materials_labels_inbound' || irregularOperation === 'exception_inventory_inbound' || irregularOperation === 'original_box_pallet_inbound') && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#fffbe6',
                      border: '1px solid #ffe58f',
                      color: '#ad8b00',
                      padding: '4px 8px',
                      borderRadius: 4,
                      whiteSpace: 'nowrap'
                    }}>
                      <span style={{ fontWeight: 700, marginRight: 4 }}>!</span>
                      <Text type="secondary" style={{ color: '#ad8b00' }}>
                        {irregularOperation === 'accessories_materials_labels_inbound'
                          ? '不要拆开原包装，填入单个包装的重量和所有包装的总数量'
                          : irregularOperation === 'exception_inventory_inbound'
                            ? '填入所有异常库存的总重量和数量，可以分批计入'
                            : '不要拆开原包装，填入单个包装的尺寸/重量和所有包装的总数量'}
                      </Text>
                    </div>
                  )}
                </div>
                
                {/* 当选择"原箱/原托盘入库"时显示额外选项 */}
                {irregularOperation === 'original_box_pallet_inbound' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '12px',
                    marginTop: '12px'
                  }}>
                    {/* 第一行：容器类型和数量 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      maxWidth: '700px'
                    }}>
                      <Text style={{ minWidth: '90px' }}>容器类型:</Text>
                      <Select
                        placeholder="请选择容器类型"
                        style={{ width: '120px' }}
                        value={originalContainerType}
                        onChange={(value) => {
                          setOriginalContainerType(value);
                          setOriginalContainerWeightEstimated(true);
                          if (value === 'pallet') {
                            setOriginalContainerDimensions(prev => ({
                              ...prev,
                              length: 120,
                              width: 100,
                              height: undefined,
                              weight: undefined
                            }));
                            originalContainerHeightInputRef.current?.focus();
                          } else if (value === 'box') {
                            setOriginalContainerDimensions(prev => ({
                              ...prev,
                              length: undefined,
                              width: undefined,
                              height: undefined,
                              weight: undefined
                            }));
                            originalContainerLengthInputRef.current?.focus();
                          }
                        }}
                        options={[
                          { value: 'box', label: '箱' },
                          { value: 'pallet', label: '托盘' }
                        ]}
                      />
                      <div style={{ width: '40px' }}></div>
                      <Text style={{ minWidth: '64px' }}>数量:</Text>
                      <InputNumber
                        min={1}
                        style={{ width: '80px' }}
                        value={originalContainerQuantity}
                        onChange={(value) => setOriginalContainerQuantity(value || 1)}
                      />
                    </div>

                    {/* 第二行：尺寸、重量和使用上次数据按钮 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      maxWidth: '960px'
                    }}>
                      <Text style={{ minWidth: '90px' }}>尺寸:</Text>
                      <InputNumber
                        ref={originalContainerLengthInputRef}
                        placeholder="长"
                        min={0}
                        style={{ width: '90px' }}
                        value={originalContainerDimensions.length}
                        onChange={(value) => setOriginalContainerDimensions(prev => ({
                          ...prev,
                          length: value || undefined
                        }))}
                      />
                      <InputNumber
                        placeholder="宽"
                        min={0}
                        style={{ width: '90px' }}
                        value={originalContainerDimensions.width}
                        onChange={(value) => setOriginalContainerDimensions(prev => ({
                          ...prev,
                          width: value || undefined
                        }))}
                      />
                      <InputNumber
                        ref={originalContainerHeightInputRef}
                        placeholder="高"
                        min={0}
                        style={{ width: '90px' }}
                        value={originalContainerDimensions.height}
                        onChange={(value) => setOriginalContainerDimensions(prev => ({
                          ...prev,
                          height: value || undefined
                        }))}
                      />
                      <Text style={{ marginLeft: '4px', minWidth: '20px', whiteSpace: 'nowrap' }}>cm</Text>
                      <Text style={{ whiteSpace: 'nowrap' }}>重量:</Text>
                      <InputNumber
                        placeholder="重量"
                        min={0}
                        precision={0}
                        style={{ width: '90px' }}
                        value={originalContainerDimensions.weight}
                        onChange={(value) => setOriginalContainerDimensions(prev => ({
                          ...prev,
                          weight: value || undefined
                        }))}
                      />
                      <Text style={{ marginLeft: '4px', minWidth: '20px', whiteSpace: 'nowrap' }}>g</Text>
                      {originalContainerType === 'pallet' && (
                        <Checkbox
                          className="original-container-weight-estimated"
                          style={{ whiteSpace: 'nowrap' }}
                          checked={originalContainerWeightEstimated}
                          onChange={(e) => setOriginalContainerWeightEstimated(e.target.checked)}
                        >
                          重量为预估
                        </Checkbox>
                      )}
                      <Button
                        size="small"
                        onClick={() => {
                          const last = loadLastDimensions(ORIGINAL_CONTAINER_LAST_DIMENSIONS_KEY);
                          if (!last) {
                            message.info('暂无上次填写的尺寸数据');
                            return;
                          }
                          const { weightEstimated, ...lastDimensions } = last;
                          setOriginalContainerDimensions(lastDimensions);
                          if (typeof weightEstimated === 'boolean') setOriginalContainerWeightEstimated(weightEstimated);
                        }}
                      >
                        使用上次数据
                      </Button>
                    </div>

                    {originalContainerType === 'pallet' && (
                      <Text type="secondary" className="original-container-weight-hint" style={{ fontSize: '12px', paddingLeft: '110px' }}>
                        重量填单个托盘的重量，单位是克；预估方法：单箱重量 × 箱数 + 托盘自重
                      </Text>
                    )}

                    {/* 第三行：库存单元备注（跟踪号、第三方参考号） */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      maxWidth: '960px'
                    }}>
                      <Text style={{ minWidth: '90px', marginRight: '8px' }}>库存单元备注:</Text>
                      <Input
                        addonBefore="跟踪号-"
                        placeholder="请输入备注信息"
                        style={{ width: '220px' }}
                        value={originalContainerRemarks}
                        onChange={(e) => setOriginalContainerRemarks(e.target.value)}
                        disabled={originalRemarksLocked}
                      />
                      {originalRemarksLocked && (
                        <Button size="small" onClick={() => setOriginalRemarksLocked(false)}>取消禁用</Button>
                      )}
                      <Divider type="vertical" style={{ height: '24px', margin: '0 8px' }} />
                      <Input
                        // 字段名和输入框边框标红提示填写；错误状态会把字段名文字也变红，这里保持默认文字颜色
                        status={originalContainerNoThirdPartyRef ? undefined : 'error'}
                        addonBefore={<span className="original-container-ref-label" style={{ color: 'rgba(0, 0, 0, 0.88)' }}>第三方参考号-</span>}
                        placeholder="如其他WMS系统箱标/FBA箱标等"
                        style={{ width: '335px' }}
                        value={originalContainerThirdPartyRef}
                        onChange={(e) => setOriginalContainerThirdPartyRef(e.target.value)}
                        disabled={originalContainerNoThirdPartyRef}
                      />
                      <Checkbox
                        style={{ whiteSpace: 'nowrap' }}
                        checked={originalContainerNoThirdPartyRef}
                        onChange={(e) => {
                          setOriginalContainerNoThirdPartyRef(e.target.checked);
                          if (e.target.checked) setOriginalContainerThirdPartyRef('');
                        }}
                      >
                        无第三方参考号
                      </Checkbox>
                    </div>

                    {/* 第四行：蓝色按钮，填写第三方参考号或勾选"无第三方参考号"后才能点击 */}
                    <div style={{ paddingLeft: '110px' }}>
                      <Tooltip title={!originalContainerThirdPartyRef.trim() && !originalContainerNoThirdPartyRef ? '需要输入第三方参考号或者勾选"无第三方参考号"才能使用此按钮' : undefined}>
                        {/* 禁用的按钮不触发鼠标事件，提示挂在外层 span 上 */}
                        <span className="original-container-create-wrap" style={{ display: 'inline-block', cursor: !originalContainerThirdPartyRef.trim() && !originalContainerNoThirdPartyRef ? 'not-allowed' : undefined }}>
                          <Button
                            type="primary"
                            style={{ fontSize: '14px', pointerEvents: !originalContainerThirdPartyRef.trim() && !originalContainerNoThirdPartyRef ? 'none' : undefined }}
                            onClick={handleCreateOriginalContainerUnit}
                            loading={isCreatingInventoryUnit}
                            disabled={!originalContainerThirdPartyRef.trim() && !originalContainerNoThirdPartyRef}
                          >
                            创建-扫描-打印 库存单元
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                )}
                
                {/* 当选择"配件/包材/标签等非上架货物入库"时显示额外选项 */}
                {irregularOperation === 'accessories_materials_labels_inbound' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '12px',
                    marginTop: '12px'
                  }}>
                    {/* 第一行：货品类型 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      maxWidth: '700px'
                    }}>
                      <Text style={{ minWidth: '80px' }}>货品类型:</Text>
                      <Select
                        placeholder="请选择货品类型"
                        style={{ width: '120px' }}
                        value={accessoriesItemType}
                        onChange={setAccessoriesItemType}
                        options={[
                          { value: 'accessories', label: '配件' },
                          { value: 'materials', label: '包材' },
                          { value: 'labels', label: '标签' },
                          { value: 'other', label: '其他' }
                        ]}
                      />
                    </div>

                    {/* 第二行：重量、数量和使用上次数据按钮 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      maxWidth: '700px'
                    }}>
                      <Text style={{ minWidth: '80px' }}>重量:</Text>
                      <InputNumber
                        placeholder="重量"
                        min={0}
                        precision={0}
                        style={{ width: '120px' }}
                        value={accessoriesItemDimensions.weight}
                        onChange={(value) => setAccessoriesItemDimensions(prev => ({
                          ...prev,
                          weight: value || undefined
                        }))}
                      />
                      <Text style={{ marginLeft: '4px', minWidth: '20px', whiteSpace: 'nowrap' }}>g</Text>
                      <Text style={{ whiteSpace: 'nowrap' }}>数量:</Text>
                      <InputNumber
                        min={1}
                        style={{ width: '80px' }}
                        value={accessoriesItemQuantity}
                        onChange={(value) => setAccessoriesItemQuantity(value || 1)}
                      />
                      <Button
                        size="small"
                        onClick={() => {
                          const last = loadLastDimensions(ACCESSORIES_ITEM_LAST_DIMENSIONS_KEY);
                          if (!last?.weight) {
                            message.info('暂无上次填写的重量数据');
                            return;
                          }
                          setAccessoriesItemDimensions(prev => ({ ...prev, weight: last.weight }));
                        }}
                      >
                        使用上次数据
                      </Button>
                    </div>

                    <Text type="secondary" className="accessories-storage-hint" style={{ fontSize: '12px', paddingLeft: '100px' }}>
                      非上架货物不收仓储费，不必填写尺寸；重量填单个包装的重量，单位是克
                    </Text>

                  {/* 第三行：备注输入框和蓝色按钮 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    maxWidth: '900px'
                    }}>
                      <Text style={{ minWidth: '90px' }}>库存单元备注:</Text>
                      <Input
                        placeholder="请输入备注信息"
                        style={{ width: '200px' }}
                        value={accessoriesItemRemarks}
                        onChange={(e) => setAccessoriesItemRemarks(e.target.value)}
                        disabled={accessoriesRemarksLocked}
                      />
                      {accessoriesRemarksLocked && (
                        <Button size="small" onClick={() => setAccessoriesRemarksLocked(false)}>取消禁用</Button>
                      )}
                      <Button 
                        type="primary"
                        style={{ fontSize: '14px' }}
                        onClick={handleCreateAccessoriesInventoryUnit}
                        loading={isCreatingAccessoriesInventoryUnit}
                      >
                        创建-扫描-打印 库存单元
                      </Button>
                    </div>
                  </div>
                )}

              {/* 当选择"原包装免上架直接出库"时显示额外选项 */}
              {irregularOperation === 'original_package_direct_outbound' && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '12px',
                  marginTop: '12px'
                }}>
                  {/* 仅重量输入 */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '20px',
                    maxWidth: '500px'
                  }}>
                    <Text style={{ minWidth: '64px' }}>重量:</Text>
                    <InputNumber
                      placeholder="重量"
                      min={0}
                      style={{ width: '160px' }}
                      value={directOutboundWeightG}
                      onChange={(value) => setDirectOutboundWeightG(value || undefined)}
                    />
                    <Text style={{ marginLeft: '4px', minWidth: '20px', whiteSpace: 'nowrap' }}>g</Text>
                  </div>
                </div>
              )}
                
                {/* 当选择"异常库存入库"时显示额外选项 */}
                {irregularOperation === 'exception_inventory_inbound' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '12px',
                    marginTop: '12px'
                  }}>
                    {/* 第一行：总重量输入框 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '20px',
                      maxWidth: '700px'
                    }}>
                      <Text style={{ minWidth: '80px' }}>总重量:</Text>
                      <InputNumber
                        placeholder="重量"
                        min={0}
                        style={{ width: '140px' }}
                        value={exceptionTotalWeightG}
                        onChange={(value) => setExceptionTotalWeightG(value || undefined)}
                      />
                      <Text style={{ marginLeft: '4px', minWidth: '20px', whiteSpace: 'nowrap' }}>g</Text>
                    </div>

                    {/* 第二行：数量输入框和蓝色按钮 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '12px',
                      maxWidth: '700px'
                    }}>
                      <Text style={{ minWidth: '64px' }}>数量：</Text>
                      <InputNumber
                        min={1}
                        style={{ width: '80px' }}
                        value={exceptionInventoryQuantity}
                        onChange={(value) => setExceptionInventoryQuantity(value || 1)}
                      />
                      <div style={{ width: '20px' }}></div>
                      <Button
                        type="primary"
                        style={{ fontSize: '14px' }}
                        onClick={handleCreateExceptionInventoryUnit}
                        loading={isCreatingExceptionInventoryUnit}
                      >
                        创建-扫描 库存单元
                      </Button>
                    </div>
                  </div>
                )}
                
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    选择非常规操作类型，用于记录特殊的入库处理方式
                  </Text>
                </div>
              </div>

              {/* 退货入库初检：收货核对面板（替代下面的库存单元扫描区域和扫描记录） */}
              {isReturnReceiving && trackingForecastData && (
                <ReturnReceivingPanel
                  key={(trackingForecastData as any).id}
                  forecast={trackingForecastData as any}
                  trackingNumber={currentScanValue}
                  onClose={handleModalClose}
                  onExecuted={handleReturnExecuted}
                  speechLang={speechLang}
                  speechEnabled={speechSupported}
                />
              )}

              {/* 库存单元扫描区域 - 左右布局 */}
              {!isReturnReceiving && (
              <div style={{ 
                display: 'flex',
                gap: '16px',
                marginBottom: '16px',
                minHeight: '200px'
              }}>
                {/* 左侧：扫描框 */}
                <div style={{ 
                  flex: 1,
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '20px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                      库存单元条码扫描
                    </Text>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    width: '100%',
                    maxWidth: '520px'
                  }}>
                    <Input
                      ref={scanInputRef}
                      size="large"
                      placeholder="请扫描库存单元系统编码或产品分拣条码"
                      value={inventoryUnitScanValue}
                      onChange={handleInventoryUnitScanChange}
                      onKeyPress={handleInventoryUnitScanInput}
                      prefix={<SearchOutlined />}
                      style={{ textAlign: 'center', flex: 1 }}
                      disabled={scanningInventoryUnit}
                      autoFocus
                    />
                    <Button
                      type="primary"
                      size="large"
                      data-testid="scan-inventory-unit-button"
                      onClick={() => {
                        if (inventoryUnitScanValue.trim()) {
                          handleInventoryUnitScanInput({ key: 'Enter' } as React.KeyboardEvent<HTMLInputElement>);
                        }
                      }}
                      disabled={scanningInventoryUnit || !inventoryUnitScanValue.trim()}
                      style={{ minWidth: '80px' }}
                    >
                      提交
                    </Button>
                    <Button
                      size="large"
                      className="manual-pick-unit-button"
                      onClick={() => { setManualPickKeyword(''); setManualPickPage(1); setManualPickOpen(true); }}
                      disabled={scanningInventoryUnit}
                    >
                      手动选择
                    </Button>
                    {scanningInventoryUnit && (
                      <Spin size="small" />
                    )}
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      按回车键查询（优先匹配库存单元系统编码）
                    </Text>
                  </div>
                </div>

                {/* 右侧：临时分拣仓位显示 */}
                <div style={{ 
                  flex: 1,
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: '#fafafa',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  {scanningInventoryUnit ? (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%'
                    }}>
                      <Spin size="large" />
                      <Text style={{ marginLeft: '12px' }}>正在查询分拣数据库...</Text>
                    </div>
                  ) : scanResult ? (
                    <div style={{ textAlign: 'center' }}>
                      {/* 1. 扫描值 */}
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ fontSize: '14px', color: '#666' }}>
                          扫描值：
                        </Text>
                        <br />
                        <span
                          style={{
                            display: 'inline-block',
                            marginTop: '6px',
                            padding: '4px 12px',
                            borderRadius: 6,
                            background: '#e6f7ff',
                            border: '1px solid #91d5ff',
                            color: '#0050b3',
                            fontWeight: 700,
                            fontSize: '16px',
                            lineHeight: 1.2,
                            maxWidth: '100%',
                            wordBreak: 'break-all',
                          }}
                        >
                          {scanResult.scannedValue || scanResult.inventoryUnitCode}
                        </span>
                      </div>
                      
                      {/* 2. 库存单元 */}
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ fontSize: '14px', color: '#666' }}>
                          库存单元系统编码：
                        </Text>
                        <br />
                        <Text code style={{ fontSize: '16px', marginTop: '4px' }}>
                          {scanResult.inventoryUnitCode}
                        </Text>
                      </div>
                      
                      {/* 3. 临时分拣仓位 */}
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                          临时分拣仓位：
                        </Text>
                        <br />
                        <Text
                          style={{
                            display: 'inline-block',
                            fontSize: '32px',
                            fontWeight: 'bold',
                            color: '#cf1322',
                            marginTop: '8px',
                            padding: '2px 16px',
                            borderRadius: 6,
                            background: '#ffeb3b',
                            border: '1px solid #fadb14',
                            lineHeight: 1.2,
                          }}
                        >
                          {scanResult.temporaryLocation}
                        </Text>
                      </div>
                      
                      <div>
                        <Tag color={scanResult.isNew ? 'green' : 'blue'} style={{ fontSize: '14px' }}>
                          {scanResult.isNew ? '新分配分拣组仓位' : '使用现有分拣组仓位'}
                        </Tag>
                        <br />
                        <Tag 
                          color={scanResult.matchType === 'inventory_unit_system_code' ? 'green' : 
                                 scanResult.matchType === 'unknown_product_created' ? 'red' :
                                 scanResult.matchType === 'unknown_product_fallback' ? 'orange' : 'orange'} 
                          style={{ fontSize: '12px', marginTop: '8px' }}
                        >
                          {scanResult.matchType === 'inventory_unit_system_code' 
                            ? '直接匹配库存单元' 
                            : scanResult.matchType === 'product_sorting_code'
                            ? '通过产品分拣条码匹配'
                            : scanResult.matchType === 'product_sorting_code_fallback'
                            ? '产品列表遍历匹配'
                            : scanResult.matchType === 'unknown_product_created'
                            ? '新创建未知产品'
                            : scanResult.matchType === 'unknown_product_fallback'
                            ? '未知产品备用处理'
                            : '手动选择分拣组'
                          }
                        </Tag>
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      color: '#999'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                          临时分拣仓位
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          扫描结果将在此显示
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </Card>
          {/* 扫描记录板块 */}
          {!isReturnReceiving && (
          <Card 
            title={
              <Space>
                <Text strong>扫描记录</Text>
                <Tag color="orange" style={{ fontSize: '12px' }}>
                  当前会话 {scanHistory.length} 条记录
                </Tag>
                {scanHistory.length > 0 && (() => {
                  const stats = calculateScanHistoryStats();
                  return (
                    <>
                      <Tag color="blue" style={{ fontSize: '12px' }}>
                        库存单元数量总计: {stats.totalProductQuantity}
                      </Tag>
                      <Tag color="green" style={{ fontSize: '12px' }}>
                        库存单元种类: {stats.uniqueInventoryUnits} 种
                      </Tag>
                      <Tag color="purple" style={{ fontSize: '12px' }}>
                        分拣组: {stats.uniqueSortingGroups} 个
                      </Tag>
                    </>
                  );
                })()}
              </Space>
            }
            style={{ marginBottom: '20px' }}
            size="small"
          >
            {scanHistory.length > 0 ? (
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto'
              }}>
                <Table
                  dataSource={scanHistory}
                  columns={[
                    {
                      title: '库存单元',
                      dataIndex: 'inventoryUnitCode',
                      key: 'inventoryUnitCode',
                      render: (text, record) => {
                        // 添加调试信息
                        console.log('=== 库存单元字段渲染调试 ===');
                        console.log('text (库存单元编码):', text);
                        console.log('record (完整记录):', record);
                        console.log('record.inventoryUnitCode (库存单元编码):', record.inventoryUnitCode);
                        console.log('record.scannedValue (扫描值):', record.scannedValue);
                        console.log('=== 库存单元字段渲染调试结束 ===');
                        
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                            <Text code style={{ fontSize: '12px' }}>{text}</Text>
                            <Tooltip title="复制">
                              <Button type="link" size="small" className="scan-history-copy" icon={<CopyOutlined />} onClick={() => { try { navigator.clipboard.writeText(String(text || '')); message.success('已复制'); } catch { message.error('复制失败'); } }} />
                            </Tooltip>
                          </span>
                        );
                      },
                      width: '15%',
                    },
                    {
                      title: '扫描值',
                      dataIndex: 'scannedValue',
                      key: 'scannedValue',
                      render: (text, record) => {
                        // 添加调试信息
                        console.log('=== 扫描值字段渲染调试 ===');
                        console.log('text (扫描值):', text);
                        console.log('record (完整记录):', record);
                        console.log('record.inventoryUnitCode (库存单元编码):', record.inventoryUnitCode);
                        console.log('record.scannedValue (扫描值):', record.scannedValue);
                        console.log('=== 扫描值字段渲染调试结束 ===');
                        
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                            <Text code style={{ fontSize: '12px', color: '#1890ff' }}>{text}</Text>
                            <Tooltip title="复制">
                              <Button type="link" size="small" className="scan-history-copy" icon={<CopyOutlined />} onClick={() => { try { navigator.clipboard.writeText(String(text || '')); message.success('已复制'); } catch { message.error('复制失败'); } }} />
                            </Tooltip>
                          </span>
                        );
                      },
                      width: '15%',
                    },
                    {
                      title: '分拣组',
                      dataIndex: 'sortingGroup',
                      key: 'sortingGroup',
                      render: (text) => (
                        <Tag color="blue" style={{ fontSize: '11px' }}>
                          {text}
                        </Tag>
                      ),
                      width: '15%',
                    },
                    {
                      title: '临时仓位',
                      dataIndex: 'temporaryLocation',
                      key: 'temporaryLocation',
                      // 黄底红字，和扫描框右侧的"临时分拣仓位"一致（表格里字号小一些）
                      render: (text) => (
                        <Text
                          className="scan-history-location"
                          style={{
                            display: 'inline-block',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#cf1322',
                            padding: '0 10px',
                            borderRadius: 4,
                            background: '#ffeb3b',
                            border: '1px solid #fadb14',
                            lineHeight: 1.4,
                          }}
                        >
                          {text}
                        </Text>
                      ),
                      width: '12%',
                    },
                    {
                      title: '数量',
                      dataIndex: 'quantity',
                      key: 'quantity',
                      render: (text, record, index) => (
                        <InputNumber
                          size="small"
                          value={text}
                          onChange={(value) => {
                            const newValue = value || 1;
                            updateScanHistoryQuantity(index, newValue);
                          }}
                          min={1}
                          step={1}
                          controls={true}
                          style={{ 
                            width: '70px',
                            textAlign: 'center',
                            color: '#52c41a',
                            fontWeight: 'bold'
                          }}
                        />
                      ),
                      width: '12%',
                    },
                    {
                      title: '扫描时间',
                      dataIndex: 'scanTime',
                      key: 'scanTime',
                      render: (time) => (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {time.toLocaleString('zh-CN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </Text>
                      ),
                      width: '18%',
                    },
                    {
                      title: '操作',
                      key: 'action',
                      render: (_, record, index) => (
                        <Button
                          type="text"
                          size="small"
                          danger
                          onClick={() => deleteScanHistoryItem(index)}
                          style={{ 
                            padding: '0 8px',
                            height: '24px',
                            fontSize: '12px'
                          }}
                        >
                          删除
                        </Button>
                      ),
                      width: '8%',
                    },
                  ]}
                  pagination={false}
                  size="small"
                  rowKey={(record, index) => `${record.inventoryUnitCode}-${index}`}
                  style={{ fontSize: '12px' }}
                />
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100px',
                color: '#999',
                border: '1px dashed #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}>
                <Text type="secondary">暂无扫描记录</Text>
              </div>
            )}
          </Card>
          )}

          {/* 入库备注输入框：写入这条入库记录的备注。退货入库初检走收货核对面板，不用这里的提交按钮，不显示 */}
          {!isReturnReceiving && (
          <Card
            className="scan-inbound-remarks"
            title={
              <Space>
                <Text strong>入库备注</Text>
                <Tag color="blue" style={{ fontSize: '12px' }}>
                  可选
                </Tag>
              </Space>
            }
            style={{ marginTop: '20px' }}
            size="small"
          >
            <Input.TextArea
              placeholder="请输入入库备注信息（可选）"
              rows={3}
              maxLength={500}
              showCount
              style={{ fontSize: '14px' }}
              value={inboundRemarks}
              onChange={(e) => setInboundRemarks(e.target.value)}
            />
          </Card>
          )}
        </div>
      </Modal>

                  </div>
  );
};

export default InboundScanPage; 